<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
  defineAsyncComponent,
} from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCevicoAccess } from 'dashboard/composables/useCevicoAccess';
import CevicoViewOnlyPill from 'dashboard/components-next/cevico/CevicoViewOnlyPill.vue';
import { AUTOMATION_TAB_NODES } from 'dashboard/helper/cevicoAccess';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TemplatesPicker from 'dashboard/components/widgets/conversation/WhatsappTemplates/TemplatesPicker.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import FollowupBotModal from 'dashboard/routes/dashboard/crm/components/FollowupBotModal.vue';
import DataTreatmentTools from 'dashboard/routes/dashboard/crm/components/DataTreatmentTools.vue';
import AiAgentsDashboard from './AiAgentsDashboard.vue';
import ShadowReviewModal from './ShadowReviewModal.vue';
import AgentTestModal from './AgentTestModal.vue';
import GuidancesPanel from './GuidancesPanel.vue';
import ConfirmationAgentCard from './ConfirmationAgentCard.vue';
import NpsSurveyCard from './NpsSurveyCard.vue';
import FollowupSmartCard from './FollowupSmartCard.vue';
import PeriodRuler from 'dashboard/components-next/cevico/PeriodRuler.vue';
import {
  PERIOD_PRESETS,
  periodDateStr,
  periodParams,
  periodRangeFor,
} from 'dashboard/helper/cevicoPeriod';
import CrmAPI from 'dashboard/api/crm';
import CevicoHero from 'dashboard/components-next/cevico/CevicoHero.vue';
import { useCevicoPalette } from 'dashboard/composables/useCevicoPalette';
import { hexToRgb } from 'dashboard/helper/cevicoPalettes';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();

// 🍎 rodada 189: o hub inteiro no kit CEVICO (vidro cristalino + paleta por
// escopo, como Relatórios e Central). Cada agente mantém a cor de identidade
// dele (AGENT_META) nas peças do próprio card — agentVars(key) abaixo.
const pal = useCevicoPalette({
  scope: 'automations',
  blocks: [
    { id: 'gasto', label: 'Gasto com os agentes', icon: 'i-lucide-wallet' },
    { id: 'roteiro', label: 'Roteiro CEVICO', icon: 'i-lucide-scroll-text' },
  ],
});
const { cvVars, blockVars } = pal;
// 🃏 rodada 191: agentes em CARDS compactos (como a Central de Criativos) ou
// em LISTA; o card clicado abre no lugar (ocupa a linha inteira) com o corpo
// completo. A escolha fica lembrada neste navegador.
const AGENTS_VIEW_KEY = 'cevico_agents_view';
const agentsView = ref('cards');
try {
  if (localStorage.getItem(AGENTS_VIEW_KEY) === 'list')
    agentsView.value = 'list';
} catch (e) {
  // navegador sem armazenamento: fica no padrão
}
watch(agentsView, v => {
  try {
    localStorage.setItem(AGENTS_VIEW_KEY, v);
  } catch (e) {
    // idem
  }
});

// 🗺️ item 170: a aba Fluxos traz o Mermaid (pesado) — carrega só quando abre
const FlowsMap = defineAsyncComponent(() => import('./FlowsMap.vue'));

const inboxes = useMapGetter('inboxes/getInboxes');
// caixas de WhatsApp (Colheitadeira envia por uma delas)
const whatsappInboxes = computed(() =>
  inboxes.value.filter(i => i.channel_type === 'Channel::Whatsapp')
);
const settings = useMapGetter('crm/getSettings');
const accountLabels = useMapGetter('labels/getLabels');
const teamAgents = useMapGetter('agents/getAgents');

const TABS = [
  'robos',
  'regras',
  'agentes',
  'painel_ia',
  'fluxos',
  'programacao',
  'resultados',
  'tratamento',
];

// 🔐 rodada 334: cada aba pelo seu nó em automacoes.* (ver abre a aba;
// editar libera criar/editar robô e mexer nos agentes)
const { canView, canEdit } = useCevicoAccess();
const visibleTabs = computed(() =>
  TABS.filter(tab => canView(AUTOMATION_TAB_NODES[tab]))
);
const canEditRobos = computed(() => canEdit('automacoes.robos'));
const canEditAgents = computed(() => canEdit('automacoes.agentes_ia'));
const activeTab = ref(
  TABS.includes(route.query.tab) ? route.query.tab : 'robos'
);

// os itens do menu lateral apontam para a MESMA rota com ?tab= diferente —
// sem este watch, clicar neles não trocava a aba (a página não remonta)
watch(
  () => route.query.tab,
  tab => {
    if (TABS.includes(tab)) activeTab.value = tab;
  }
);
// trocar de aba volta ao topo da tela (a página não remonta, então o
// rolamento da aba anterior ficava — no celular parecia que nada mudou)
const pageRoot = ref(null);
watch(activeTab, () => {
  nextTick(() => pageRoot.value?.scrollTo?.({ top: 0 }));
});

// garante que atendente concedido nunca fica numa aba que não é dele
watch(
  visibleTabs,
  tabs => {
    if (tabs.length && !tabs.includes(activeTab.value)) {
      activeTab.value = tabs[0];
    }
  },
  { immediate: true }
);

// ── Agentes de IA internos (editar prompt / pausar) ──
// O Radar perene é configurado por VIGIAS: cada vigia = coluna + painel do
// atendente que recebe os avisos (null = todos) + janela de tempo própria.
const aiAgents = ref({
  conversation: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
  },
  form: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
  },
  scheduler: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
  },
  opportunity: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    watchers: [],
    wait_minutes: 10,
    response_goal_minutes: 15,
  },
  mentor: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
  },
  comments: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    page_access_token: '',
    fb_page_id: '',
    ig_user_id: '',
    page_token_set: false,
  },
  closing: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
  },
  nps: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
  },
  sales: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
  },
  instagram: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    inbox_ids: [],
  },
  copywriter: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    references: '',
  },
  pagebuilder: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    references: '',
    max_tokens: '',
  },
  harvest: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    mode: 'organize',
    monthly_size: '',
    cold_days: '',
    daily_cap: '',
    day_of_month: '',
    inbox_id: null,
    require_approval: true,
    message_preview: '',
    template_params: null,
    stage_ids: [],
  },
  manager: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    drop_pct: '',
  },
  auditor: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    daily_cap: '',
  },
  creative: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    winners_count: '',
    variations_count: '',
  },
  // 🎙️ rodada 195: Agente de Ligação no molde dos atendentes — nasce em
  // SOMBRA (monta a lista de quem ligaria; nada é discado). Voz, número e
  // chave continuam em Integrações; aqui mora o "quando" e "para quem".
  voice: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    mode: 'shadow',
    stage_ids: [],
    silence_hours: 24,
    lookback_days: 7,
    max_attempts: 2,
    daily_cap: 20,
    live_days: [],
    hours_start: '',
    hours_end: '',
    handoff_inbox_id: '',
  },
  // 🗣️ rodada 188: Atendente de Agendamento (WhatsApp) — nasce em SOMBRA; o "prompt" é o bloco da etapa
  atendente_agendamento: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    mode: 'shadow',
    inbox_ids: [],
    stage_ids: [],
    no_card: true,
    after_booking_stage_id: '',
    shadow_daily_cap: 30,
    reply_delay_seconds: 6,
    hours_start: '',
    hours_end: '',
    live_days: [],
  },
  // 🗣️ agente B: suporte a quem já marcou (dúvidas, remarcar, cancelar) — mesmas peças, sem "sem card"
  atendente_pos: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    mode: 'shadow',
    inbox_ids: [],
    stage_ids: [],
    no_card: false,
    after_booking_stage_id: '',
    shadow_daily_cap: 30,
    reply_delay_seconds: 6,
    hours_start: '',
    hours_end: '',
    live_days: [],
  },
  // 🩺 item 251: pós-operatório 24h — + quem recebe as tarefas e a janela de cirurgia recente
  atendente_pos_op: {
    enabled: false,
    prompt: '',
    model: '',
    effort: '',
    has_draft: false,
    default_prompt: '',
    mode: 'shadow',
    inbox_ids: [],
    stage_ids: [],
    no_card: false,
    after_booking_stage_id: '',
    shadow_daily_cap: 30,
    reply_delay_seconds: 6,
    hours_start: '',
    hours_end: '',
    live_days: [],
    task_assignee_id: '',
    recent_surgery_days: 60,
  },
});

const LOOKBACK_OPTIONS = [
  { value: 6, label: 'últimas 6 horas' },
  { value: 12, label: 'últimas 12 horas' },
  { value: 24, label: 'últimas 24 horas' },
  { value: 48, label: 'últimas 48 horas' },
];

const addWatcher = () => {
  aiAgents.value.opportunity.watchers.push({
    stage_id: '',
    user_id: null,
    lookback_hours: 24,
  });
};
const removeWatcher = idx => {
  aiAgents.value.opportunity.watchers.splice(idx, 1);
};

// Radar PONTUAL: varredura única (coluna + atendente + etiqueta + período),
// roda uma vez e NÃO fica ativa — diferente do Radar perene acima
const showSweepModal = ref(false);
const sweep = ref({ stage_id: '', user_id: null, label: '', since_hours: 24 });
const isSweeping = ref(false);
const runSweep = async () => {
  isSweeping.value = true;
  try {
    const { data } = await CrmAPI.radarScan({
      stage_ids: sweep.value.stage_id ? [sweep.value.stage_id] : [],
      label: sweep.value.label,
      since_hours: sweep.value.since_hours,
      user_id: sweep.value.user_id || null,
    });
    useAlert(data.message || 'Radar pontual iniciado!');
    showSweepModal.value = false;
  } catch {
    useAlert('Erro ao iniciar o radar pontual.');
  } finally {
    isSweeping.value = false;
  }
};

const radarLastRun = () => settings.value?.ai?.opportunity_last_run || null;

// ── Colunas onde o Secretário da Agenda ATUA ──
// Cada coluna marcada ganha (por baixo) uma automação "card entrou →
// anotar na Agenda", criada/removida automaticamente pelo endpoint de sync.
const schedulerStageIds = ref([]);
const isSavingSchedulerStages = ref(false);

const toggleSchedulerStage = id => {
  const idx = schedulerStageIds.value.indexOf(id);
  if (idx >= 0) schedulerStageIds.value.splice(idx, 1);
  else schedulerStageIds.value.push(id);
};

const saveSchedulerStages = async () => {
  isSavingSchedulerStages.value = true;
  try {
    const { data } = await CrmAPI.syncSchedulerStages(schedulerStageIds.value);
    schedulerStageIds.value = data.scheduler_stage_ids || [];
    useAlert(
      'Colunas do Secretário salvas — já está valendo (com o agente LIGADO).'
    );
  } catch {
    useAlert('Erro ao salvar as colunas do Secretário.');
  } finally {
    isSavingSchedulerStages.value = false;
  }
};

// colunas de atuação dos DEMAIS agentes de coluna (Analista, Monitor de
// Fechamento, NPS) — mesma mecânica do Secretário, endpoint genérico
const STAGE_AGENTS = ['conversation', 'closing', 'nps'];
const agentStageIds = ref({ conversation: [], closing: [], nps: [] });
const savingAgentStages = ref('');
const toggleAgentStage = (agent, id) => {
  const list = agentStageIds.value[agent];
  const idx = list.indexOf(id);
  if (idx >= 0) list.splice(idx, 1);
  else list.push(id);
};
const saveAgentStages = async agent => {
  savingAgentStages.value = agent;
  try {
    const { data } = await CrmAPI.syncAgentStages(
      agent,
      agentStageIds.value[agent]
    );
    agentStageIds.value[agent] = data.stage_ids || [];
    useAlert(
      `Colunas de ${AGENT_META[agent].title} salvas — valendo com o agente LIGADO.`
    );
  } catch {
    useAlert('Erro ao salvar as colunas.');
  } finally {
    savingAgentStages.value = '';
  }
};

// 💼 insights comerciais do Consultor Comercial (gestão)
const salesInsights = () => settings.value?.ai?.sales_insights || null;
// registro de atividade do Atendente Instagram (respondeu/agendou/pausou)
const instagramEvents = () => settings.value?.ai?.instagram_events || [];
const INSTAGRAM_EVENT_LABELS = {
  respondeu: '💬 respondeu',
  agendou: '📅 agendou e pausou',
  horario_invalido: '⚠️ horário não validou (tarefa criada)',
  teto_diario: '🛑 teto diário — pausado',
  erro: '❌ erro',
};
// painel de situação do Atendente Instagram (números dos últimos registros)
const instagramStats = () => {
  const evs = instagramEvents();
  return {
    replied: evs.filter(e => e.type === 'respondeu').length,
    scheduled: evs.filter(e => e.type === 'agendou').length,
    errors: evs.filter(e => e.type === 'erro' || e.type === 'horario_invalido')
      .length,
  };
};
// 🗣️ rodada 188: atendentes do WhatsApp (A = agendamento, B = pós-agendamento)
const RESPONDER_WA_KEYS = [
  'atendente_agendamento',
  'atendente_pos',
  'atendente_pos_op',
];
// quem CONVERSA com o paciente não "analisa": cada chamada à IA é uma resposta
const RESPONDER_NOUN_KEYS = [
  'atendente_agendamento',
  'atendente_pos',
  'atendente_pos_op',
  'instagram',
  'comments',
  'voice',
];
const usageNoun = key =>
  RESPONDER_NOUN_KEYS.includes(key) ? 'resposta(s)' : 'análise(s)';
const atendenteEvents = key =>
  settings.value?.ai?.responder_events?.[key] || [];
const ATENDENTE_EVENT_LABELS = {
  sombra: '🕶️ sombra: anotou o que teria respondido',
  respondeu: '💬 respondeu',
  agendou: '📅 agendou e pausou',
  horario_invalido: '⚠️ horário não validou',
  teto_sombra: '🛑 teto de conversas/dia da sombra',
  midia: '👂 leu áudio/imagem do paciente',
  teto_diario: '🛑 teto diário — pausado',
  erro: '❌ erro',
};
const shadowTodayConversations = key =>
  (settings.value?.ai?.responder_shadow_today?.[key]?.conversation_ids || [])
    .length;
const liveEnabled = computed(() => settings.value?.ai?.live_enabled === true);
// 👂🖼️ item 204: áudio/imagem do paciente lidos pelo Gemini (chave em Integrações → IA)
const geminiKeySet = computed(
  () => settings.value?.ai?.gemini_key_set === true
);
// 🟢 rodada 193: JANELA AO VIVO por dia da semana + horas. Fora da janela o
// agente continua em sombra (aprendendo). live_days vazio = todos os dias.
const LIVE_DAYS = [
  { value: 0, short: 'Dom', long: 'domingos' },
  { value: 1, short: 'Seg', long: 'segundas' },
  { value: 2, short: 'Ter', long: 'terças' },
  { value: 3, short: 'Qua', long: 'quartas' },
  { value: 4, short: 'Qui', long: 'quintas' },
  { value: 5, short: 'Sex', long: 'sextas' },
  { value: 6, short: 'Sáb', long: 'sábados' },
];
const toggleLiveDay = (key, day) => {
  const list = (aiAgents.value[key].live_days || []).map(Number);
  const idx = list.indexOf(day);
  if (idx === -1) list.push(day);
  else list.splice(idx, 1);
  aiAgents.value[key].live_days = list.sort();
};
const liveDayOn = (agent, day) =>
  (agent.live_days || []).map(Number).includes(day);
// frase por extenso: "Ao vivo aos sábados e domingos, das 08:00 às 18:00"
const liveWindowSentence = agent => {
  const days = (agent.live_days || []).map(Number).sort();
  const names = LIVE_DAYS.filter(d => days.includes(d.value)).map(d => d.long);
  let when = 'todos os dias';
  if (names.length === 7) when = 'todos os dias';
  else if (names.length === 1) when = `aos ${names[0]}`;
  else if (names.length > 1)
    when = `aos ${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
  const start = agent.hours_start || '00:00';
  const end = agent.hours_end || '23:59';
  const hours =
    agent.hours_start || agent.hours_end
      ? `das ${start} às ${end}${agent.hours_start && agent.hours_end && start > end ? ' (vira a noite)' : ''}`
      : 'o dia inteiro';
  return `Ao vivo ${when}, ${hours}; fora disso continua em sombra.`;
};
// está dentro da janela AGORA? (mesma regra do backend, pelo relógio do navegador)
const liveNow = agent => {
  const now = new Date();
  const days = (agent.live_days || []).map(Number);
  if (days.length && !days.includes(now.getDay())) return false;
  const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const start = agent.hours_start || '';
  const end = agent.hours_end || '';
  if (!start && !end) return true;
  if (start && end && start > end) return hhmm >= start || hhmm <= end; // vira a noite
  if (start && hhmm < start) return false;
  if (end && hhmm > end) return false;
  return true;
};
const situationOf = agent => {
  if (!agent.enabled) return { label: '○ Desligado', tone: '' };
  if (agent.mode === 'live' && liveEnabled.value)
    return liveNow(agent)
      ? { label: '🟢 Ao vivo (agora)', tone: 'live' }
      : { label: '🕶️ Sombra (fora da janela)', tone: 'shadow' };
  return { label: '🕶️ Em sombra', tone: 'shadow' };
};
const toggleAtendInbox = (key, id) => {
  const list = aiAgents.value[key].inbox_ids || [];
  const idx = list.indexOf(id);
  if (idx === -1) list.push(id);
  else list.splice(idx, 1);
  aiAgents.value[key].inbox_ids = [...list];
};
const toggleAtendStage = (key, id) => {
  const list = aiAgents.value[key].stage_ids || [];
  const idx = list.indexOf(id);
  if (idx === -1) list.push(id);
  else list.splice(idx, 1);
  aiAgents.value[key].stage_ids = [...list];
};
// coluna já marcada no OUTRO atendente (uma coluna tem um dono só)
const stageOwnedByOther = (key, id) =>
  RESPONDER_WA_KEYS.filter(k => k !== key).find(k =>
    (aiAgents.value[k]?.stage_ids || []).includes(id)
  );
const atendStageNames = agent =>
  (agent.stage_ids || [])
    .map(id => allStages.value.find(s => s.id === id)?.name)
    .filter(Boolean);
const atendInboxNames = agent =>
  (agent.inbox_ids || [])
    .map(id => inboxes.value.find(i => i.id === id)?.name)
    .filter(Boolean);

// ── 🎙️ rodada 195: Agente de Ligação (leads não responsivos) ──
// O servidor devolve, no payload de IA: voice_events (registro), voice_shadow
// ({date, items}) = quem ele ligaria hoje, voice_ready (ElevenLabs
// configurada) e voice_live_now. Enquanto a frente A não responder, tudo cai
// em listas vazias / no `settings.voice.configured` que já existe.
const voiceAi = () => settings.value?.ai || {};
const voiceEvents = () =>
  voiceAi().voice_events || voiceAi().agents?.voice?.voice_events || [];
const VOICE_EVENT_LABELS = {
  ligaria: '🕶️ ligaria (sombra: nada discado)',
  enfileirou: '📞 entrou na fila de ligação de hoje',
  ligou: '📞 ligou',
  atendeu: '🙋 atendeu',
  agendou: '📅 agendou',
  quer_whatsapp: '💬 pediu para seguir pelo WhatsApp',
  sem_permissao: '🔒 sem permissão da Meta para ligar',
  caixa_postal: '📼 caiu na caixa postal',
  teto_diario: '🛑 teto de ligações do dia',
  fora_da_janela: '🕶️ fora da janela: ficou em sombra',
  erro: '❌ erro',
};
// lista do dia (sombra gravada pelo job de hora em hora)
const voiceShadowStored = () => {
  const s = voiceAi().voice_shadow || voiceAi().agents?.voice?.voice_shadow;
  if (Array.isArray(s)) return s;
  return s?.items || [];
};
// resultado do botão "Ver quem ligaria hoje" (vence a lista gravada)
const voiceShadowList = ref(null);
const voiceShadowRunning = ref(false);
const voiceShadowItems = () => voiceShadowList.value ?? voiceShadowStored();
const runVoiceShadow = async () => {
  if (voiceShadowRunning.value) return;
  voiceShadowRunning.value = true;
  try {
    const { data } = await CrmAPI.voiceShadowRun();
    const items = Array.isArray(data)
      ? data
      : data?.items || data?.shadow?.items || [];
    voiceShadowList.value = items;
    useAlert(
      items.length
        ? `📋 Hoje ele ligaria para ${items.length} pessoa(s). Nada foi discado.`
        : '📋 Ninguém se encaixa hoje: nenhum lead parado nas colunas vigiadas.'
    );
  } catch (e) {
    const status = e?.response?.status;
    const msg = e?.response?.data?.error || e?.response?.data?.message;
    useAlert(
      status === 404
        ? 'O servidor ainda não tem a seleção de leads (frente A da rodada 195).'
        : msg || 'Não consegui montar a lista de hoje.'
    );
  } finally {
    voiceShadowRunning.value = false;
  }
};
// ElevenLabs pronta? (chave + agente + número) — o servidor manda voice_ready;
// senão vale o `configured` da tela de Integrações
const voiceReady = computed(() => {
  const ai = voiceAi();
  if (typeof ai.voice_ready === 'boolean') return ai.voice_ready;
  return settings.value?.voice?.configured === true;
});
// ligações de hoje (ao vivo): eventos de hoje que viraram ligação
const voiceCallsToday = () => {
  const today = new Date().toDateString();
  return voiceEvents().filter(
    ev =>
      ['enfileirou', 'ligou', 'atendeu', 'agendou'].includes(ev.type) &&
      ev.at &&
      new Date(ev.at).toDateString() === today
  ).length;
};
const voiceStageNames = agent => atendStageNames(agent);
// situação do card: igual aos atendentes, mas ao vivo ainda exige a
// ElevenLabs pronta (sem ela, o servidor deixa em sombra)
const voiceSituation = agent => {
  const base = situationOf(agent);
  if (!agent.enabled) return base;
  const ai = voiceAi();
  if (typeof ai.voice_live_now === 'boolean' && agent.mode === 'live')
    return ai.voice_live_now
      ? { label: '🟢 Ao vivo (agora)', tone: 'live' }
      : { label: '🕶️ Sombra (fora da janela)', tone: 'shadow' };
  if (agent.mode === 'live' && liveEnabled.value && !voiceReady.value)
    return { label: '🕶️ Sombra (falta a ElevenLabs)', tone: 'shadow' };
  return base;
};
// tela Sombra (lado a lado: o que a IA teria dito × o que foi dito)
const shadowOpen = ref(false);
const shadowAgentKey = ref('atendente_agendamento');
// 🧪 testar agente: bate-papo em caixa interna (sem envio), com a IA de verdade
const testAgentKey = ref('');
const openScriptFromTest = () => {
  testAgentKey.value = '';
  scriptExpanded.value = true;
  scriptOpenSection.value = 'persona';
  document
    .getElementById('cv-roteiro')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
// ✍️ rodada 191: painel de Orientações (abaixo do Roteiro) + "Testar de novo"
// abre o simulador com a fala do paciente já digitada
const guidancesPanel = ref(null);
const testPrefill = ref('');
const openTestAgent = (key, prefill = '') => {
  testPrefill.value = prefill || '';
  testAgentKey.value = key;
};
const onGuided = () => guidancesPanel.value?.reload?.();
const onGuidanceApplied = () => store.dispatch('crm/fetchSettings');

// 🕘 rodada 191: histórico de versões do Roteiro / dos Passos (guardado
// antes de cada edição na tela e de cada orientação aplicada)
const scriptHistoryOpen = ref(false);
const scriptVersions = ref([]);
const loadingVersions = ref(false);
const versionsError = ref('');
const restoringVersion = ref(null);
const loadScriptVersions = async () => {
  loadingVersions.value = true;
  versionsError.value = '';
  try {
    const { data } = await CrmAPI.scriptVersions();
    scriptVersions.value = Array.isArray(data)
      ? data
      : data?.versions || data?.items || [];
  } catch {
    scriptVersions.value = [];
    versionsError.value =
      'Não consegui carregar o histórico — o servidor não respondeu.';
  } finally {
    loadingVersions.value = false;
  }
};
const toggleScriptHistory = () => {
  scriptHistoryOpen.value = !scriptHistoryOpen.value;
  if (scriptHistoryOpen.value) loadScriptVersions();
};
const versionLabel = v =>
  v.kind === 'stage'
    ? `Passos · ${AGENT_META[v.agent_key]?.title || v.agent_key || 'agente'}`
    : 'Roteiro CEVICO';
const fmtVersionDate = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
const restoreScriptVersion = async v => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      `Voltar o ${versionLabel(v)} para a versão de ${fmtVersionDate(v.created_at)}? O texto de agora fica guardado no histórico.`
    )
  )
    return;
  restoringVersion.value = v.id;
  try {
    await CrmAPI.restoreScriptVersion(v.id);
    await store.dispatch('crm/fetchSettings');
    await loadScriptVersions();
    useAlert(`🕘 ${versionLabel(v)} voltou para a versão escolhida — já vale.`);
  } catch (e) {
    useAlert(
      e?.response?.data?.error || 'Não consegui voltar para essa versão.'
    );
  } finally {
    restoringVersion.value = null;
  }
};

// 📜 ROTEIRO CEVICO — fonte única dos atendentes que falam com paciente.
// Seção editada vale na hora para todos; seção em branco = padrão do sistema.
// 🧪 22/09: duas versões no mesmo card. 'v1' = o Roteiro ATUAL (o que os
// atendentes leem em sombra/ao vivo). 'v2' = o PARALELO, que só o 🧪 Testar
// agente lê (Roteiro: atual | v2) — para testar um, depois o outro, sem risco.
// No v2 os Passos dos dois atendentes aparecem como seções extras.
const scriptVersion = ref('v1');
const scriptIsV2 = computed(() => scriptVersion.value === 'v2');
const SCRIPT_VERSION_TABS = [
  // nomes claros (pedido 22/09: "melhorar a nomenclatura para poder testar")
  {
    key: 'v1',
    label: 'Roteiro 1 · Fiel ao N8N',
    hint: 'estrutura do robô do N8N + regras de 22/09; é o que os atendentes leem',
  },
  {
    key: 'v2',
    label: '🧪 Roteiro 2 · Otimizado pela análise',
    hint: 'análise do banco de 22/09 + regras de 22/09; só o Testar agente lê',
  },
];
const scriptSections = computed(() =>
  scriptIsV2.value
    ? settings.value?.ai?.script_v2 || []
    : settings.value?.ai?.script || []
);
const scriptUpdatedAt = computed(() =>
  scriptIsV2.value
    ? settings.value?.ai?.script_v2_updated_at || null
    : settings.value?.ai?.script_updated_at || null
);
const scriptExpanded = ref(false);
const scriptEditing = ref(false);
const scriptDraft = ref({});
const scriptOpenSection = ref('');
const savingScript = ref(false);
const startScriptEdit = () => {
  scriptDraft.value = Object.fromEntries(
    scriptSections.value.map(sec => [sec.key, sec.text || ''])
  );
  scriptEditing.value = true;
  if (!scriptOpenSection.value)
    scriptOpenSection.value = scriptSections.value[0]?.key || '';
};
const cancelScriptEdit = () => {
  scriptEditing.value = false;
  scriptDraft.value = {};
};
const pickScriptVersion = key => {
  if (key === scriptVersion.value) return;
  cancelScriptEdit();
  scriptVersion.value = key;
  scriptOpenSection.value = scriptSections.value[0]?.key || '';
};
// 🧪 abre o Testar agente já na versão que está aberta no card
const testScriptVersion = ref('v1');
const openTestWithVersion = key => {
  testScriptVersion.value = scriptVersion.value;
  openTestAgent(key);
};
const restoreScriptSection = key => {
  scriptDraft.value[key] =
    scriptSections.value.find(sec => sec.key === key)?.default || '';
};
const scriptSectionIsDefault = key => {
  const sec = scriptSections.value.find(x => x.key === key);
  return (scriptDraft.value[key] || '').trim() === (sec?.default || '').trim();
};
const saveScript = async () => {
  savingScript.value = true;
  try {
    // texto igual ao padrão viaja vazio: a seção continua "padrão" (e acompanha melhorias futuras)
    const payload = Object.fromEntries(
      scriptSections.value.map(sec => [
        sec.key,
        scriptSectionIsDefault(sec.key)
          ? ''
          : (scriptDraft.value[sec.key] || '').trim(),
      ])
    );
    await CrmAPI.updateAi(
      scriptIsV2.value ? { script_v2: payload } : { script: payload }
    );
    await store.dispatch('crm/fetchSettings');
    scriptEditing.value = false;
    useAlert(
      scriptIsV2.value
        ? '🧪 Roteiro 2 · Otimizado salvo — vale só no Testar agente; os atendentes continuam no Roteiro 1 · Fiel ao N8N.'
        : '📜 Roteiro CEVICO salvo — já vale para todos os atendentes que falam com paciente.'
    );
  } catch {
    useAlert('Erro ao salvar o Roteiro.');
  } finally {
    savingScript.value = false;
  }
};

const instagramInboxNames = agent =>
  (agent.inbox_ids || [])
    .map(id => inboxes.value.find(i => i.id === id)?.name)
    .filter(Boolean);
const isGeneratingInsights = ref(false);
// Mentor do Time pontual: gera o feedback dos últimos 7 dias agora
const isRunningMentor = ref(false);
// 27/09: papel e responsabilidades de cada pessoa — o Mentor julga cada um pelo papel
const teamRoles = ref({});
const teamRolesDirty = ref(false);
const savingTeamRoles = ref(false);
watch(
  () => settings.value?.team_roles,
  roles => {
    if (!teamRolesDirty.value)
      teamRoles.value = JSON.parse(JSON.stringify(roles || {}));
  },
  { immediate: true }
);
const roleOf = id => {
  if (!teamRoles.value[id])
    teamRoles.value[id] = { papel: '', responsabilidades: '' };
  return teamRoles.value[id];
};
const touchTeamRoles = () => {
  teamRolesDirty.value = true;
};
const saveTeamRoles = async () => {
  savingTeamRoles.value = true;
  try {
    await CrmAPI.updateTeamRoles(teamRoles.value);
    teamRolesDirty.value = false;
    await store.dispatch('crm/fetchSettings');
    useAlert('Papéis salvos ✓ — o Mentor usa a partir da próxima rodada.');
  } catch {
    useAlert('Não consegui salvar os papéis. Tente de novo.');
  } finally {
    savingTeamRoles.value = false;
  }
};
const humanTeam = computed(() =>
  (teamAgents.value || []).filter(
    ag => ag.id !== Number(settings.value?.ai_user_id)
  )
);

const runMentorNow = async () => {
  if (isRunningMentor.value) return;
  isRunningMentor.value = true;
  try {
    const { data } = await CrmAPI.runMentor();
    useAlert(
      data.message || 'Mentor iniciado! Veja o Meu Painel em alguns minutos.'
    );
    setTimeout(() => {
      isRunningMentor.value = false;
    }, 90000);
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao iniciar o Mentor.');
    isRunningMentor.value = false;
  }
};

const generateSalesInsights = async () => {
  if (isGeneratingInsights.value) return;
  isGeneratingInsights.value = true;
  try {
    const { data } = await CrmAPI.generateSalesInsights();
    useAlert(data.message || 'Análise iniciada!');
    setTimeout(async () => {
      await store.dispatch('crm/fetchSettings');
      isGeneratingInsights.value = false;
    }, 90000);
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao iniciar a análise.');
    isGeneratingInsights.value = false;
  }
};

// ── 🌾 OPERAÇÃO DA COLHEITA (Colheitadeira da Base) ──
// A prévia é gerada em segundo plano (~1-2 min) — a tela avisa e fica
// repollando o status a cada 15s até a lista ficar pronta.
const harvest = ref(null); // estado da colheita do mês (GET harvest_status)
const harvestActing = ref(''); // qual botão está trabalhando agora
const harvestGenerating = ref(false);
const showHarvestTable = ref(false);
let harvestPollTimer = null;
let harvestPollTicks = 0;

const loadHarvestStatus = async () => {
  try {
    const { data } = await CrmAPI.getHarvestStatus();
    harvest.value = data;
  } catch {
    // silencioso — o card mostra "sem colheita ainda"
  }
};

const stopHarvestPolling = () => {
  clearInterval(harvestPollTimer);
  harvestPollTimer = null;
};

const startHarvestPolling = (baseGeneratedAt, baseError) => {
  stopHarvestPolling();
  harvestPollTicks = 0;
  harvestPollTimer = setInterval(async () => {
    harvestPollTicks += 1;
    await loadHarvestStatus();
    const h = harvest.value;
    const ready =
      h?.status === 'preview' &&
      h?.generated_at &&
      h.generated_at !== baseGeneratedAt;
    const failed = h?.last_error && h.last_error !== baseError;
    if (ready || failed || harvestPollTicks > 24) {
      harvestGenerating.value = false;
      stopHarvestPolling();
      if (ready) {
        showHarvestTable.value = true;
        useAlert(
          '🌾 Prévia da colheita pronta — revise a lista e aprove quando quiser.'
        );
      } else if (failed) {
        useAlert(`⚠️ A prévia não saiu: ${h.last_error}`);
      }
    }
  }, 15000);
};

const runHarvestPreview = async () => {
  if (harvestActing.value || harvestGenerating.value) return;
  harvestActing.value = 'preview';
  const baseGeneratedAt = harvest.value?.generated_at || null;
  const baseError = harvest.value?.last_error || null;
  try {
    await CrmAPI.harvestPreview();
    harvestGenerating.value = true;
    useAlert(
      '🌾 Gerando a prévia — leva uns 2 minutos, a lista aparece aqui sozinha.'
    );
    startHarvestPolling(baseGeneratedAt, baseError);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Erro ao gerar a prévia da colheita.'
    );
  } finally {
    harvestActing.value = '';
  }
};

const refreshHarvestPreview = async () => {
  if (harvestActing.value) return;
  harvestActing.value = 'refresh';
  await loadHarvestStatus();
  showHarvestTable.value = true;
  harvestActing.value = '';
};

// ação simples da colheita: aprovar / pausar / retomar / enviar lote
const harvestAction = async (name, fn, okMessage) => {
  if (harvestActing.value) return;
  harvestActing.value = name;
  try {
    const { data } = await fn();
    if (data?.error) {
      useAlert(`⚠️ ${data.error}`);
    } else {
      useAlert(okMessage);
      await loadHarvestStatus();
    }
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro na operação da colheita.');
  } finally {
    harvestActing.value = '';
  }
};

const approveHarvest = () =>
  harvestAction(
    'approve',
    () => CrmAPI.harvestApprove(),
    aiAgents.value.harvest.mode === 'send'
      ? '✅ Colheita aprovada — os envios começam aos poucos, dentro do teto diário.'
      : '✅ Colheita aprovada — a IA etiqueta os leads escolhidos; filtre pela etiqueta no CRM.'
  );
const pauseHarvest = () =>
  harvestAction(
    'pause',
    () => CrmAPI.harvestPause(),
    '⏸ Colheita pausada — nada sai até você retomar.'
  );
const resumeHarvest = () =>
  harvestAction(
    'resume',
    () => CrmAPI.harvestResume(),
    '▶️ Colheita retomada — os envios continuam de onde pararam.'
  );
const harvestSendNow = () =>
  harvestAction(
    'send_now',
    () => CrmAPI.harvestSendNow(),
    '📤 Lote de hoje disparado — os envios saem em alguns minutos.'
  );

const skipHarvestLead = async lead => {
  try {
    await CrmAPI.harvestSkipLead(lead.contact_id);
    lead.skipped = true;
    const st = harvest.value?.stats;
    if (st) {
      st.skipped = (st.skipped || 0) + 1;
      st.pending = Math.max(0, (st.pending || 0) - 1);
    }
    useAlert(
      `${lead.name || 'Lead'} pulado — não recebe a mensagem desta colheita.`
    );
  } catch {
    useAlert('Erro ao pular o lead.');
  }
};

// status do mês em linguagem humana
const HARVEST_STATUS_INFO = {
  preview: {
    label: 'Prévia pronta — revise a lista e aprove para começar',
    class: 'bg-amber-500/15 text-amber-600',
  },
  approved: {
    label: 'Aprovada — enviando aos poucos, dentro do teto diário',
    class: 'bg-green-500/15 text-green-600',
  },
  paused: {
    label: 'Pausada — nada sai até você retomar',
    class: 'bg-n-alpha-2 text-n-slate-10',
  },
  done: {
    label: 'Concluída — colheita do mês encerrada 🎉',
    class: 'bg-sky-500/15 text-sky-600',
  },
};
const harvestStatusInfo = () =>
  HARVEST_STATUS_INFO[harvest.value?.status] || {
    label: 'Nenhuma colheita neste mês ainda — gere a prévia quando quiser.',
    class: 'bg-n-alpha-2 text-n-slate-11',
  };
const harvestMonthLabel = () => {
  const mk = harvest.value?.month_key;
  if (!mk) return '';
  return new Date(`${mk}-01T12:00:00`).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
};
// modo organize: quantos leads já ganharam a etiqueta oportunidade_AAAA_MM
const harvestOrganizedCount = () =>
  harvest.value?.organized_count ??
  harvest.value?.stats?.organized ??
  harvest.value?.stats?.sent ??
  0;
const fmtBrl = v => (v ? `R$ ${Number(v).toLocaleString('pt-BR')}` : '—');
// pílula do score: verde ≥70, âmbar ≥40, cinza <40
const scoreClass = score => {
  if (Number(score) >= 70) return 'bg-green-500/15 text-green-600';
  if (Number(score) >= 40) return 'bg-amber-500/15 text-amber-600';
  return 'bg-n-alpha-2 text-n-slate-10';
};

// escolher a mensagem modelo da colheita (mesmo fluxo do robô de follow-up)
const showHarvestTemplatePicker = ref(false);
const harvestPickerTemplate = ref(null);
const harvestPickerInboxId = computed(
  () => aiAgents.value.harvest.inbox_id ?? whatsappInboxes.value[0]?.id
);
const openHarvestTemplatePicker = () => {
  harvestPickerTemplate.value = null;
  showHarvestTemplatePicker.value = true;
};
const closeHarvestTemplatePicker = () => {
  showHarvestTemplatePicker.value = false;
  harvestPickerTemplate.value = null;
};
const useHarvestTemplate = payload => {
  aiAgents.value.harvest.template_params = payload.templateParams;
  aiAgents.value.harvest.message_preview = payload.message;
  closeHarvestTemplatePicker();
};

// ── 📊 Gestor Autônomo: estado + rodar agora ──
const managerState = () => settings.value?.ai?.manager_state || null;
// chip do desvio: "Taxa de comparecimento −38%"
const managerFindingLabel = f =>
  `${f.label || f.indicator} ${Number(f.deviation_pct) < 0 ? '−' : '+'}${Math.abs(Math.round(f.deviation_pct || 0))}%`;
const isRunningManager = ref(false);
const runManagerNow = async () => {
  if (isRunningManager.value) return;
  isRunningManager.value = true;
  try {
    const { data } = await CrmAPI.runManager();
    useAlert(
      data.message ||
        '📊 Gestor rodando! O briefing aparece aqui em ~30 segundos.'
    );
    setTimeout(async () => {
      await store.dispatch('crm/fetchSettings');
      isRunningManager.value = false;
    }, 30000);
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao rodar o Gestor.');
    isRunningManager.value = false;
  }
};

// ── 🎓 Auditor de Conversas: qualidade do time ──
// O resumo (ranking + falhas) é carregado LAZY: só quando o card expande
// pela primeira vez. "Auditar agora" re-audita ontem em segundo plano
// (~1-2 min) — a tela repolla o resumo a cada 20s por até 5 minutos.
const auditorSummary = ref(null);
// item 321: a régua padrão do sistema no lugar da janela 7/14/30
const auditorPeriod = ref({ preset: 'last7', ...periodRangeFor('last7') });
const loadingAuditorSummary = ref(false);
const isRunningAuditor = ref(false);
let auditorPollTimer = null;
let auditorPollTicks = 0;

// último dia auditado (vem no GET settings, bloco auditor_last)
const auditorLast = () => settings.value?.ai?.auditor_last || null;

const loadAuditorSummary = async () => {
  loadingAuditorSummary.value = true;
  try {
    const { data } = await CrmAPI.getAuditorSummary(
      periodParams(auditorPeriod.value)
    );
    auditorSummary.value = data;
  } catch {
    // silencioso — a seção mostra "nenhuma auditoria ainda"
  } finally {
    loadingAuditorSummary.value = false;
  }
};

const stopAuditorPolling = () => {
  clearInterval(auditorPollTimer);
  auditorPollTimer = null;
};

const runAuditorNow = async () => {
  if (isRunningAuditor.value) return;
  isRunningAuditor.value = true;
  const baseRunAt = auditorSummary.value?.last_run_at || null;
  try {
    await CrmAPI.runAuditor();
    useAlert(
      '🎓 Auditando as conversas de ontem — leva ~2 minutos, o ranking atualiza aqui sozinho.'
    );
    stopAuditorPolling();
    auditorPollTicks = 0;
    auditorPollTimer = setInterval(async () => {
      auditorPollTicks += 1;
      await loadAuditorSummary();
      const done =
        auditorSummary.value?.last_run_at &&
        auditorSummary.value.last_run_at !== baseRunAt;
      if (done || auditorPollTicks >= 15) {
        stopAuditorPolling();
        isRunningAuditor.value = false;
        if (done) {
          useAlert('🎓 Auditoria concluída — ranking atualizado.');
          // atualiza o "último dia auditado" (auditor_last vem do settings)
          store.dispatch('crm/fetchSettings').catch(() => {});
        }
      }
    }, 20000);
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao iniciar a auditoria.');
    isRunningAuditor.value = false;
  }
};

watch(auditorPeriod, () => loadAuditorSummary(), { deep: true });

// pílula da média: verde ≥8, âmbar ≥6, vermelho <6 (uma casa decimal)
const auditorAvgClass = avg => {
  if (Number(avg) >= 8) return 'bg-green-500/15 text-green-600';
  if (Number(avg) >= 6) return 'bg-amber-500/15 text-amber-600';
  return 'bg-red-500/10 text-red-500';
};
const fmtAvg = avg =>
  Number(avg || 0)
    .toFixed(1)
    .replace('.', ',');
// "Atendente 0" = conversas sem atendente humano (robô/fluxo)
const auditorRowName = row =>
  row.user_id === 0 || row.name === 'Atendente 0'
    ? 'Sem atendente (robô/fluxo)'
    : row.name;

// ── 🎨 Criativo Perpétuo: criativos da semana ──
// O estado (vencedores + variações + despensa) é carregado LAZY: só quando
// o card expande pela primeira vez. "Gerar agora" roda em segundo plano
// (~1-2 min) — a tela repolla o estado a cada 20s por até 5 minutos.
const creativeState = ref(null);
const loadingCreative = ref(false);
const isRunningCreative = ref(false);
const reviewingCreative = ref('');
let creativePollTimer = null;
let creativePollTicks = 0;

// resumo da última geração (vem no GET settings, bloco creative_last)
const creativeLast = () => settings.value?.ai?.creative_last || null;

const loadCreativeState = async () => {
  loadingCreative.value = true;
  try {
    const { data } = await CrmAPI.getCreativeState();
    creativeState.value = data;
  } catch {
    // silencioso — a seção mostra "nenhum criativo ainda"
  } finally {
    loadingCreative.value = false;
  }
};

const stopCreativePolling = () => {
  clearInterval(creativePollTimer);
  creativePollTimer = null;
};

const runCreativeNow = async () => {
  if (isRunningCreative.value) return;
  isRunningCreative.value = true;
  const baseGeneratedAt = creativeState.value?.generated_at || null;
  try {
    await CrmAPI.runCreative();
    useAlert(
      '🎨 Gerando os criativos da semana — leva ~2 minutos, eles aparecem aqui sozinhos.'
    );
    stopCreativePolling();
    creativePollTicks = 0;
    creativePollTimer = setInterval(async () => {
      creativePollTicks += 1;
      await loadCreativeState();
      const done =
        creativeState.value?.generated_at &&
        creativeState.value.generated_at !== baseGeneratedAt;
      if (done || creativePollTicks >= 15) {
        stopCreativePolling();
        isRunningCreative.value = false;
        if (done) {
          useAlert(
            '🎨 Criativos da semana prontos — revise e aprove os que quiser usar.'
          );
          // atualiza o resumo do topo (creative_last vem do settings)
          store.dispatch('crm/fetchSettings').catch(() => {});
        }
      }
    }, 20000);
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao gerar os criativos.');
    isRunningCreative.value = false;
  }
};

// aprovar/recusar UMA variação — atualiza o estado local com a resposta
const reviewCreativeVariation = async (wi, vi, status) => {
  if (reviewingCreative.value) return;
  reviewingCreative.value = `${wi}-${vi}`;
  try {
    const { data } = await CrmAPI.reviewCreative(wi, vi, status);
    if (data?.error) {
      useAlert(`⚠️ ${data.error}`);
    } else {
      const variation = creativeState.value?.winners?.[wi]?.variations?.[vi];
      if (variation) {
        variation.status = data?.status || status;
        // aprovada entra na despensa na hora (o servidor grava no approved_log)
        if (variation.status === 'approved' && creativeState.value) {
          creativeState.value.approved_log = [
            ...(creativeState.value.approved_log || []),
            {
              week_key: creativeState.value.week_key,
              source: creativeState.value.winners[wi]?.name,
              kind: creativeState.value.winners[wi]?.kind,
              gancho: variation.gancho,
              texto: variation.texto,
              cta: variation.cta,
              approved_at: new Date().toISOString(),
            },
          ];
        }
      }
      useAlert(
        status === 'approved'
          ? '✓ Variação aprovada — guardada na despensa do Estúdio Criativo.'
          : 'Variação recusada — ela fica esmaecida e fora da despensa.'
      );
    }
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Erro ao registrar a revisão.');
  } finally {
    reviewingCreative.value = '';
  }
};

// copiar a variação pronta (gancho + texto + CTA) p/ o Gerenciador/Estúdio
const copyCreativeVariation = async v => {
  await navigator.clipboard.writeText(
    [v.gancho, '', v.texto, '', v.cta].filter(Boolean).join('\n')
  );
  useAlert(
    'Criativo copiado! 📋 Cole no Gerenciador de Anúncios ou no Estúdio.'
  );
};

// semana em dd/mm (week_key vem como data da segunda-feira)
const fmtWeekKey = wk => {
  if (!wk) return '';
  const d = new Date(`${wk}T12:00:00`);
  if (Number.isNaN(d.getTime())) return wk;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

// registro de atividade do Secretário (últimas leituras — entender os números)
const schedulerLog = () => settings.value?.scheduler_log || [];
const showSchedulerLog = ref(false);
const SCHEDULER_OUTCOMES = {
  created: { label: 'criada', class: 'bg-green-500/15 text-green-600' },
  rescheduled: { label: 'reagendada', class: 'bg-amber-500/15 text-amber-600' },
  already: { label: 'já existia', class: 'bg-n-alpha-2 text-n-slate-10' },
  skipped: {
    label: 'sem dia/hora (tarefa criada)',
    class: 'bg-red-500/10 text-red-500',
  },
  // rodada 148: cancelamento + erros visíveis — é assim que se vê quem escapou
  canceled: { label: 'cancelada', class: 'bg-rose-500/15 text-rose-600' },
  cancel_no_match: {
    label: 'pediu cancelar — consulta não achada',
    class: 'bg-red-500/10 text-red-500',
  },
  erro: { label: 'erro na leitura', class: 'bg-red-500/10 text-red-500' },
};
const fmtLogDate = iso =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

// ── Preencher a Agenda com o histórico (Agente de Agendamento) ──
// Varre conversas com confirmação de agendamento e registra as consultas na
// Agenda do sistema — "duplica" a agenda do Google SEM mexer no bot do N8N.
const showBackfillModal = ref(false);
const backfill = ref({ since_days: 90, limit: 100 });
const isBackfilling = ref(false);
const backfillLastRun = () => settings.value?.agenda_backfill_last_run || null;

const runBackfill = async () => {
  isBackfilling.value = true;
  try {
    const { data } = await CrmAPI.agendaBackfill({
      since_days: backfill.value.since_days,
      limit: backfill.value.limit,
    });
    useAlert(data.message || 'Preenchimento iniciado!');
    showBackfillModal.value = false;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Erro ao iniciar o preenchimento.'
    );
  } finally {
    isBackfilling.value = false;
  }
};

// ── Editar | Salvar (rascunho) | Publicar por agente ──
// Os cards abrem TRAVADOS (leitura). Editar destrava; Salvar guarda um
// ✍️ ESTÚDIO DO COPYWRITER: conteúdo multi-formato com estrutura escolhida
const STUDIO_MODALITIES = [
  { key: 'carrossel', label: 'Carrossel' },
  { key: 'reels', label: 'Roteiro de Reels' },
  { key: 'post', label: 'Post / Descrição' },
  { key: 'anuncio', label: 'Anúncio' },
];
const STUDIO_STRUCTURES = [
  { key: 'auto', label: 'Automática' },
  { key: 'kishotenketsu', label: 'Kishōtenketsu' },
  { key: 'storytelling', label: 'Storytelling' },
  { key: 'jornada_heroi', label: 'Jornada do Herói' },
  { key: 'noticia', label: 'Notícia' },
  { key: 'perguntas_respostas', label: 'Perguntas e Respostas' },
  { key: 'dialogo', label: 'Diálogo' },
];
const studio = ref({
  modality: 'carrossel',
  structure: 'auto',
  briefing: '',
  form_id: '',
  generating: false,
  result: null,
});
const studioForms = ref([]);
const loadStudioForms = async () => {
  try {
    const { data } = await CrmAPI.getForms();
    studioForms.value = (data || []).filter(f => f.has_insight);
  } catch {
    studioForms.value = [];
  }
};
const generateStudio = async () => {
  if (!studio.value.briefing.trim()) {
    useAlert('Escreva o briefing do conteúdo.');
    return;
  }
  studio.value.generating = true;
  studio.value.result = null;
  try {
    const { data } = await CrmAPI.generateCopyContent({
      briefing: studio.value.briefing,
      modality: studio.value.modality,
      structure: studio.value.structure,
      form_id: studio.value.form_id || undefined,
    });
    studio.value.result = data;
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Não consegui gerar o conteúdo.');
  } finally {
    studio.value.generating = false;
  }
};
const copyStudioResult = async () => {
  const r = studio.value.result;
  if (!r) return;
  const parts = [
    r.titulo,
    '',
    ...(r.blocos || []).map(b => `${b.rotulo}\n${b.texto}`),
  ];
  if (r.legenda) parts.push('', `LEGENDA:\n${r.legenda}`);
  if (r.hashtags) parts.push('', r.hashtags);
  await navigator.clipboard.writeText(parts.join('\n'));
  useAlert('Conteúdo copiado! 📋');
};
const savingRefs = ref(false);
const saveCopyReferences = async () => {
  savingRefs.value = true;
  try {
    await CrmAPI.updateAi({
      agents: {
        copywriter: { references: aiAgents.value.copywriter.references || '' },
      },
    });
    useAlert('Referências salvas — o Copywriter segue esse estilo em tudo.');
  } catch {
    useAlert('Erro ao salvar as referências.');
  } finally {
    savingRefs.value = false;
  }
};

// Construtor PRO (23/07): referências de estilo + teto de resposta
const savingBuilderPro = ref(false);
const saveBuilderPro = async () => {
  savingBuilderPro.value = true;
  try {
    const tokens = parseInt(aiAgents.value.pagebuilder.max_tokens, 10);
    await CrmAPI.updateAi({
      agents: {
        pagebuilder: {
          references: aiAgents.value.pagebuilder.references || '',
          max_tokens: Number.isFinite(tokens) && tokens >= 1000 ? tokens : '',
        },
      },
    });
    useAlert(
      'Construtor ajustado — as próximas montagens seguem essas rédeas. 🏗️'
    );
  } catch {
    useAlert('Erro ao salvar os ajustes do Construtor.');
  } finally {
    savingBuilderPro.value = false;
  }
};

// SANFONA dos agentes: cabeçalho sempre visível, corpo desce ao clicar
// (são muitos agentes — assim dá para navegar pela página com controle)
const expandedAgents = ref({});
// 🃏 card que está abrindo ganha um anel pulsando na cor dele e a página rola
// até ele (pedido 21/09: "é esse card que está abrindo")
const openingAgent = ref('');
const toggleAgentExpand = key => {
  const next = !expandedAgents.value[key];
  expandedAgents.value = { ...expandedAgents.value, [key]: next };
  if (next) {
    openingAgent.value = key;
    nextTick(() => {
      document
        .getElementById(`cv-agent-${key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        if (openingAgent.value === key) openingAgent.value = '';
      }, 1200);
    });
  }
  // 🎓 Auditor: carrega o resumo de qualidade ao expandir pela 1ª vez (lazy)
  if (
    key === 'auditor' &&
    next &&
    !auditorSummary.value &&
    !loadingAuditorSummary.value
  ) {
    loadAuditorSummary();
  }
  // 🎨 Criativo: carrega os criativos da semana ao expandir pela 1ª vez (lazy)
  if (
    key === 'creative' &&
    next &&
    !creativeState.value &&
    !loadingCreative.value
  ) {
    loadCreativeState();
  }
};

// RASCUNHO que NÃO muda o agente no ar; Publicar é o que passa a valer.
const editingAgent = ref({});
const savingAgent = ref('');
// valores carregados por agente (para o Descartar voltar atrás)
const loadedValues = ref({});

const snapshotAgent = key => {
  const a = aiAgents.value[key];
  const snap = { prompt: a.prompt, model: a.model, effort: a.effort };
  if (key === 'opportunity') {
    snap.watchers = JSON.parse(JSON.stringify(a.watchers || []));
    snap.wait_minutes = a.wait_minutes;
    snap.response_goal_minutes = a.response_goal_minutes;
  }
  if (key === 'instagram') snap.inbox_ids = [...(a.inbox_ids || [])];
  if (RESPONDER_WA_KEYS.includes(key)) {
    snap.inbox_ids = [...(a.inbox_ids || [])];
    snap.stage_ids = [...(a.stage_ids || [])];
    snap.no_card = a.no_card;
    snap.after_booking_stage_id = a.after_booking_stage_id;
    snap.shadow_daily_cap = a.shadow_daily_cap;
    snap.reply_delay_seconds = a.reply_delay_seconds;
    snap.hours_start = a.hours_start;
    snap.hours_end = a.hours_end;
    snap.mode = a.mode;
    snap.live_days = [...(a.live_days || [])];
    snap.task_assignee_id = a.task_assignee_id;
    snap.recent_surgery_days = a.recent_surgery_days;
  }
  // 🎙️ rodada 195
  if (key === 'voice') {
    snap.mode = a.mode;
    snap.stage_ids = [...(a.stage_ids || [])];
    snap.silence_hours = a.silence_hours;
    snap.lookback_days = a.lookback_days;
    snap.max_attempts = a.max_attempts;
    snap.daily_cap = a.daily_cap;
    snap.live_days = [...(a.live_days || [])];
    snap.hours_start = a.hours_start;
    snap.hours_end = a.hours_end;
    snap.handoff_inbox_id = a.handoff_inbox_id;
  }
  if (key === 'comments') {
    snap.fb_page_id = a.fb_page_id;
    snap.ig_user_id = a.ig_user_id;
  }
  if (key === 'harvest') {
    snap.mode = a.mode;
    snap.monthly_size = a.monthly_size;
    snap.cold_days = a.cold_days;
    snap.daily_cap = a.daily_cap;
    snap.day_of_month = a.day_of_month;
    snap.inbox_id = a.inbox_id;
    snap.require_approval = a.require_approval;
    snap.message_preview = a.message_preview;
    snap.template_params = a.template_params
      ? JSON.parse(JSON.stringify(a.template_params))
      : null;
    snap.stage_ids = [...(a.stage_ids || [])];
  }
  if (key === 'manager') snap.drop_pct = a.drop_pct;
  if (key === 'auditor') snap.daily_cap = a.daily_cap;
  if (key === 'creative') {
    snap.winners_count = a.winners_count;
    snap.variations_count = a.variations_count;
  }
  return snap;
};

const startEdit = key => {
  loadedValues.value[key] = snapshotAgent(key);
  editingAgent.value = { ...editingAgent.value, [key]: true };
};

const discardEdit = key => {
  const snap = loadedValues.value[key];
  if (snap) {
    const a = aiAgents.value[key];
    a.prompt = snap.prompt;
    a.model = snap.model;
    a.effort = snap.effort;
    if (key === 'opportunity') {
      a.watchers = JSON.parse(JSON.stringify(snap.watchers || []));
      a.wait_minutes = snap.wait_minutes;
      a.response_goal_minutes = snap.response_goal_minutes;
    }
    if (key === 'instagram') a.inbox_ids = [...(snap.inbox_ids || [])];
    if (RESPONDER_WA_KEYS.includes(key)) {
      a.inbox_ids = [...(snap.inbox_ids || [])];
      a.stage_ids = [...(snap.stage_ids || [])];
      a.no_card = snap.no_card;
      a.after_booking_stage_id = snap.after_booking_stage_id;
      a.shadow_daily_cap = snap.shadow_daily_cap;
      a.reply_delay_seconds = snap.reply_delay_seconds;
      a.hours_start = snap.hours_start;
      a.hours_end = snap.hours_end;
      a.mode = snap.mode || 'shadow';
      a.live_days = [...(snap.live_days || [])];
      a.task_assignee_id = snap.task_assignee_id || '';
      a.recent_surgery_days = snap.recent_surgery_days;
    }
    // 🎙️ rodada 195
    if (key === 'voice') {
      a.mode = snap.mode || 'shadow';
      a.stage_ids = [...(snap.stage_ids || [])];
      a.silence_hours = snap.silence_hours;
      a.lookback_days = snap.lookback_days;
      a.max_attempts = snap.max_attempts;
      a.daily_cap = snap.daily_cap;
      a.live_days = [...(snap.live_days || [])];
      a.hours_start = snap.hours_start;
      a.hours_end = snap.hours_end;
      a.handoff_inbox_id = snap.handoff_inbox_id;
    }
    if (key === 'comments') {
      a.fb_page_id = snap.fb_page_id;
      a.ig_user_id = snap.ig_user_id;
      a.page_access_token = '';
    }
    if (key === 'harvest') {
      a.mode = snap.mode || 'organize';
      a.monthly_size = snap.monthly_size;
      a.cold_days = snap.cold_days;
      a.daily_cap = snap.daily_cap;
      a.day_of_month = snap.day_of_month;
      a.inbox_id = snap.inbox_id;
      a.require_approval = snap.require_approval;
      a.message_preview = snap.message_preview;
      a.template_params = snap.template_params
        ? JSON.parse(JSON.stringify(snap.template_params))
        : null;
      a.stage_ids = [...(snap.stage_ids || [])];
    }
    if (key === 'manager') a.drop_pct = snap.drop_pct;
    if (key === 'auditor') a.daily_cap = snap.daily_cap;
    if (key === 'creative') {
      a.winners_count = snap.winners_count;
      a.variations_count = snap.variations_count;
    }
  }
  editingAgent.value = { ...editingAgent.value, [key]: false };
};

// monta os campos de config do agente (sem o enabled — esse é do interruptor)
const packAgentFields = key => {
  const a = aiAgents.value[key];
  const fields = {
    prompt: (a.prompt || '').trim(),
    model: a.model,
    effort: a.effort,
  };
  if (key === 'opportunity') {
    fields.watchers = (a.watchers || [])
      .filter(w => w.stage_id)
      .map(w => ({
        stage_id: Number(w.stage_id),
        user_id: w.user_id || null,
        lookback_hours: Number(w.lookback_hours) || 24,
      }));
    fields.wait_minutes = Number(a.wait_minutes) || 10;
    fields.response_goal_minutes = Number(a.response_goal_minutes) || 15;
  }
  if (key === 'instagram') {
    fields.inbox_ids = (a.inbox_ids || []).map(Number);
  }
  if (RESPONDER_WA_KEYS.includes(key)) {
    fields.inbox_ids = (a.inbox_ids || []).map(Number);
    fields.stage_ids = (a.stage_ids || []).map(Number);
    fields.no_card = a.no_card !== false;
    fields.after_booking_stage_id = a.after_booking_stage_id
      ? Number(a.after_booking_stage_id)
      : null;
    fields.shadow_daily_cap = Number(a.shadow_daily_cap) || 30;
    // ⏱️ "Responde em": segundos que ele espera depois da mensagem (junta as
    // picadas); o servidor prende entre 3 e 30, padrão 6
    fields.reply_delay_seconds = Math.min(
      30,
      Math.max(3, Number(a.reply_delay_seconds) || 6)
    );
    // 🔁 item 343: quem recebe as tarefas nos três atendentes ('retorno_team' = a equipe dos retornos)
    fields.task_assignee_id =
      a.task_assignee_id === 'retorno_team'
        ? 'retorno_team'
        : Number(a.task_assignee_id) || null;
    if (key === 'atendente_pos_op') {
      // 🩺 janela de cirurgia recente pela Agenda (0 = só pela coluna)
      fields.recent_surgery_days = Math.min(
        365,
        Math.max(0, Number(a.recent_surgery_days) || 0)
      );
    }
    fields.hours_start = a.hours_start || '';
    fields.hours_end = a.hours_end || '';
    // 🟢 rodada 193: ao vivo só dentro da janela (dias + horas); fora dela
    // continua em sombra. live_days vazio = todos os dias.
    fields.mode = a.mode === 'live' ? 'live' : 'shadow';
    fields.live_days = [...new Set((a.live_days || []).map(Number))].sort();
  }
  // 🎙️ rodada 195: Agente de Ligação — mesmos padrões do servidor (24 h,
  // 7 dias, 2 tentativas, 20 ligações/dia); ao vivo só dentro da janela
  if (key === 'voice') {
    fields.mode = a.mode === 'live' ? 'live' : 'shadow';
    fields.stage_ids = (a.stage_ids || []).map(Number);
    fields.silence_hours = Number(a.silence_hours) || 24;
    fields.lookback_days = Number(a.lookback_days) || 7;
    fields.max_attempts = Number(a.max_attempts) || 2;
    fields.daily_cap = Number(a.daily_cap) || 20;
    fields.live_days = [...new Set((a.live_days || []).map(Number))].sort();
    fields.hours_start = a.hours_start || '';
    fields.hours_end = a.hours_end || '';
    fields.handoff_inbox_id = a.handoff_inbox_id
      ? Number(a.handoff_inbox_id)
      : null;
  }
  if (key === 'comments') {
    fields.fb_page_id = (a.fb_page_id || '').trim();
    fields.ig_user_id = (a.ig_user_id || '').trim();
    // token só viaja quando digitado (nunca volta preenchido do servidor)
    if ((a.page_access_token || '').trim())
      fields.page_access_token = a.page_access_token.trim();
  }
  if (key === 'harvest') {
    fields.mode = a.mode === 'send' ? 'send' : 'organize';
    fields.monthly_size = Number(a.monthly_size) || 300;
    fields.cold_days = Number(a.cold_days) || 60;
    fields.daily_cap = Number(a.daily_cap) || 50;
    fields.day_of_month = Math.min(
      28,
      Math.max(1, Number(a.day_of_month) || 1)
    );
    fields.inbox_id = a.inbox_id || null;
    fields.require_approval = a.require_approval !== false;
    fields.message_preview = a.message_preview || '';
    fields.template_params = a.template_params || null;
    fields.stage_ids = (a.stage_ids || []).map(Number);
  }
  if (key === 'manager') {
    fields.drop_pct = Number(a.drop_pct) || 25;
  }
  if (key === 'auditor') {
    fields.daily_cap = Number(a.daily_cap) || 150;
  }
  if (key === 'creative') {
    fields.winners_count = Number(a.winners_count) || 3;
    fields.variations_count = Number(a.variations_count) || 3;
  }
  return fields;
};

// caixas do Atendente Instagram: pílulas de seleção no card
const toggleInstagramInbox = id => {
  const list = aiAgents.value.instagram.inbox_ids || [];
  const idx = list.indexOf(id);
  if (idx === -1) list.push(id);
  else list.splice(idx, 1);
  aiAgents.value.instagram.inbox_ids = [...list];
};

// SALVAR = rascunho: guarda no banco mas o agente continua usando a
// configuração publicada
const saveAgentDraft = async key => {
  savingAgent.value = key;
  try {
    // 🔐 auditoria 12/10 (achado #338): o token da Página NUNCA vai no rascunho
    // (o rascunho volta para o navegador no GET settings); ele só entra ao Publicar
    const draft = packAgentFields(key);
    const tokenTyped = Boolean(draft.page_access_token);
    delete draft.page_access_token;
    await CrmAPI.updateAi({
      agents: { [key]: { draft } },
    });
    aiAgents.value[key].has_draft = true;
    editingAgent.value = { ...editingAgent.value, [key]: false };
    const title = AGENT_META[key].title;
    useAlert(
      tokenTyped
        ? `💾 Rascunho de ${title} salvo — o token da Página NÃO fica no rascunho: cole de novo e Publique para aplicar.`
        : `💾 Rascunho de ${title} salvo — ainda NÃO está valendo. Publique quando quiser aplicar.`
    );
  } catch {
    useAlert('Erro ao salvar o rascunho.');
  } finally {
    savingAgent.value = '';
  }
};

// PUBLICAR = aplica de verdade (e limpa o rascunho)
const publishAgent = async key => {
  savingAgent.value = key;
  try {
    const { data } = await CrmAPI.updateAi({
      agents: { [key]: { ...packAgentFields(key), draft: {} } },
    });
    aiAgents.value[key].has_draft = false;
    editingAgent.value = { ...editingAgent.value, [key]: false };
    loadedValues.value[key] = snapshotAgent(key);
    // 🎙️ rodada 195: o servidor pode devolver um aviso (ex.: o prompt novo não
    // subiu para a ElevenLabs) sem quebrar a publicação
    const sync = data?.voice_sync;
    let warning = data?.warning || data?.aviso;
    if (sync && sync.ok === false)
      warning = `A ElevenLabs não recebeu o prompt novo${sync.error ? `: ${sync.error}` : ''}. Tente "Sincronizar" em Integrações.`;
    useAlert(
      warning
        ? `🚀 ${AGENT_META[key].title} publicado. ⚠️ ${warning}`
        : `🚀 ${AGENT_META[key].title} publicado — vale a partir das próximas análises.`
    );
    if (key === 'voice') store.dispatch('crm/fetchSettings');
  } catch (e) {
    // 422 com frase clara (ex.: "ao vivo exige coluna vigiada / ElevenLabs configurada")
    const msg = e?.response?.data?.error || e?.response?.data?.message;
    useAlert(msg ? `Não publicou: ${msg}` : 'Erro ao publicar o agente.');
  } finally {
    savingAgent.value = '';
  }
};

// ── 🗺️ Mapa de Fluxos (item 170) ──
// "Ver fluxo" no card → aba Fluxos já no fluxograma daquele agente
const openFlow = key => {
  activeTab.value = 'fluxos';
  router.replace({ query: { tab: 'fluxos', flow: key } });
};
// "Abrir configuração" no mapa chega com ?tab=agentes&agent=<chave>:
// expande o card certo e desce até ele
const focusAgent = key => {
  if (
    !key ||
    (!aiAgents.value[key] && !['confirmacao', 'nps_survey'].includes(key))
  )
    return;
  expandedAgents.value = { ...expandedAgents.value, [key]: true };
  nextTick(() => {
    setTimeout(() => {
      document
        .getElementById(`cv-agent-${key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  });
};
watch(
  () => route.query.agent,
  key => {
    if (activeTab.value === 'agentes') focusAgent(key);
  }
);
// 🤖📞 Agente de Ligação: a tela dele é a de Integrações (rota do item 169;
// se ainda não existir nesta build, cai na lista de Integrações)
const openVoiceSettings = () => {
  const name = router.hasRoute('crm_integrations_voice_agent')
    ? 'crm_integrations_voice_agent'
    : 'crm_integrations';
  router.push({ name, params: { accountId: accountId.value } });
};

// ── Interruptor DEFINITIVO do agente ──
// Liga/desliga NA HORA (grava direto no banco, sem depender do botão
// "Salvar agentes"). Desligado = o agente não roda por nenhum caminho:
// botão da conversa, automação de coluna ou cron do Radar.
const togglingAgent = ref('');
const toggleAgent = async key => {
  if (togglingAgent.value) return;
  const agent = aiAgents.value[key];
  const next = !agent.enabled;
  togglingAgent.value = key;
  try {
    await CrmAPI.updateAi({ agents: { [key]: { enabled: next } } });
    agent.enabled = next;
    useAlert(
      next
        ? `✅ ${AGENT_META[key].title} LIGADO — já está valendo.`
        : `⏹ ${AGENT_META[key].title} DESLIGADO — parou em todos os caminhos.`
    );
  } catch (e) {
    // 22/09: mostrar o MOTIVO do servidor (403 sem ser admin, 422 de coluna
    // repetida ou trava do ao vivo) em vez de um erro genérico
    const msg = e?.response?.data?.error || e?.response?.data?.message;
    useAlert(
      msg
        ? `Não consegui mudar o interruptor: ${msg}`
        : 'Erro ao mudar o interruptor do agente.'
    );
  } finally {
    togglingAgent.value = '';
  }
};

// uso/custo dos agentes (relatório)
const aiUsage = ref(null);
const usageByAgent = key =>
  (aiUsage.value?.by_agent || []).find(r => r.key === key) || null;
// 📅 item 321 (03/10, pedido dele: a MESMA régua de período em todos os
// ambientes): o gasto da IA usa a régua padrão — hoje, ontem, 7 dias, semana
// passada, mês, mês passado, 90 dias, ano, personalizado. As setas ◀ ▶
// continuam analisando UM dia (viram um Personalizado de um dia só).
const usagePeriod = ref({ preset: 'last7', ...periodRangeFor('last7') });
const isLoadingUsageDay = ref(false);
const usageRows = computed(() => aiUsage.value?.period?.by_agent || []);
const usageTotals = computed(() => aiUsage.value?.period?.totals || null);
const brDay = iso => {
  const [y, m, d] = String(iso || '').split('-');
  return d ? `${d}/${m}/${y}` : '';
};
const usagePeriodLabel = computed(() => {
  const p = usagePeriod.value;
  const known = PERIOD_PRESETS.find(x => x.key === p.preset);
  if (known) return known.label.toLowerCase();
  return p.from === p.to ? brDay(p.from) : `${brDay(p.from)} a ${brDay(p.to)}`;
});
const loadUsage = async () => {
  isLoadingUsageDay.value = true;
  try {
    const { data } = await CrmAPI.getAiUsage(periodParams(usagePeriod.value));
    aiUsage.value = data;
  } catch {
    // mantém o que já estava na tela
  } finally {
    isLoadingUsageDay.value = false;
  }
};
watch(usagePeriod, loadUsage, { deep: true });
const usageSingleDay = computed(() => {
  const p = usagePeriod.value;
  if (p.preset === 'custom' && p.from && p.from === p.to) return p.from;
  if (p.preset === 'yesterday') return periodRangeFor('yesterday').from;
  return periodDateStr(new Date());
});
const usageAtToday = computed(
  () => usageSingleDay.value >= periodDateStr(new Date())
);
const shiftUsageDay = delta => {
  const base = new Date(`${usageSingleDay.value}T12:00:00`);
  base.setDate(base.getDate() + delta);
  if (base > new Date()) return;
  const day = periodDateStr(base);
  usagePeriod.value = { preset: 'custom', from: day, to: day };
};
// gastos que não são de um card de agente (Gemini): nome legível na lista
const EXTRA_USAGE_TITLES = {
  media_reading: 'Leitura de áudio e imagem (Gemini)',
  creatives_video_transcription: 'Transcrição de vídeo dos criativos (Gemini)',
};
const fmtUsd = v => `US$ ${(v || 0).toFixed(2)}`;
const fmtTokens = v =>
  v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v || 0);

const AGENT_META = {
  conversation: {
    title: 'Analista de Conversas',
    icon: 'i-lucide-message-square-text',
    gradient: 'linear-gradient(135deg, #0F5FA6, #7C3AED)',
    color: '#0F5FA6',
    tag: 'Atendimento',
    description:
      'Lê a conversa e devolve, numa análise só: indicador de interesse (alto/médio/baixo/perdido), resumo, próximo passo, a ETAPA DO SCRIPT CEVICO em que o paciente está e 2-3 FRASES PRONTAS para a atendente copiar e usar.',
    triggers: [
      {
        icon: 'i-lucide-mouse-pointer-click',
        label: 'Botão "Analisar com IA" no painel da conversa',
      },
      { icon: 'i-lucide-zap', label: 'Ação de coluna "Analisar com IA"' },
      { icon: 'i-lucide-rocket', label: 'Resultado aparece no balão do CRM' },
    ],
    suggestion:
      'Tarefa exigente — Opus ou Sonnet dão a melhor leitura de interesse.',
  },
  form: {
    title: 'Analista de Formulários',
    icon: 'i-lucide-clipboard-list',
    gradient: 'linear-gradient(135deg, #5B21B6, #7C3AED)',
    color: '#7C3AED',
    tag: 'Marketing',
    description:
      'Lê todas as respostas de um formulário e sintetiza dores, desejos, objeções e recomendações práticas de marketing e atendimento.',
    triggers: [
      {
        icon: 'i-lucide-sparkles',
        label: 'Botão "Gerar insights com IA" na página Formulários',
      },
    ],
    suggestion: 'Analisa muitas respostas de uma vez — vale usar esforço alto.',
  },
  scheduler: {
    title: 'Secretário da Agenda',
    icon: 'i-lucide-calendar-plus',
    gradient: 'linear-gradient(135deg, #B8860B, #D4A017)',
    color: '#B8860B',
    tag: 'Agenda',
    description:
      'Lê a conversa e ANOTA a consulta na Agenda do sistema: nome, telefone, dia, hora, médico, unidade, valor e observações. Entende reagendamento (atualiza a consulta existente). NUNCA fala com o paciente — quem conversa é o Atendente IA (N8N).',
    triggers: [
      {
        icon: 'i-lucide-zap',
        label: 'Card entra nas colunas escolhidas abaixo',
      },
      {
        icon: 'i-lucide-calendar-days',
        label: 'Anota/reagenda na Agenda do sistema',
      },
      {
        icon: 'i-lucide-list-checks',
        label: 'Sem dia/hora → tarefa "⚠️ Confirmar consulta"',
      },
    ],
    suggestion:
      'Extração estruturada — Sonnet no esforço médio resolve bem e custa menos.',
  },
  opportunity: {
    title: 'Radar de Oportunidades',
    icon: 'i-lucide-radar',
    gradient: 'linear-gradient(135deg, #059669, #4ADE80)',
    color: '#059669',
    tag: 'Não perder venda',
    description:
      'Audita as colunas vigiadas e encontra pacientes QUENTES parados sem atendimento (ex.: quer agendar e ninguém respondeu). Cria o aviso no Meu Painel com nome, motivo e o que a atendente deve fazer.',
    triggers: [
      {
        icon: 'i-lucide-clock',
        label: 'Auditoria automática a cada 10 minutos',
      },
      {
        icon: 'i-lucide-house',
        label: 'Aviso vermelho no Meu Painel do atendente escolhido',
      },
      {
        icon: 'i-lucide-columns-3',
        label: 'Cada coluna vigiada com atendente e janela próprios',
      },
      {
        icon: 'i-lucide-scan-search',
        label: 'Radar pontual: varredura única que não fica ativa',
      },
    ],
    suggestion:
      'Classificação simples e frequente — Haiku mantém o custo baixinho.',
  },
  closing: {
    title: 'Monitor de Fechamento',
    icon: 'i-lucide-hand-coins',
    gradient: 'linear-gradient(135deg, #065F46, #10B981)',
    color: '#065F46',
    tag: 'Cirurgias',
    description:
      'Lê a conversa quando o card entra na coluna escolhida e registra o FECHAMENTO da cirurgia: valor fechado, forma de pagamento e data combinada. Preenche o valor do card (se vazio) e o 💰 aparece na Agenda de Cirurgias — visível só para admin.',
    triggers: [
      {
        icon: 'i-lucide-zap',
        label:
          'Ação de coluna "Adicionar agente de IA" → Monitor de Fechamento',
      },
      {
        icon: 'i-lucide-wallet',
        label: 'Valor + forma de pagamento gravados no contato',
      },
      {
        icon: 'i-lucide-calendar-days',
        label: 'Valor visível na conferência da Agenda de Cirurgias (admin)',
      },
    ],
    suggestion: 'Extração estruturada — Sonnet no esforço médio.',
  },
  sales: {
    title: 'Consultor Comercial',
    icon: 'i-lucide-handshake',
    gradient: 'linear-gradient(135deg, #065F46, #34D399)',
    color: '#047857',
    tag: 'Fechamento',
    description:
      'Dois papéis: AO VIVO, o botão "💼 Ajuda com objeção" no painel da conversa identifica o que está travando o paciente e sugere respostas prontas no tom CEVICO para a vendedora. Para a GESTÃO, analisa as conversas que geraram fechamento de cirurgia e produz insights comerciais (o que funciona, objeções vencidas, recomendações).',
    triggers: [
      {
        icon: 'i-lucide-handshake',
        label: 'Botão "💼 Ajuda com objeção" no painel da conversa',
      },
      {
        icon: 'i-lucide-lightbulb',
        label: 'Botão "Gerar insights comerciais" abaixo (gestão)',
      },
      {
        icon: 'i-lucide-shield',
        label: 'Nunca fala com o paciente — quem decide e envia é a vendedora',
      },
    ],
    suggestion: 'Leitura fina de vendas — Opus no esforço alto vale o custo.',
  },
  atendente_agendamento: {
    title: 'Atendente de Agendamento',
    icon: 'i-lucide-message-square-heart',
    gradient: 'linear-gradient(135deg, #059669, #34D399)',
    color: '#059669',
    tag: 'WhatsApp · ao vivo',
    description:
      'O agente de agendamento DENTRO do sistema, feito para substituir o fluxo do N8N: conversa com o paciente no WhatsApp da recepção até a consulta agendada, seguindo o Roteiro CEVICO (fonte única, no topo desta aba) e oferecendo só vagas LIVRES da Agenda. Nasce em modo SOMBRA: lê as conversas e anota, numa nota interna, o que TERIA respondido — nada chega ao paciente. A tela Sombra compara com o que o N8N respondeu, para você afinar o Roteiro antes de ligar de verdade.',
    triggers: [
      {
        icon: 'i-lucide-message-square',
        label:
          'Mensagem do paciente numa caixa de WhatsApp escolhida (espera os segundos de "Responde em" e junta mensagens picadas)',
      },
      {
        icon: 'i-lucide-columns-3',
        label:
          'A COLUNA do card decide: só fala nas colunas marcadas abaixo (ou sem card)',
      },
      {
        icon: 'i-lucide-calendar-check',
        label:
          'Só oferece vagas livres reais da Agenda; ao vivo, grava a consulta ANTES de confirmar ao paciente e segue na conversa (agendar não pausa)',
      },
      {
        icon: 'i-lucide-eye-off',
        label:
          'Sombra = nota interna, nada ao paciente · Ao vivo = só na janela (dias + horas) escolhida, com o N8N desligado',
      },
    ],
    suggestion:
      'Conversa ao vivo — Sonnet no esforço médio equilibra qualidade e custo. Compare na tela Sombra antes de ligar.',
  },
  // 🩺 item 251: pós-operatório 24h — orientações oficiais + tarefa para a equipe
  atendente_pos_op: {
    title: 'Atendente de Pós-operatório',
    icon: 'i-lucide-heart-pulse',
    gradient: 'linear-gradient(135deg, #BE123C, #FB7185)',
    color: '#BE123C',
    tag: 'WhatsApp · pós-op 24h',
    description:
      'Acolhe quem JÁ OPEROU, a qualquer hora: ardência, areia no olho, colírio (como pingar, nunca muda a receita), banho, cabelo, telas, maquiagem, sol, exercício, viagem, compressas (PRK × LASIK), lente terapêutica e o retorno — com os textos das orientações oficiais da clínica. O que sai disso (dúvida do caso, receita, atestado, insatisfação) vira TAREFA no Meu Painel da responsável; sintoma de alerta (dor forte, perda de visão, secreção, trauma) orienta pronto atendimento, avisa a equipe na hora e o agente se pausa. Lê a cirurgia na Agenda (data, procedimento, há quantos dias). Nasce em SOMBRA.',
    triggers: [
      {
        icon: 'i-lucide-message-square',
        label:
          'Mensagem do paciente numa caixa de WhatsApp escolhida (24h; espera os segundos de "Responde em")',
      },
      {
        icon: 'i-lucide-columns-3',
        label:
          'Nas colunas marcadas (ex.: Cirurgia Realizada, Pós Operatório) OU com cirurgia realizada na Agenda nos últimos N dias',
      },
      {
        icon: 'i-lucide-clipboard-list',
        label:
          'Abre tarefa para a pessoa responsável em tudo que não é dúvida simples; sintoma de alerta = tarefa urgente + aviso no painel',
      },
      {
        icon: 'i-lucide-shield-alert',
        label:
          'Nunca diagnostica, nunca muda colírio, nunca promete resultado. Paciente de parceiro do hub nunca recebe.',
      },
    ],
    suggestion:
      'Maior risco clínico dos atendentes: Sonnet no esforço médio. Deixe em sombra por mais tempo e revise as notas antes de ligar.',
  },
  atendente_pos: {
    title: 'Atendente Pós-agendamento',
    icon: 'i-lucide-life-buoy',
    gradient: 'linear-gradient(135deg, #0F5FA6, #38BDF8)',
    color: '#0F5FA6',
    tag: 'WhatsApp · suporte',
    description:
      'Cuida de quem JÁ TEM consulta marcada, até o dia da consulta: onde fica, o que levar, quanto demora, acompanhante, jejum, "que dia é minha consulta" (ele lê a consulta futura na Agenda), remarcar com vagas reais e cancelar. Não vende, não convida, não manda lembrete. Mesmo Roteiro CEVICO do Atendente de Agendamento, só muda o bloco da etapa. Nasce em SOMBRA e fala só nas colunas dele.',
    triggers: [
      {
        icon: 'i-lucide-message-square',
        label:
          'Mensagem do paciente numa caixa de WhatsApp escolhida (espera os segundos de "Responde em" e junta mensagens picadas)',
      },
      {
        icon: 'i-lucide-columns-3',
        label:
          'Só nas colunas marcadas abaixo (ex.: Agendamento de Consulta, Consulta Confirmada, Desmarcou)',
      },
      {
        icon: 'i-lucide-calendar-clock',
        label:
          'Vê a consulta futura do paciente; remarca só com vagas livres; cancela só se o paciente confirmar',
      },
      {
        icon: 'i-lucide-eye-off',
        label:
          'Sombra = nota interna, nada ao paciente · Ao vivo = remarca/cancela de verdade na Agenda, só na janela escolhida',
      },
    ],
    suggestion:
      'Suporte curto e objetivo — Sonnet no esforço médio resolve bem. Teste no simulador com "preciso remarcar".',
  },
  instagram: {
    title: 'Atendente Direct & Messenger',
    icon: 'i-lucide-instagram',
    gradient: 'linear-gradient(135deg, #C2185B, #7C3AED)',
    color: '#C2185B',
    tag: 'Atendimento ao vivo',
    description:
      'FALA com o paciente nas caixas escolhidas — Instagram Direct E Facebook Messenger — seguindo o script CEVICO (sondagem → autoridade → orçamento → agendamento). Agenda SOZINHO na Agenda interna oferecendo só horários livres, captura o telefone e avisa que a confirmação oficial vai pelo WhatsApp. Roda solto: agendar não pausa. Só PAUSA quando um humano responde na conversa; humano manda 👍 para reativar.',
    triggers: [
      {
        icon: 'i-lucide-instagram',
        label:
          'Mensagem recebida nas caixas escolhidas abaixo (espera ~12s e junta mensagens picadas)',
      },
      {
        icon: 'i-lucide-calendar-check',
        label: 'Agenda direto na Agenda interna (só horários livres reais)',
      },
      {
        icon: 'i-lucide-message-circle',
        label: 'Confirmações oficiais e lembretes seguem pelo WhatsApp',
      },
      {
        icon: 'i-lucide-pause',
        label:
          'Humano respondeu = pausa · 👍 = reativa · urgência = chama humano',
      },
    ],
    suggestion:
      'Conversa ao vivo — Sonnet no esforço médio equilibra qualidade e custo.',
  },
  copywriter: {
    title: 'Copywriter',
    icon: 'i-lucide-pen-line',
    gradient: 'linear-gradient(135deg, #7C3AED, #D4AF37)',
    color: '#7C3AED',
    tag: 'Marketing',
    description:
      'Escreve a copy da casa em VÁRIOS FORMATOS: páginas, carrosséis, roteiros de reels, posts e anúncios — com estruturas validadas (kishōtenketsu, storytelling, jornada do herói, notícia, P&R, diálogo) e as SUAS referências de estilo. Pode usar os INSIGHTS reais dos formulários (dores/desejos/objeções do público).',
    triggers: [
      {
        icon: 'i-lucide-sparkles',
        label: 'Botão "Gerar página com IA" no editor de Páginas',
      },
      {
        icon: 'i-lucide-pen-line',
        label:
          'Estúdio de conteúdo aqui embaixo (carrossel, reels, post, anúncio)',
      },
      {
        icon: 'i-lucide-clipboard-list',
        label: 'Opcional: insights de um formulário alimentam a copy',
      },
      {
        icon: 'i-lucide-shield',
        label: 'Nada vai ao ar sozinho — você revisa e publica',
      },
    ],
    suggestion:
      'Copy é fino — Opus no esforço alto escreve os melhores textos.',
  },
  pagebuilder: {
    title: 'Construtor de Páginas',
    icon: 'i-lucide-layout-template',
    gradient: 'linear-gradient(135deg, #0F5FA6, #D4AF37)',
    color: '#0F5FA6',
    tag: 'Marketing',
    description:
      'Recebe uma COPY PRONTA (do Copywriter, sua ou do time) e MONTA a página no editor: distribui o texto em seções, escolhe os efeitos visuais e gera o SEO — sem reescrever o conteúdo. É a dupla do Copywriter: um escreve, o outro constrói.',
    triggers: [
      {
        icon: 'i-lucide-layout-template',
        label: 'Modo "Montar de copy pronta" no editor de Páginas',
      },
      {
        icon: 'i-lucide-shield',
        label: 'Publicar continua sendo decisão humana (admin)',
      },
    ],
    suggestion: 'Montagem estruturada — Sonnet no esforço médio resolve bem.',
  },
  nps: {
    title: 'Agente de NPS',
    icon: 'i-lucide-smile',
    gradient: 'linear-gradient(135deg, #0D9488, #2DD4BF)',
    color: '#0D9488',
    tag: 'Satisfação',
    description:
      'Lê a conversa do pós-operatório e identifica a NOTA (0-10) que o paciente deu. Etiqueta o contato com a faixa (nps-9-10 / nps-7-8 / nps-0-6) — o Dashboard CRM e o painel do Gestor mostram a % de satisfação a partir daí.',
    triggers: [
      {
        icon: 'i-lucide-zap',
        label:
          'Ação de coluna "Adicionar agente de IA" → Agente de NPS (ex.: coluna Pós-Operatório)',
      },
      {
        icon: 'i-lucide-tags',
        label: 'Etiquetas nps-9-10 / nps-7-8 / nps-0-6 no contato',
      },
      {
        icon: 'i-lucide-bar-chart-3',
        label: 'Bloco "Satisfação (NPS)" no Dashboard CRM',
      },
    ],
    suggestion: 'Ler uma nota é simples — Haiku resolve baratinho.',
  },
  comments: {
    title: 'Respondedor de Comentários',
    icon: 'i-lucide-message-square-reply',
    gradient: 'linear-gradient(135deg, #7C3AED, #DB2777)',
    color: '#7C3AED',
    tag: 'Atendimento ao vivo',
    description:
      'Responde os COMENTÁRIOS públicos dos posts e anúncios do Instagram e Facebook: agradece elogios, tira dúvidas leves e convida pro direct/WhatsApp — sem falar preço nem dado clínico em público. Reclamação séria ou urgência = marca pro humano e fica quieto. Corrige o gargalo de atendimento dos comentários.',
    triggers: [
      {
        icon: 'i-lucide-clock',
        label: 'Varre os comentários novos a cada 5 minutos',
      },
      {
        icon: 'i-lucide-message-square-reply',
        label: 'Responde em público no tom CEVICO (curto e caloroso)',
      },
      {
        icon: 'i-lucide-hand',
        label: 'Caso delicado → não responde e marca pro humano',
      },
      {
        icon: 'i-lucide-key-round',
        label: 'Precisa do token da Página da Meta (cole aqui embaixo)',
      },
    ],
    suggestion: 'Comentário público é a vitrine — Sonnet no esforço médio.',
  },
  mentor: {
    title: 'Mentor do Time',
    icon: 'i-lucide-graduation-cap',
    gradient: 'linear-gradient(135deg, #C2410C, #FB923C)',
    color: '#C2410C',
    tag: 'Evolução do time',
    description:
      'Capta os dados de uso da semana de CADA pessoa (tempo de resposta, conversas resolvidas, mensagens, tarefas) e entrega um feedback individual no Meu Painel: o ponto forte, O PONTO FRACO exato a corrigir e 2-3 soluções simples que geram grande resultado. Compara com a mediana do time sem expor ninguém.',
    triggers: [
      {
        icon: 'i-lucide-calendar-check',
        label: 'Toda segunda de manhã, analisando a semana que acabou',
      },
      {
        icon: 'i-lucide-house',
        label: 'Feedback individual no Meu Painel de cada pessoa',
      },
      { icon: 'i-lucide-eye', label: 'Admin vê o feedback do time inteiro' },
      {
        icon: 'i-lucide-shield',
        label: 'Nunca fala com paciente — só lê números de uso',
      },
    ],
    suggestion:
      'Feedback humano e fino, 1x por semana — Sonnet no esforço alto.',
  },
  harvest: {
    title: 'Colheitadeira da Base',
    icon: 'i-lucide-wheat',
    gradient: 'linear-gradient(135deg, #CA8A04, #FACC15)',
    color: '#CA8A04',
    tag: 'Reativação',
    description:
      'Pontua a base fria todo mês e reativa os leads mais propensos, um a um, com aprovação sua. A IA escreve um gancho pessoal para cada paciente e os envios saem aos poucos, dentro do teto diário — anti-bloqueio.',
    triggers: [
      {
        icon: 'i-lucide-calendar-clock',
        label: 'Todo mês, no dia escolhido, gera a prévia da colheita',
      },
      {
        icon: 'i-lucide-list-checks',
        label: 'Você revisa a lista e aprova antes de qualquer envio',
      },
      {
        icon: 'i-lucide-message-circle',
        label: 'Envia pelo WhatsApp com gancho pessoal por lead',
      },
      {
        icon: 'i-lucide-shield',
        label: 'Teto de envios por dia — menos é mais seguro',
      },
    ],
    suggestion:
      'Pontuar a base e escrever ganchos — Sonnet no esforço médio equilibra bem.',
  },
  manager: {
    title: 'Gestor Autônomo',
    icon: 'i-lucide-gauge',
    gradient: 'linear-gradient(135deg, #0F5FA6, #3B82F6)',
    color: '#0F5FA6',
    tag: 'Gestão',
    description:
      'Lê o funil todos os dias contra a média de 12 semanas, abre tarefas nos desvios e escreve o briefing do dia. Você abre o Meu Painel e já sabe onde o processo está vazando — sem planilha.',
    triggers: [
      {
        icon: 'i-lucide-clock',
        label: 'Roda todos os dias, sozinho, de manhã',
      },
      {
        icon: 'i-lucide-trending-down',
        label: 'Compara cada indicador com a média de 12 semanas',
      },
      {
        icon: 'i-lucide-list-checks',
        label: 'Desvio encontrado → abre tarefa para o time agir',
      },
      {
        icon: 'i-lucide-house',
        label: 'Briefing do dia no Meu Painel (admin)',
      },
    ],
    suggestion:
      'Leitura fina de números todo dia — Sonnet no esforço alto escreve o melhor briefing.',
  },
  auditor: {
    title: 'Auditor de Conversas',
    icon: 'i-lucide-clipboard-check',
    gradient: 'linear-gradient(135deg, #0F766E, #2DD4BF)',
    color: '#0F766E',
    tag: 'Evolução do time',
    description:
      'Dá nota 0-10 nas conversas de ontem contra o script — coaching contínuo por atendente. Todo dia audita o atendimento da véspera e monta o ranking do time com as falhas mais comuns.',
    triggers: [
      {
        icon: 'i-lucide-clock',
        label: 'Roda todo dia, sozinho, auditando as conversas de ontem',
      },
      {
        icon: 'i-lucide-list-checks',
        label: 'Nota 0-10 por conversa contra o script da clínica',
      },
      {
        icon: 'i-lucide-trophy',
        label: 'Ranking por atendente + falhas mais comuns do time',
      },
      {
        icon: 'i-lucide-shield',
        label: 'Nunca fala com o paciente — só lê e avalia',
      },
    ],
    suggestion:
      'Avaliar contra o script é leitura fina — Sonnet no esforço médio equilibra qualidade e custo.',
  },
  creative: {
    title: 'Criativo Perpétuo',
    icon: 'i-lucide-wand-sparkles',
    gradient: 'linear-gradient(135deg, #9D174D, #DB2777)',
    color: '#DB2777',
    tag: 'Marketing',
    description:
      'Toda semana encontra os termos do Google e os anúncios do Meta que mais viraram cirurgia — na jornada real do banco — e escreve variações de copy prontas para os próximos anúncios. Você só aprova; as aprovadas ficam guardadas para o Estúdio Criativo.',
    triggers: [
      {
        icon: 'i-lucide-calendar-clock',
        label: 'Toda segunda 08:30 gera as variações da semana',
      },
      {
        icon: 'i-lucide-trophy',
        label:
          'Vencedores reais: o que mais virou consulta e cirurgia (90 dias)',
      },
      {
        icon: 'i-lucide-list-checks',
        label: 'Você aprova ou recusa cada variação — nada vai ao ar sozinho',
      },
      {
        icon: 'i-lucide-copy',
        label: 'Aprovou? Copia e cola no Gerenciador de Anúncios / Estúdio',
      },
    ],
    suggestion:
      'Copy é fino — Sonnet no esforço alto escreve as melhores variações.',
  },
  // 🎙️ rodada 195: liga para leads NÃO RESPONSIVOS (fala pela ElevenLabs;
  // voz/número em Integrações). Por texto ele se testa aqui mesmo.
  voice: {
    title: 'Agente de Ligação',
    icon: 'i-lucide-phone-call',
    gradient: 'linear-gradient(135deg, #7C3AED, #DB2777)',
    color: '#7C3AED',
    tag: 'Atendimento',
    description:
      'Atende quem liga para o número dela e liga para leads parados nas colunas escolhidas depois de N horas sem resposta; marca, remarca e confirma consultas na Agenda, responde dúvidas com o Roteiro CEVICO e passa para a equipe quando precisa. Fala pela ElevenLabs como assistente virtual. Aqui mora o "quando" e "para quem" da ligação automática; voz, nome, frases e o card depois da ligação ficam em Integrações → Agente de Ligação.',
    triggers: [
      {
        icon: 'i-lucide-clock',
        label: 'Lead numa coluna vigiada sem responder há N horas',
      },
      {
        icon: 'i-lucide-phone-outgoing',
        label: 'Liga só na janela (dias + horas), no máximo N por dia',
      },
      {
        icon: 'i-lucide-calendar-check',
        label: 'Atendeu e marcou → entra na Agenda + confirmação no WhatsApp',
      },
      {
        icon: 'i-lucide-message-circle',
        label: 'Não pode falar → segue por escrito na caixa do WhatsApp',
      },
    ],
    suggestion:
      'Comece em Sombra: veja "quem ligaria hoje" e teste por texto antes de ligar de verdade.',
  },
};

// ── Seções da aba "agentes": os 16 agentes agrupados por área ──
// (são muitos — o agrupamento dá o mapa; card/sanfona continuam os mesmos)
// variáveis --cv* do kit com a cor do agente (ícone, chips, botões e
// interruptor do card ficam na cor DELE)
const agentVars = key => {
  const meta = AGENT_META[key];
  if (!meta) return {};
  return {
    '--cv': meta.color,
    '--cv-rgb': hexToRgb(meta.color),
    '--cv-deep': meta.color,
    '--cv-deep-rgb': hexToRgb(meta.color),
    '--cv-grad': meta.gradient,
    '--cv-grad-2': meta.gradient,
    '--cv-grad-3': meta.gradient,
  };
};
const AGENT_GROUPS = [
  {
    title: 'Atendimento ao paciente',
    icon: '🗣️',
    keys: [
      'atendente_agendamento',
      'atendente_pos',
      'atendente_pos_op',
      'conversation',
      'scheduler',
      'instagram',
      'comments',
      'nps',
      'voice',
    ],
  },
  {
    title: 'Vendas e fechamento',
    icon: '💰',
    keys: ['sales', 'closing', 'opportunity', 'form'],
  },
  {
    title: 'Marketing e aquisição',
    icon: '📣',
    keys: ['copywriter', 'pagebuilder', 'creative', 'harvest'],
  },
  {
    title: 'Gestão e evolução do time',
    icon: '📈',
    keys: ['manager', 'auditor', 'mentor'],
  },
];
// agente novo que ainda não entrou em nenhum grupo cai em "Outros" (à prova
// de futuro — nada some da tela)
const agentGroups = computed(() => {
  const known = AGENT_GROUPS.flatMap(g => g.keys);
  const groups = AGENT_GROUPS.map(g => ({
    ...g,
    keys: g.keys.filter(k => aiAgents.value[k]),
  }));
  const leftovers = Object.keys(aiAgents.value).filter(k => !known.includes(k));
  if (leftovers.length)
    groups.push({ title: 'Outros', icon: '🧩', keys: leftovers });
  return groups.filter(g => g.keys.length);
});
// mesmo formato do v-for original — (agent, key) — só que restrito ao grupo
const groupAgents = group =>
  Object.fromEntries(group.keys.map(k => [k, aiAgents.value[k]]));

// opções de modelo/esforço por agente ('' = usa o RECOMENDADO do agente)
const AGENT_MODELS = [
  { value: 'claude-opus-4-8', label: 'Opus 4.8 — melhor análise' },
  { value: 'claude-sonnet-5', label: 'Sonnet 5 — equilíbrio' },
  { value: 'claude-haiku-4-5', label: 'Haiku 4.5 — mais barato e rápido' },
];
const AGENT_EFFORTS = [
  { value: 'low', label: 'Baixo — rápido e econômico' },
  { value: 'medium', label: 'Médio — equilíbrio' },
  { value: 'high', label: 'Alto' },
  { value: 'max', label: 'Máximo — melhor análise possível' },
];

const MODEL_SHORT = {
  'claude-opus-4-8': 'Opus 4.8',
  'claude-sonnet-5': 'Sonnet 5',
  'claude-haiku-4-5': 'Haiku 4.5',
};
const EFFORT_SHORT = {
  low: 'baixo',
  medium: 'médio',
  high: 'alto',
  xhigh: 'muito alto',
  max: 'máximo',
};

// recomendação do sistema para cada agente (vem do backend)
const recommendedFor = key => settings.value?.ai?.agents?.[key] || {};

const recommendedModelLabel = key => {
  const m = recommendedFor(key).recommended_model;
  return `Recomendado — ${MODEL_SHORT[m] || 'padrão'}`;
};
const recommendedEffortLabel = key => {
  const rec = recommendedFor(key);
  if (
    (rec.recommended_model || '').includes('haiku') &&
    !rec.recommended_effort
  )
    return 'Recomendado — não se aplica (Haiku)';
  return `Recomendado — ${EFFORT_SHORT[rec.recommended_effort] || 'alto'}`;
};

// o que o agente vai usar de verdade (escolha própria > recomendado > global)
const resolvedModel = (key, agent) => {
  const m =
    agent.model ||
    recommendedFor(key).recommended_model ||
    settings.value?.ai?.model ||
    'claude-opus-4-8';
  return MODEL_SHORT[m] || m;
};
const resolvedEffort = (key, agent) => {
  const m =
    agent.model ||
    recommendedFor(key).recommended_model ||
    settings.value?.ai?.model ||
    '';
  if (m.includes('haiku')) return 'não se aplica (Haiku)';
  const e =
    agent.effort ||
    recommendedFor(key).recommended_effort ||
    settings.value?.ai?.effort ||
    'high';
  return EFFORT_SHORT[e] || e;
};

// colunas de todos os funis (para o Radar escolher o que vigiar)
const allStages = ref([]);
const loadStages = async () => {
  await store.dispatch('crm/fetchPipelines');
  const pipelines = store.getters['crm/getPipelines'] || [];
  allStages.value = pipelines.flatMap(p =>
    (p.stages || []).map(s => ({ id: s.id, name: s.name, pipeline: p.name }))
  );
};

const loadAgents = async () => {
  await store.dispatch('crm/fetchSettings');
  const a = settings.value?.ai?.agents || {};
  // se há rascunho salvo, os CAMPOS mostram o rascunho (continuar de onde
  // parou); o agente no ar continua usando a config publicada até Publicar
  const load = key => {
    const draft = a[key]?.draft || null;
    return {
      // opt-in: agente só aparece LIGADO se foi ligado de propósito
      enabled: a[key]?.enabled === true,
      prompt: (draft ? draft.prompt : a[key]?.prompt) || '',
      model: (draft ? draft.model : a[key]?.model) || '',
      effort: (draft ? draft.effort : a[key]?.effort) || '',
      has_draft: !!draft,
      default_prompt: a[key]?.default_prompt || '',
    };
  };
  // compat: config antiga (stage_ids soltos) vira vigia sem direcionamento
  const legacyWatchers = (a.opportunity?.stage_ids || []).map(id => ({
    stage_id: id,
    user_id: null,
    lookback_hours: a.opportunity?.lookback_hours || 24,
  }));
  const oppDraft = a.opportunity?.draft || null;
  const harvestDraft = a.harvest?.draft || null;
  const managerDraft = a.manager?.draft || null;
  const auditorDraft = a.auditor?.draft || null;
  const creativeDraft = a.creative?.draft || null;
  // 🗣️ atendentes do WhatsApp (A e B): mesmos campos, rascunho vence o publicado nos campos
  const loadResponder = key => {
    const draft = a[key]?.draft || null;
    const real = a[key] || {};
    return {
      ...load(key),
      mode: (draft?.mode ?? real.mode) || 'shadow',
      live_days: [...((draft?.live_days ?? real.live_days) || [])].map(Number),
      inbox_ids: [...((draft?.inbox_ids ?? real.inbox_ids) || [])],
      stage_ids: [...((draft?.stage_ids ?? real.stage_ids) || [])],
      no_card:
        key === 'atendente_agendamento'
          ? (draft?.no_card ?? real.no_card ?? true)
          : false,
      after_booking_stage_id:
        (draft?.after_booking_stage_id ?? real.after_booking_stage_id) || '',
      shadow_daily_cap:
        (draft?.shadow_daily_cap ?? real.shadow_daily_cap) || 30,
      reply_delay_seconds:
        (draft?.reply_delay_seconds ?? real.reply_delay_seconds) || 6,
      hours_start: (draft?.hours_start ?? real.hours_start) || '',
      hours_end: (draft?.hours_end ?? real.hours_end) || '',
      task_assignee_id:
        (draft?.task_assignee_id ?? real.task_assignee_id) || '',
      recent_surgery_days:
        draft?.recent_surgery_days ?? real.recent_surgery_days ?? 60,
    };
  };
  aiAgents.value = {
    conversation: load('conversation'),
    form: load('form'),
    scheduler: load('scheduler'),
    closing: load('closing'),
    nps: load('nps'),
    sales: load('sales'),
    copywriter: {
      ...load('copywriter'),
      references: a.copywriter?.references || '',
    },
    pagebuilder: {
      ...load('pagebuilder'),
      references: a.pagebuilder?.references || '',
      max_tokens: a.pagebuilder?.max_tokens || '',
    },
    instagram: {
      ...load('instagram'),
      inbox_ids: [...(a.instagram?.inbox_ids || [])],
    },
    atendente_agendamento: loadResponder('atendente_agendamento'),
    atendente_pos: loadResponder('atendente_pos'),
    atendente_pos_op: loadResponder('atendente_pos_op'),
    opportunity: {
      ...load('opportunity'),
      watchers: oppDraft?.watchers?.length
        ? oppDraft.watchers
        : a.opportunity?.watchers?.length
          ? a.opportunity.watchers
          : legacyWatchers,
      wait_minutes: oppDraft?.wait_minutes || a.opportunity?.wait_minutes || 10,
      response_goal_minutes:
        oppDraft?.response_goal_minutes ||
        a.opportunity?.response_goal_minutes ||
        15,
    },
    mentor: load('mentor'),
    // 🎙️ rodada 195: campos do Agente de Ligação (rascunho vence o publicado
    // nos campos); a caixa do WhatsApp também mora em settings.voice
    voice: (() => {
      const draft = a.voice?.draft || null;
      const real = a.voice || {};
      const pick = (k, fallback) => draft?.[k] ?? real[k] ?? fallback;
      return {
        ...load('voice'),
        mode: pick('mode', '') || 'shadow',
        stage_ids: [...(pick('stage_ids', []) || [])].map(Number),
        silence_hours: pick('silence_hours', '') || 24,
        lookback_days: pick('lookback_days', '') || 7,
        max_attempts: pick('max_attempts', '') || 2,
        daily_cap: pick('daily_cap', '') || 20,
        live_days: [...(pick('live_days', []) || [])].map(Number),
        hours_start: pick('hours_start', '') || '',
        hours_end: pick('hours_end', '') || '',
        handoff_inbox_id:
          pick('handoff_inbox_id', '') ||
          settings.value?.voice?.handoff_inbox_id ||
          '',
      };
    })(),
    comments: {
      ...load('comments'),
      page_access_token: '',
      fb_page_id: a.comments?.fb_page_id || '',
      ig_user_id: a.comments?.ig_user_id || '',
      page_token_set: a.comments?.page_token_set === true,
    },
    harvest: {
      ...load('harvest'),
      // null/vazio = organize (novo padrão): a IA etiqueta em vez de enviar
      mode: (harvestDraft?.mode ?? a.harvest?.mode) || 'organize',
      monthly_size: harvestDraft?.monthly_size ?? a.harvest?.monthly_size ?? '',
      cold_days: harvestDraft?.cold_days ?? a.harvest?.cold_days ?? '',
      daily_cap: harvestDraft?.daily_cap ?? a.harvest?.daily_cap ?? '',
      day_of_month: harvestDraft?.day_of_month ?? a.harvest?.day_of_month ?? '',
      inbox_id: harvestDraft?.inbox_id ?? a.harvest?.inbox_id ?? null,
      require_approval:
        (harvestDraft?.require_approval ?? a.harvest?.require_approval) !==
        false,
      message_preview:
        (harvestDraft?.message_preview ?? a.harvest?.message_preview) || '',
      template_params:
        (harvestDraft?.template_params ?? a.harvest?.template_params) || null,
      stage_ids: [...((harvestDraft?.stage_ids ?? a.harvest?.stage_ids) || [])],
    },
    manager: {
      ...load('manager'),
      drop_pct: managerDraft?.drop_pct ?? a.manager?.drop_pct ?? '',
    },
    auditor: {
      ...load('auditor'),
      daily_cap: auditorDraft?.daily_cap ?? a.auditor?.daily_cap ?? '',
    },
    creative: {
      ...load('creative'),
      winners_count:
        creativeDraft?.winners_count ?? a.creative?.winners_count ?? '',
      variations_count:
        creativeDraft?.variations_count ?? a.creative?.variations_count ?? '',
    },
  };
  Object.keys(aiAgents.value).forEach(key => {
    loadedValues.value[key] = snapshotAgent(key);
  });
  schedulerStageIds.value = [...(settings.value?.scheduler_stage_ids || [])];
  const saved = settings.value?.agent_stage_ids || {};
  STAGE_AGENTS.forEach(a => {
    agentStageIds.value[a] = [...(saved[a] || [])];
  });
  // relatório de gastos + colunas para o Radar + colheita do mês (em paralelo)
  loadStages().catch(() => {});
  loadHarvestStatus();
  loadUsage();
};

const aiConfigured = ref(false);

// ── Modo Programação: todas as automações de coluna num lugar ──
const columnAutomations = ref([]);
const loadingColumnAutomations = ref(false);

const loadColumnAutomations = async () => {
  loadingColumnAutomations.value = true;
  try {
    await store.dispatch('crm/fetchPipelines');
    const pipelines = store.getters['crm/getPipelines'] || [];
    const all = [];
    for (const p of pipelines) {
      for (const s of p.stages || []) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await CrmAPI.getAutomations(p.id, s.id);
        (data || [])
          .filter(a => a.action_type) // só automações de coluna (não réguas)
          .forEach(a =>
            all.push({ ...a, stage_name: s.name, pipeline_name: p.name })
          );
      }
    }
    columnAutomations.value = all;
  } catch {
    columnAutomations.value = [];
  } finally {
    loadingColumnAutomations.value = false;
  }
};

const ACTION_LABELS = {
  webhook: 'Disparar webhook',
  n8n_flow: 'Acionar fluxo n8n',
  apply_label: 'Aplicar etiqueta',
  move_card: 'Mover para coluna',
  log_timeline: 'Registrar na timeline',
  notify_team: 'Notificar equipe',
  meta_ads_event: 'Evento Meta Ads',
  google_ads_conversion: 'Conversão Google Ads',
  send_form: 'Enviar formulário',
  ai_analyze: 'Agente de IA: Analista de Conversas',
  schedule_appointment: 'IA: Secretário da Agenda',
  set_value: 'Adicionar preço no card',
};

const openProgrammingMode = () => {
  window.location.href = `/app/accounts/${accountId.value}/crm?programming=1`;
};

// ── Resultados das automações — régua de período PADRÃO CEVICO (06/08) ──
const resultsPeriod = ref({ preset: 'today', from: '', to: '' });
const resultsData = ref(null);
const loadingResults = ref(false);

const TRIGGER_LABELS = {
  card_entered: 'Card entrou na coluna',
  card_left: 'Card saiu da coluna',
  card_stalled: 'Card parado por X tempo',
  label_added: 'Etiqueta adicionada',
  label_removed: 'Etiqueta removida',
  message_created: 'Mensagem criada',
  value_added: 'Valor adicionado no card',
};

const loadResults = async () => {
  loadingResults.value = true;
  try {
    const { data } = await CrmAPI.getAutomationsDashboard({
      preset: resultsPeriod.value.preset,
      ...(resultsPeriod.value.preset === 'custom'
        ? { from: resultsPeriod.value.from, to: resultsPeriod.value.to }
        : {}),
    });
    resultsData.value = data;
  } catch {
    resultsData.value = null;
    useAlert('Erro ao carregar os resultados das automações.');
  } finally {
    loadingResults.value = false;
  }
};

watch(resultsPeriod, loadResults, { deep: true });

// carrega quando a aba abre pela primeira vez
watch(activeTab, tab => {
  if (tab === 'resultados' && !resultsData.value && !loadingResults.value)
    loadResults();
});

const maxTimelineFired = () =>
  Math.max(1, ...(resultsData.value?.timeline || []).map(d => d.fired));

const fmtDay = iso => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const fmtLastFired = iso => {
  if (!iso) return 'nunca no período';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ── Réguas de mensagem (mesmo motor da Campanha WhatsApp) ──
const automations = ref([]);
const loadingReguas = ref(true);

const loadReguas = async () => {
  loadingReguas.value = true;
  try {
    automations.value = await store.dispatch('crm/fetchMessageAutomations');
  } catch {
    useAlert('Erro ao carregar réguas');
  } finally {
    loadingReguas.value = false;
  }
};

const toggleRegua = async a => {
  try {
    await store.dispatch('crm/updateMessageAutomation', {
      id: a.id,
      active: !a.active,
    });
    a.active = !a.active;
  } catch {
    useAlert('Erro ao atualizar');
  }
};

const deleteRegua = async a => {
  try {
    await store.dispatch('crm/deleteMessageAutomation', a.id);
    automations.value = automations.value.filter(x => x.id !== a.id);
    useAlert('Régua excluída');
  } catch {
    useAlert('Erro ao excluir');
  }
};

const goToCampaign = () => {
  router.push({
    name: 'crm_campaigns',
    params: { accountId: accountId.value },
  });
};

// ── Regras da caixa de entrada (Automation nativa do Chatwoot) ──
// A aba mostra e liga/desliga as regras; criar/editar continua no editor
// completo nativo (condições e ações complexas ficam lá).
const nativeRules = useMapGetter('automations/getAutomations');
const loadingNativeRules = ref(false);
const loadNativeRules = async () => {
  loadingNativeRules.value = true;
  await store.dispatch('automations/get');
  loadingNativeRules.value = false;
};
const toggleNativeRule = async rule => {
  try {
    await store.dispatch('automations/update', {
      ...rule,
      active: !rule.active,
    });
  } catch {
    useAlert('Erro ao atualizar a regra');
  }
};
const openNativeEditor = () =>
  router.push({
    name: 'automation_list',
    params: { accountId: accountId.value },
  });
const RULE_EVENT_LABELS = {
  conversation_created: 'Conversa criada',
  conversation_updated: 'Conversa atualizada',
  conversation_opened: 'Conversa reaberta',
  message_created: 'Mensagem criada',
};

// ── Robôs de follow-up ──
const bots = ref([]);
const loadingBots = ref(true);

const loadBots = async () => {
  loadingBots.value = true;
  try {
    bots.value = await store.dispatch('crm/fetchFollowupBots');
    if (!accountLabels.value.length)
      store.dispatch('labels/get').catch(() => {});
  } catch {
    useAlert('Erro ao carregar robôs');
  } finally {
    loadingBots.value = false;
  }
};

const showBotModal = ref(false);
const editingBot = ref(null);

const openCreateBot = () => {
  editingBot.value = null;
  showBotModal.value = true;
};
const openEditBot = bot => {
  editingBot.value = bot;
  showBotModal.value = true;
};
const onBotSaved = async () => {
  showBotModal.value = false;
  editingBot.value = null;
  await loadBots();
};

const toggleBot = async bot => {
  try {
    const data = await store.dispatch('crm/updateFollowupBot', {
      id: bot.id,
      active: !bot.active,
    });
    bot.active = data.active;
  } catch {
    useAlert('Erro ao atualizar');
  }
};

const deleteBotConfirmId = ref(null);
const deleteBot = async bot => {
  try {
    await store.dispatch('crm/deleteFollowupBot', bot.id);
    bots.value = bots.value.filter(b => b.id !== bot.id);
    deleteBotConfirmId.value = null;
    useAlert('Robô excluído');
  } catch {
    useAlert('Erro ao excluir');
  }
};

// registro de atividade do robô — mostra por que ele está (ou não) enviando
const expandedBotLog = ref(null);
const REASON_LABELS = {
  aguardando_prazo: 'aguardando o prazo da cutucada',
  aguardando_espacamento:
    'aguardando o espaçamento da cadência (não sai empilhada)',
  paciente_falou_ultimo: 'paciente falou por último (vez do atendimento)',
  cadencia_completa: 'já recebeu todas as cutucadas',
  etiquetas: 'barrado pelo filtro de etiquetas',
  etiqueta_de_encerramento:
    'etiqueta de encerramento (nao_perturbe / perda_* / lista da conta)',
  protegida_por_etiqueta: 'etapa protegida por etiqueta (não enviada)',
  janela_whatsapp:
    'fora da janela de 24h do WhatsApp — texto simples não entrega (use mensagem modelo)',
  momento_perdido: 'cutucada vencida há horas — descartada (anti-rajada)',
  pausado_para_paciente:
    'follow-up pausado para o paciente (trava da atendente)',
  trava_intervalo_minimo: 'trava: 30 min mínimos entre cutucadas',
  trava_teto_diario: 'trava: teto de 4 cutucadas por dia',
  trava_cadencia_completa: 'trava: cadência já saiu inteira (mensagens reais)',
  // 🚦 item 345: número no teto do ritmo de envio — espera a próxima rodada
  ritmo: 'na fila do ritmo de envio (sai nas próximas rodadas)',
  erro: 'erro ao enviar',
};
const reasonLine = run =>
  Object.entries(run?.reasons || {})
    .map(([k, v]) => `${v} ${REASON_LABELS[k] || k}`)
    .join(' · ');
// por que o robô pode estar parado — aviso bem visível no card
const botWarning = bot => {
  if (bot.window_status === 'janela_encerrada')
    return `⏸ A data "para em" já passou (${fmtLogDate(bot.ends_at)}) — o robô NÃO está enviando nada. Edite o robô e limpe ou estenda a janela.`;
  if (bot.window_status === 'ainda_nao_comecou')
    return `⏳ A janela "começa em" ainda não chegou (${fmtLogDate(bot.starts_at)}) — o robô só envia a partir dela.`;
  return '';
};

// unidade correta por etapa: minutos ÷ 60, dias × 24 (igual ao backend)
const delayLabel = step => {
  let hours;
  if (step.delay_value != null) {
    const v = Number(step.delay_value);
    if (step.delay_unit === 'days') hours = v * 24;
    else if (step.delay_unit === 'minutes') hours = v / 60;
    else hours = v;
  } else {
    hours = Number(step.delay_hours);
  }
  if (hours < 1) return `${Math.round(hours * 60)}min`;
  if (hours < 24) return `${Math.round(hours * 10) / 10}h`;
  return `${Math.round(hours / 24)}d`;
};

// 🕐 HORÁRIO DE ENVIO das cutucadas (item 147) — vale pra todos os robôs da
// conta. Padrão 08h–20h; 0–24 = a qualquer hora do dia (aproveita a janela
// de 24h do WhatsApp em vez de empilhar as cutucadas pro dia seguinte).
const followupHours = ref({ start: 8, end: 20 });
const savingFollowupHours = ref(false);
watch(
  settings,
  s => {
    const h = s?.followup_hours;
    if (h && h.start != null)
      followupHours.value = { start: Number(h.start), end: Number(h.end) };
  },
  { immediate: true }
);
const followupHoursLabel = computed(() => {
  const { start, end } = followupHours.value;
  if (start === 0 && end === 24) return '24 horas por dia';
  return `das ${String(start).padStart(2, '0')}h às ${end === 24 ? '00h' : `${String(end).padStart(2, '0')}h`}`;
});
const saveFollowupHours = async (start, end) => {
  // janela sempre válida: o fim precisa vir depois do começo
  const s = Math.min(Math.max(start, 0), 23);
  const e = Math.min(Math.max(end, s + 1), 24);
  followupHours.value = { start: s, end: e };
  savingFollowupHours.value = true;
  try {
    await CrmAPI.updateFollowupHours({ start: s, end: e });
    useAlert(
      s === 0 && e === 24
        ? 'Cutucadas liberadas 24 horas por dia'
        : `Cutucadas saem ${followupHoursLabel.value}`
    );
  } catch {
    useAlert('Erro ao salvar o horário de envio');
  } finally {
    savingFollowupHours.value = false;
  }
};

// 🚦 RITMO DE ENVIO (item 345) — até N cutucadas a cada M minutos POR NÚMERO
// (somando todos os robôs e a reativação). Às 08h tudo o que venceu de
// madrugada saía de uma vez e parecia spam para a Meta. Quem tem a janela de
// 24h do WhatsApp mais perto de fechar sai primeiro. 0 = sem limite.
const followupPace = ref({ per_window: 5, minutes: 5 });
const savingFollowupPace = ref(false);
watch(
  settings,
  s => {
    const p = s?.followup_pace;
    if (p && p.per_window != null)
      followupPace.value = {
        per_window: Number(p.per_window),
        minutes: Number(p.minutes),
      };
  },
  { immediate: true }
);
const saveFollowupPace = async (perWindow, minutes) => {
  // mesma faixa do backend: 0–100 mensagens, 1–120 minutos; campo vazio ou
  // lixo mantém o valor atual (não vira "sem limite" sem querer)
  const toInt = (v, fallback) => {
    const x = Number(v);
    return v === '' || !Number.isFinite(x) ? fallback : Math.round(x);
  };
  const n = Math.min(
    Math.max(toInt(perWindow, followupPace.value.per_window), 0),
    100
  );
  const m = Math.min(
    Math.max(toInt(minutes, followupPace.value.minutes), 1),
    120
  );
  followupPace.value = { per_window: n, minutes: m };
  savingFollowupPace.value = true;
  try {
    await CrmAPI.updateFollowupPace({ per_window: n, minutes: m });
    useAlert(
      n === 0
        ? 'Ritmo de envio sem limite'
        : `Até ${n} mensagens a cada ${m} min por número`
    );
  } catch {
    useAlert('Erro ao salvar o ritmo de envio');
  } finally {
    savingFollowupPace.value = false;
  }
};

// 🏷️ ETIQUETAS QUE ENCERRAM O FOLLOW-UP (rodada 158) — contato ou conversa
// com uma delas não recebe cutucada de robô nenhum (além das fixas
// nao_perturbe e perda_*). Caso real 12/09: att_encerrado aplicada por
// automação e a conversa seguia recebendo mensagem.
const followupStopLabels = ref([]);
const savingStopLabels = ref(false);
watch(
  settings,
  s => {
    if (Array.isArray(s?.followup_stop_labels))
      followupStopLabels.value = [...s.followup_stop_labels];
  },
  { immediate: true }
);
const saveStopLabels = async labels => {
  const previous = followupStopLabels.value;
  followupStopLabels.value = labels;
  savingStopLabels.value = true;
  try {
    await CrmAPI.updateFollowupStopLabels(labels);
    useAlert(
      labels.length
        ? `Etiquetas de encerramento: ${labels.join(', ')}`
        : 'Só as etiquetas fixas (nao_perturbe / perda_*) encerram o follow-up'
    );
  } catch {
    followupStopLabels.value = previous;
    useAlert('Erro ao salvar as etiquetas de encerramento');
  } finally {
    savingStopLabels.value = false;
  }
};
const toggleStopLabel = title => {
  const t = String(title || '')
    .trim()
    .toLowerCase();
  if (!t || savingStopLabels.value) return;
  const current = followupStopLabels.value;
  saveStopLabels(
    current.includes(t) ? current.filter(l => l !== t) : [...current, t]
  );
};

// 📅 item 253 (26/09): os LEMBRETES / CONFIRMAÇÃO DE CONSULTA saíram daqui e
// viraram o card "Confirmação de consulta" em Agentes de IA
// (ConfirmationAgentCard.vue). Na aba Robôs fica só o atalho.
const goToConfirmation = () => {
  activeTab.value = 'agentes';
  router.replace({ query: { tab: 'agentes' } });
  expandedAgents.value = { ...expandedAgents.value, confirmacao: true };
  nextTick(() => {
    setTimeout(() => {
      document
        .getElementById('cv-agent-confirmacao')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  });
};

onMounted(async () => {
  loadStudioForms();
  if (!inboxes.value.length) store.dispatch('inboxes/get');
  if (!accountLabels.value.length) store.dispatch('labels/get');
  if (!teamAgents.value.length) store.dispatch('agents/get');
  loadBots();
  loadReguas(); // aparecem no painel panorâmico (Modo Programação)
  loadNativeRules(); // aba "Regras da caixa de entrada"
  await loadAgents();
  aiConfigured.value = !!settings.value?.ai?.configured;
  loadColumnAutomations();
});

// não deixa repoll nenhum rodando depois de sair da tela
// (colheita + auditor + criativo)
onUnmounted(() => {
  stopHarvestPolling();
  stopAuditorPolling();
  stopCreativePolling();
});
</script>

<template>
  <div
    ref="pageRoot"
    class="cv-page bg-n-surface-1 flex flex-col h-full w-full overflow-y-auto"
    :style="cvVars"
  >
    <!-- Cabeçalho (rodada 189): banner de vidro do kit + abas em segmento,
         mesma linguagem de Relatórios/Central; cada aba mantém o ícone -->
    <div class="px-4 sm:px-6 pt-4 sm:pt-6">
      <div class="max-w-5xl mx-auto w-full">
        <CevicoHero
          :pal="pal"
          title="Automações"
          subtitle="Tudo que trabalha sozinho no sistema: réguas, robôs, agentes de IA, automações de coluna e tratamento de dados."
          icon="i-lucide-workflow"
        >
          <template #chips>
            <CevicoViewOnlyPill
              v-if="!canEdit(AUTOMATION_TAB_NODES[activeTab])"
            />
          </template>
        </CevicoHero>
      </div>
      <!-- Abas (atendente concedido só vê as abas da área dele) -->
      <div class="max-w-5xl mx-auto w-full">
        <div class="cv-seg cv-seg-sm !flex flex-wrap">
          <button
            v-if="visibleTabs.includes('robos')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'robos' }"
            @click="activeTab = 'robos'"
          >
            <span class="i-lucide-bot text-xs" />Robôs de follow-up
          </button>
          <button
            v-if="visibleTabs.includes('regras')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'regras' }"
            @click="activeTab = 'regras'"
          >
            <span class="i-lucide-repeat text-xs" />Regras da caixa de entrada
          </button>
          <button
            v-if="visibleTabs.includes('agentes')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'agentes' }"
            @click="activeTab = 'agentes'"
          >
            <span class="i-lucide-sparkles text-xs" />Agentes de IA
          </button>
          <button
            v-if="visibleTabs.includes('painel_ia')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'painel_ia' }"
            @click="activeTab = 'painel_ia'"
          >
            <span class="i-lucide-activity text-xs" />Painel dos agentes
          </button>
          <button
            v-if="visibleTabs.includes('fluxos')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'fluxos' }"
            @click="activeTab = 'fluxos'"
          >
            <span class="i-lucide-git-branch text-xs" />Fluxos
          </button>
          <button
            v-if="visibleTabs.includes('programacao')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'programacao' }"
            @click="activeTab = 'programacao'"
          >
            <span class="i-lucide-zap text-xs" />Modo Programação
          </button>
          <button
            v-if="visibleTabs.includes('resultados')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'resultados' }"
            @click="activeTab = 'resultados'"
          >
            <span class="i-lucide-bar-chart-3 text-xs" />Resultados
          </button>
          <button
            v-if="visibleTabs.includes('tratamento')"
            class="cv-seg-item"
            :class="{ 'cv-seg-on': activeTab === 'tratamento' }"
            @click="activeTab = 'tratamento'"
          >
            <span class="i-lucide-database text-xs" />Tratamento de dados
          </button>
        </div>
      </div>
    </div>

    <div class="px-4 sm:px-6 py-5 sm:py-6">
      <div class="max-w-5xl mx-auto w-full">
        <!-- ══ ROBÔS ══ -->
        <div v-if="activeTab === 'robos'" class="max-w-3xl">
          <div class="flex items-center justify-between mb-4">
            <p class="text-sm text-n-slate-11">
              Cutucadas simples para reabrir a conversa quando o paciente some.
              Ex.: 3h "oi, pode falar?", 10h "[nome], no seu tempo ok?". Para
              sozinho se o paciente responder.
            </p>
            <button
              v-if="canEditRobos"
              class="text-sm px-3 py-2 rounded-lg bg-n-brand text-white hover:bg-n-brand/90 flex items-center gap-1.5 flex-shrink-0"
              @click="openCreateBot"
            >
              <span class="i-lucide-plus text-sm" />
              Novo robô
            </button>
          </div>

          <!-- 🕐 Horário de envio (item 147): janela em que as cutucadas podem
             sair — o admin estende até 24h pra não empilhar pro dia seguinte -->
          <div
            v-if="canEditRobos"
            class="mb-4 p-4 bg-n-solid-2 border border-n-weak rounded-xl"
          >
            <div class="flex items-center gap-2 flex-wrap">
              <span class="i-lucide-clock text-sm" style="color: #0f5fa6" />
              <p class="text-sm font-semibold text-n-slate-12">
                Horário de envio das cutucadas
              </p>
              <span class="text-xs font-medium text-n-brand">{{
                followupHoursLabel
              }}</span>
              <Spinner
                v-if="savingFollowupHours"
                :size="14"
                class="text-n-brand"
              />
            </div>
            <div class="mt-2.5 flex items-center gap-2 flex-wrap">
              <span class="text-xs text-n-slate-11">das</span>
              <select
                :value="followupHours.start"
                class="text-sm border border-n-weak rounded-lg px-2 py-1 bg-n-solid-1"
                style="width: 86px; margin-bottom: 0"
                @change="
                  saveFollowupHours(
                    Number($event.target.value),
                    followupHours.end
                  )
                "
              >
                <option v-for="h in 24" :key="`fh-s-${h}`" :value="h - 1">
                  {{ String(h - 1).padStart(2, '0') }}h
                </option>
              </select>
              <span class="text-xs text-n-slate-11">às</span>
              <select
                :value="followupHours.end"
                class="text-sm border border-n-weak rounded-lg px-2 py-1 bg-n-solid-1"
                style="width: 86px; margin-bottom: 0"
                @change="
                  saveFollowupHours(
                    followupHours.start,
                    Number($event.target.value)
                  )
                "
              >
                <option v-for="h in 24" :key="`fh-e-${h}`" :value="h">
                  {{ h === 24 ? '24h' : `${String(h).padStart(2, '0')}h` }}
                </option>
              </select>
              <button
                class="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                :class="
                  followupHours.start === 8 && followupHours.end === 20
                    ? 'border-n-brand text-n-brand font-semibold'
                    : 'border-n-weak text-n-slate-10 hover:text-n-brand'
                "
                @click="saveFollowupHours(8, 20)"
              >
                Padrão 08h–20h
              </button>
              <button
                class="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                :class="
                  followupHours.start === 0 && followupHours.end === 24
                    ? 'border-n-brand text-n-brand font-semibold'
                    : 'border-n-weak text-n-slate-10 hover:text-n-brand'
                "
                @click="saveFollowupHours(0, 24)"
              >
                24 horas
              </button>
            </div>
            <p class="mt-2 text-[11px] text-n-slate-10 leading-relaxed">
              Vale para todos os robôs. Cutucada que vence fora do horário
              espera ele reabrir (não é descartada) e, ao reabrir, as etapas
              <b>não saem empilhadas</b>: entre uma e a seguinte vale a
              diferença de tempo da cadência, contada da cutucada anterior. 💡
              Texto simples só chega dentro da janela de 24h do WhatsApp — fora
              dela a etapa de texto é pulada (fica no registro) e só mensagem
              modelo entrega.
            </p>

            <!-- 🚦 Ritmo de envio (item 345): até N cutucadas a cada M min
               por número — evita a rajada das 08h que parece spam -->
            <div class="mt-3 pt-3 border-t border-n-weak">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="i-lucide-gauge text-sm" style="color: #0f5fa6" />
                <p class="text-sm font-semibold text-n-slate-12">
                  Ritmo de envio
                </p>
                <span class="text-xs font-medium text-n-brand">{{
                  followupPace.per_window === 0
                    ? 'sem limite'
                    : `até ${followupPace.per_window} a cada ${followupPace.minutes} min`
                }}</span>
                <Spinner
                  v-if="savingFollowupPace"
                  :size="14"
                  class="text-n-brand"
                />
              </div>
              <div class="mt-2.5 flex items-center gap-2 flex-wrap">
                <span class="text-xs text-n-slate-11">até</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  :value="followupPace.per_window"
                  class="text-sm border border-n-weak rounded-lg px-2 py-1 bg-n-solid-1"
                  style="width: 70px; margin-bottom: 0"
                  @change="
                    saveFollowupPace($event.target.value, followupPace.minutes)
                  "
                />
                <span class="text-xs text-n-slate-11">mensagens a cada</span>
                <input
                  type="number"
                  min="1"
                  max="120"
                  :value="followupPace.minutes"
                  class="text-sm border border-n-weak rounded-lg px-2 py-1 bg-n-solid-1"
                  style="width: 70px; margin-bottom: 0"
                  @change="
                    saveFollowupPace(
                      followupPace.per_window,
                      $event.target.value
                    )
                  "
                />
                <span class="text-xs text-n-slate-11">
                  minutos por número (evita parecer spam)
                </span>
                <button
                  class="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                  :class="
                    followupPace.per_window === 5 && followupPace.minutes === 5
                      ? 'border-n-brand text-n-brand font-semibold'
                      : 'border-n-weak text-n-slate-10 hover:text-n-brand'
                  "
                  @click="saveFollowupPace(5, 5)"
                >
                  Padrão 5 a cada 5 min
                </button>
              </div>
              <p class="mt-2 text-[11px] text-n-slate-10 leading-relaxed">
                Vale para todos os robôs e para a reativação de quem adiou,
                somados por número de WhatsApp. Quem passa do limite
                <b>não perde a cutucada</b>: espera a vez e sai nas próximas
                rodadas. A fila começa por quem está com a janela de 24h do
                WhatsApp mais perto de fechar. Use 0 para não limitar.
              </p>
            </div>

            <!-- 🏷️ Etiquetas que encerram o follow-up (rodada 158) -->
            <div class="mt-3 pt-3 border-t border-n-weak">
              <div class="flex items-center gap-2 flex-wrap">
                <span
                  class="i-lucide-octagon-x text-sm"
                  style="color: #b91c1c"
                />
                <p class="text-sm font-semibold text-n-slate-12">
                  Etiquetas que encerram o follow-up
                </p>
                <Spinner
                  v-if="savingStopLabels"
                  :size="14"
                  class="text-n-brand"
                />
              </div>
              <p class="mt-1 text-[11px] text-n-slate-10 leading-relaxed">
                Contato ou conversa com uma destas etiquetas não recebe cutucada
                de <b>nenhum</b> robô — ex.: <code>att_encerrado</code>. Fixas
                (sempre valem): <code>nao_perturbe</code> e
                <code>perda_*</code>. Clique numa etiqueta para ligar/desligar.
              </p>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <button
                  v-for="l in accountLabels"
                  :key="`stop-${l.id}`"
                  class="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors"
                  :class="
                    followupStopLabels.includes(l.title)
                      ? 'bg-red-500/15 border-red-500 text-red-600 font-medium'
                      : 'border-n-weak text-n-slate-10 hover:bg-n-alpha-1'
                  "
                  :disabled="savingStopLabels"
                  @click="toggleStopLabel(l.title)"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :style="{ backgroundColor: l.color ?? '#6B7280' }"
                  />
                  {{ l.title }}
                </button>
                <span
                  v-if="!accountLabels.length"
                  class="text-[11px] text-n-slate-9"
                  >Nenhuma etiqueta cadastrada na conta ainda.</span
                >
              </div>
            </div>
          </div>

          <!-- 🧠 13/10: follow-up inteligente (leitura da conversa, lead frio,
               fim de semana, reativação de quem adiou) + resultado por coluna -->
          <FollowupSmartCard :can-edit="canEditRobos" />

          <!-- 📅 item 253: lembretes e confirmação de consulta agora ficam em
               Agentes de IA → "Confirmação de consulta"; aqui só o atalho -->
          <div
            v-if="canEditRobos"
            class="mb-4 p-4 bg-n-solid-2 border border-n-weak rounded-xl flex items-center gap-3 flex-wrap"
          >
            <span
              class="i-lucide-calendar-check text-base"
              style="color: #b45309"
            />
            <div class="flex-1 min-w-[220px]">
              <p class="text-sm font-semibold text-n-slate-12">
                Confirmação e lembretes de consulta
              </p>
              <p class="text-[11px] text-n-slate-10">
                Mudaram para <b>Agentes de IA → Confirmação de consulta</b>:
                quantos lembretes quiser (2 dias antes, véspera, no dia…), cada
                um com modelo por unidade e modo sombra.
              </p>
            </div>
            <button class="cv-btn cv-btn-sm" @click="goToConfirmation">
              <span class="i-lucide-arrow-right text-xs" /> Abrir
            </button>
          </div>

          <div v-if="loadingBots" class="flex justify-center py-10">
            <Spinner :size="28" class="text-n-brand" />
          </div>
          <div
            v-else-if="!bots.length"
            class="text-center py-12 text-n-slate-10"
          >
            <span class="i-lucide-bot text-4xl mb-2 block mx-auto" />
            <p class="text-sm">Nenhum robô criado ainda.</p>
          </div>
          <div v-else class="space-y-3">
            <div
              v-for="bot in bots"
              :key="bot.id"
              class="p-4 bg-n-solid-2 border border-n-weak rounded-xl"
            >
              <div class="flex items-center gap-2 mb-2">
                <span
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :class="bot.active ? 'bg-green-500' : 'bg-n-slate-9'"
                />
                <p
                  class="text-sm font-semibold text-n-slate-12 flex-1 truncate"
                >
                  {{ bot.name }}
                </p>
                <span class="text-xs text-n-slate-10">{{
                  (bot.inbox_names || []).length
                    ? bot.inbox_names.join(', ')
                    : bot.inbox_name || '🔁 Caixa automática'
                }}</span>
                <button
                  v-if="canEditRobos"
                  class="text-xs px-2 py-1 rounded-lg border border-n-weak ml-1"
                  :class="bot.active ? 'text-yellow-600' : 'text-green-600'"
                  @click="toggleBot(bot)"
                >
                  {{ bot.active ? 'Pausar' : 'Ativar' }}
                </button>
                <button
                  v-if="canEditRobos"
                  class="text-n-slate-9 hover:text-n-brand i-lucide-pencil text-sm"
                  @click="openEditBot(bot)"
                />
                <button
                  v-if="canEditRobos && deleteBotConfirmId !== bot.id"
                  class="text-n-slate-9 hover:text-red-500 i-lucide-trash-2 text-sm"
                  @click="deleteBotConfirmId = bot.id"
                />
                <button
                  v-else-if="canEditRobos"
                  class="text-xs text-red-500"
                  @click="deleteBot(bot)"
                >
                  Confirmar
                </button>
              </div>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="(s, i) in bot.steps"
                  :key="i"
                  class="text-[11px] bg-n-alpha-2 text-n-slate-11 rounded-full px-2 py-0.5"
                >
                  {{ delayLabel(s) }}:
                  {{
                    s.kind === 'ai'
                      ? '✨ IA escreve pela conversa'
                      : s.template_params
                        ? `📋 ${s.template_params.name}`
                        : `"${s.message}"`
                  }}
                </span>
              </div>

              <!-- aviso quando a janela parou o robô (causa silenciosa nº 1) -->
              <div
                v-if="bot.active && botWarning(bot)"
                class="mt-2 text-xs rounded-lg px-3 py-2 font-medium"
                style="
                  background: rgba(217, 119, 6, 0.1);
                  color: #b45309;
                  border: 1px solid rgba(217, 119, 6, 0.35);
                "
              >
                {{ botWarning(bot) }}
              </div>

              <!-- última rodada + registro de atividade -->
              <div
                class="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-n-slate-10"
              >
                <template v-if="bot.activity?.last_run">
                  <span>
                    Última rodada {{ fmtLogDate(bot.activity.last_run.at) }} ·
                    <template
                      v-if="bot.activity.last_run.status === 'fora_da_janela'"
                      >fora da janela (não enviou)</template
                    >
                    <template
                      v-else-if="
                        bot.activity.last_run.status === 'fora_do_expediente'
                      "
                      >fora do horário de envio ({{
                        followupHoursLabel
                      }})</template
                    >
                    <template v-else>
                      {{ bot.activity.last_run.candidates }} conversa(s) na mira
                      ·
                      <b
                        :class="
                          bot.activity.last_run.sent ? 'text-green-600' : ''
                        "
                        >{{ bot.activity.last_run.sent }} enviada(s)</b
                      >
                    </template>
                  </span>
                  <span
                    v-if="reasonLine(bot.activity.last_run)"
                    class="text-n-slate-9"
                    >{{ reasonLine(bot.activity.last_run) }}</span
                  >
                </template>
                <span v-else
                  >Sem rodadas registradas ainda (o registro começa após esta
                  atualização).</span
                >
                <button
                  v-if="bot.activity?.events?.length"
                  class="text-n-brand font-medium hover:underline"
                  @click="
                    expandedBotLog = expandedBotLog === bot.id ? null : bot.id
                  "
                >
                  📒 Registro de atividade ({{ bot.activity.events.length }})
                </button>
              </div>
              <div
                v-if="expandedBotLog === bot.id && bot.activity?.events?.length"
                class="mt-2 rounded-lg bg-n-alpha-1 divide-y divide-n-weak"
              >
                <div
                  v-for="(ev, i) in bot.activity.events"
                  :key="i"
                  class="px-3 py-1.5 text-[11px] flex items-center gap-2"
                >
                  <span>{{
                    ev.type === 'sent'
                      ? '✉️'
                      : ev.type === 'skipped'
                        ? '⏭️'
                        : '⚠️'
                  }}</span>
                  <span class="text-n-slate-9 flex-shrink-0">{{
                    fmtLogDate(ev.at)
                  }}</span>
                  <span class="text-n-slate-11 truncate"
                    >{{ ev.contact || 'Contato' }} · conversa #{{
                      ev.conversation_id
                    }}</span
                  >
                  <span class="text-n-slate-10 truncate">{{
                    ev.type === 'error' ? `erro: ${ev.note}` : ev.note
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- ══ AGENTES DE IA ══ -->
        <!-- ══ REGRAS DA CAIXA DE ENTRADA (Automation nativa) ══ -->
        <div v-else-if="activeTab === 'regras'" class="max-w-3xl">
          <div class="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <p class="text-sm text-n-slate-11 flex-1 min-w-[240px]">
              Regras que rodam nas conversas da caixa de entrada: quando uma
              conversa é criada ou uma mensagem chega, aplicam etiqueta,
              atribuem time/atendente, mudam prioridade... Ligue e desligue por
              aqui; criar e editar é no editor completo.
            </p>
            <button
              class="px-3 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:opacity-90 transition-opacity flex items-center gap-1.5"
              @click="openNativeEditor"
            >
              <span class="i-lucide-external-link text-xs" />
              Abrir editor completo
            </button>
          </div>

          <div v-if="loadingNativeRules" class="flex justify-center py-10">
            <Spinner />
          </div>
          <p
            v-else-if="!nativeRules.length"
            class="text-sm text-n-slate-10 border border-dashed border-n-weak rounded-xl p-6 text-center"
          >
            Nenhuma regra criada ainda — use o editor completo para criar a
            primeira.
          </p>
          <div v-else class="space-y-2">
            <div
              v-for="rule in nativeRules"
              :key="rule.id"
              class="border border-n-weak rounded-xl p-3 flex items-center gap-3 bg-n-solid-2"
            >
              <span
                class="w-2 h-2 rounded-full flex-shrink-0"
                :class="rule.active ? 'bg-green-500' : 'bg-n-slate-7'"
              />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  {{ rule.name }}
                </p>
                <p class="text-[11px] text-n-slate-10 truncate">
                  {{ RULE_EVENT_LABELS[rule.event_name] || rule.event_name }}
                  <template v-if="rule.description">
                    · {{ rule.description }}
                  </template>
                </p>
              </div>
              <span
                class="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                :class="
                  rule.active
                    ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                    : 'bg-n-alpha-1 text-n-slate-10'
                "
              >
                {{ rule.active ? 'Ativa' : 'Desligada' }}
              </span>
              <button
                class="text-xs font-medium px-2.5 py-1 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors flex-shrink-0"
                @click="toggleNativeRule(rule)"
              >
                {{ rule.active ? 'Desligar' : 'Ligar' }}
              </button>
            </div>
          </div>
        </div>

        <div v-else-if="activeTab === 'agentes'" class="max-w-4xl space-y-5">
          <div
            v-if="!aiConfigured"
            class="cv-block cv-strip cv-amber px-4 py-3.5 flex items-center gap-3 flex-wrap"
          >
            <span class="cv-icon cv-icon-sm"
              ><span class="i-lucide-key-round text-xs"
            /></span>
            <p class="text-xs text-n-slate-11 flex-1 min-w-0">
              A Claude ainda não está conectada. Os agentes ficam prontos, mas
              só funcionam com a chave da API.
            </p>
            <button
              class="cv-btn cv-btn-sm"
              @click="
                router.push({ name: 'crm_integrations', params: { accountId } })
              "
            >
              Integrações → Claude
            </button>
          </div>

          <!-- 💰 Gasto com os agentes (rodada 189: vidro cristalino do kit, número grande) -->
          <div
            v-if="aiUsage"
            class="cv-block p-5 sm:p-6"
            :style="blockVars('gasto')"
          >
            <div class="flex items-center gap-3 mb-4">
              <span class="cv-icon cv-icon-lg"
                ><span class="i-lucide-wallet text-base"
              /></span>
              <div class="min-w-0">
                <p class="text-base font-bold text-n-slate-12 leading-tight">
                  Gasto com os agentes de IA
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  custo estimado pelas tabelas da Anthropic (US$)
                </p>
              </div>
            </div>

            <!-- 📅 item 321: a régua padrão do sistema + setas para analisar um dia -->
            <div class="flex items-center gap-2 flex-wrap mb-4">
              <PeriodRuler v-model="usagePeriod" glass class="min-w-0" />
              <div class="flex items-center gap-1 flex-shrink-0">
                <button
                  class="cv-btn cv-btn-ghost cv-iconbtn"
                  title="Analisar o dia anterior"
                  @click="shiftUsageDay(-1)"
                >
                  <span class="i-lucide-chevron-left text-sm" />
                </button>
                <span class="text-[11px] text-n-slate-10">dia a dia</span>
                <button
                  class="cv-btn cv-btn-ghost cv-iconbtn"
                  title="Analisar o dia seguinte"
                  :disabled="usageAtToday"
                  @click="shiftUsageDay(1)"
                >
                  <span class="i-lucide-chevron-right text-sm" />
                </button>
              </div>
              <span
                v-if="isLoadingUsageDay"
                class="i-lucide-loader-2 animate-spin text-xs text-n-slate-9"
              />
            </div>

            <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <div class="cv-stat px-4 py-3">
                <p class="cv-label">Gasto · {{ usagePeriodLabel }}</p>
                <p
                  class="text-2xl font-bold text-n-slate-12 leading-tight mt-1 tabular-nums"
                >
                  {{ fmtUsd(usageTotals?.cost_usd) }}
                </p>
                <p class="text-[11px] text-n-slate-10 mt-0.5">
                  {{ brDay(aiUsage.period?.from) }} a
                  {{ brDay(aiUsage.period?.to) }}
                </p>
              </div>
              <div class="cv-stat px-4 py-3">
                <p class="cv-label">Chamadas à IA</p>
                <p
                  class="text-2xl font-bold text-n-slate-12 leading-tight mt-1 tabular-nums"
                >
                  {{ usageTotals?.calls || 0 }}
                </p>
                <p class="text-[11px] text-n-slate-10 mt-0.5">no período</p>
              </div>
              <div class="cv-stat px-4 py-3">
                <p class="cv-label">Tokens</p>
                <p
                  class="text-2xl font-bold text-n-slate-12 leading-tight mt-1 tabular-nums"
                >
                  {{ fmtTokens(usageTotals?.input_tokens) }}
                </p>
                <p
                  class="text-[11px] text-n-slate-10 mt-0.5"
                  title="Fatia da entrada que veio do cache (custa 10%). Baixo = o roteiro está sendo pago cheio."
                >
                  entrada · {{ fmtTokens(usageTotals?.output_tokens) }} saída ·
                  {{ usageTotals?.cache_pct ?? 0 }}% do cache
                </p>
              </div>
              <div class="cv-stat px-4 py-3">
                <p class="cv-label">Desde o início</p>
                <p
                  class="text-2xl font-bold text-n-slate-12 leading-tight mt-1 tabular-nums"
                >
                  {{ fmtUsd(aiUsage.periods?.all?.cost_usd) }}
                </p>
                <p class="text-[11px] text-n-slate-10 mt-0.5">
                  {{ aiUsage.periods?.all?.calls || 0 }} chamada(s) à IA
                </p>
              </div>
            </div>

            <div v-if="usageRows.length" class="space-y-1.5">
              <p class="cv-label mb-1">
                Por agente ({{ usagePeriodLabel }})
              </p>
              <div
                v-for="row in usageRows"
                :key="row.key"
                class="cv-row px-3 py-2 flex items-center gap-2 text-xs text-n-slate-11 flex-wrap"
              >
                <span
                  class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  :style="{
                    background: AGENT_META[row.key]?.gradient || '#94A3B8',
                  }"
                />
                <span class="font-semibold text-n-slate-12 w-48 truncate">{{
                  AGENT_META[row.key]?.title ||
                  EXTRA_USAGE_TITLES[row.key] ||
                  row.key
                }}</span>
                <span>{{ row.calls }} {{ usageNoun(row.key) }}</span>
                <span class="text-n-slate-9"
                  >· {{ fmtTokens(row.input_tokens) }} tokens entrada ·
                  {{ fmtTokens(row.output_tokens) }} saída</span
                >
                <span
                  v-if="row.cache_pct !== undefined"
                  title="Fatia da entrada lida do cache (custa 10%)"
                  class="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                  :class="
                    row.cache_pct >= 50
                      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                  "
                  >{{ row.cache_pct }}% cache</span
                >
                <span class="ml-auto font-bold text-n-slate-12 tabular-nums">{{
                  fmtUsd(row.cost_usd)
                }}</span>
              </div>
            </div>
            <p v-else class="text-xs text-n-slate-10">
              Nenhuma chamada à IA neste período ({{ usagePeriodLabel }}).
            </p>
          </div>

          <!-- 📜 ROTEIRO CEVICO (rodada 188): fonte única dos atendentes que falam com paciente -->
          <div id="cv-roteiro" class="cv-block cv-gold p-5 sm:p-6">
            <div
              class="flex items-start gap-3 cursor-pointer select-none"
              :class="scriptExpanded ? 'mb-4' : ''"
              @click="scriptExpanded = !scriptExpanded"
            >
              <span class="cv-icon cv-icon-xl"
                ><span class="i-lucide-scroll-text text-lg"
              /></span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <p class="text-base font-bold text-n-slate-12 leading-tight">
                    Roteiro CEVICO
                  </p>
                  <span class="cv-chip" :class="scriptIsV2 ? 'cv-amber' : ''">{{
                    scriptIsV2
                      ? '🧪 Roteiro 2 · só no teste'
                      : 'Roteiro 1 · fonte única dos atendentes'
                  }}</span>
                  <span class="cv-chip cv-slate">
                    {{ scriptSections.filter(sec => sec.custom).length }} de
                    {{ scriptSections.length }} seções personalizadas
                  </span>
                  <span
                    v-if="scriptUpdatedAt"
                    class="text-[11px] text-n-slate-9"
                  >
                    editado
                    {{
                      new Date(scriptUpdatedAt).toLocaleString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    }}
                  </span>
                </div>
                <p
                  class="text-xs text-n-slate-10 mt-1 leading-relaxed"
                  :class="scriptExpanded ? '' : 'line-clamp-2'"
                >
                  Quem ele é, como fala, dados oficiais, objeções e quando
                  chamar humano — o mesmo texto para TODOS os atendentes que
                  falam com o paciente (Atendente de Agendamento e
                  Pós-agendamento hoje; os próximos amanhã). Os PASSOS de cada
                  etapa ficam no card de cada agente. Mudou aqui, mudou em
                  todos.
                </p>
              </div>
              <span
                class="i-lucide-chevron-down text-n-slate-9 text-lg mt-2 flex-shrink-0 transition-transform duration-200"
                :class="scriptExpanded ? 'rotate-180' : ''"
              />
            </div>

            <div v-if="scriptExpanded" class="cevico-agent-body">
              <!-- 🧪 22/09: Atual × v2 paralelo -->
              <div class="flex items-center gap-2 flex-wrap mb-3">
                <div class="cv-seg cv-seg-sm">
                  <button
                    v-for="v in SCRIPT_VERSION_TABS"
                    :key="v.key"
                    class="cv-seg-item"
                    :class="{ 'cv-seg-on': scriptVersion === v.key }"
                    :title="v.hint"
                    @click="pickScriptVersion(v.key)"
                  >
                    {{ v.label }}
                  </button>
                </div>
                <button
                  class="cv-btn cv-btn-sm cv-btn-ghost"
                  title="Abre o Testar agente já com esta versão do Roteiro"
                  @click="openTestWithVersion('atendente_agendamento')"
                >
                  <span class="i-lucide-flask-conical text-xs" /> Testar com
                  {{ scriptIsV2 ? 'o v2' : 'o atual' }}
                </button>
              </div>
              <div
                v-if="scriptIsV2"
                class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed mb-3"
              >
                🧪 <b>Roteiro 2 · Otimizado pela análise</b> (22/09): mesmo
                processo de vendas, com flexibilidade e o conhecimento da
                equipe. Só o <b>Testar agente</b> lê esta versão (escolha
                "Roteiro 2" lá). Nenhum atendente em sombra ou ao vivo usa o v2.
                Aqui também ficam os <b>Passos</b> dos dois atendentes do v2.
                Seção em branco volta ao padrão do v2.
              </div>
              <div
                v-else
                class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed mb-3"
              >
                📌 Cada atendente lê: <b>Roteiro</b> (este card, igual para
                todos) + <b>Passos desta etapa</b> (no card do agente: o passo a
                passo da recepção ao agendamento) + trava fixa do sistema (nunca
                inventar valor, horário ou diagnóstico). Seção em branco volta
                ao padrão. Salvar aqui vale <b>na hora</b>.
              </div>

              <!-- abas das seções -->
              <div class="cv-seg cv-seg-sm !flex flex-wrap mb-3">
                <button
                  v-for="sec in scriptSections"
                  :key="sec.key"
                  class="cv-seg-item"
                  :class="{
                    'cv-seg-on':
                      (scriptOpenSection || scriptSections[0]?.key) === sec.key,
                  }"
                  @click="scriptOpenSection = sec.key"
                >
                  <span :class="sec.icon" class="text-xs" />
                  {{ sec.title }}
                  <span
                    v-if="sec.custom"
                    class="i-lucide-pencil text-[10px]"
                    title="personalizado"
                  />
                </button>
              </div>

              <template v-for="sec in scriptSections" :key="`body-${sec.key}`">
                <div
                  v-if="
                    scriptOpenSection === sec.key ||
                    (!scriptOpenSection && sec === scriptSections[0])
                  "
                  class="cv-sub p-4"
                >
                  <div class="flex items-center gap-2 flex-wrap mb-2">
                    <p class="text-sm font-bold text-n-slate-12">
                      {{ sec.title }}
                    </p>
                    <span class="text-[11px] text-n-slate-9">{{
                      sec.hint
                    }}</span>
                    <span
                      class="ml-auto cv-chip"
                      :class="sec.custom ? 'cv-amber' : 'cv-slate'"
                    >
                      {{ sec.custom ? 'personalizado' : 'padrão do sistema' }}
                    </span>
                  </div>
                  <textarea
                    v-if="scriptEditing"
                    v-model="scriptDraft[sec.key]"
                    rows="14"
                    class="cv-input w-full text-xs font-mono leading-relaxed"
                  />
                  <pre
                    v-else
                    class="text-xs text-n-slate-11 whitespace-pre-wrap font-sans leading-relaxed max-h-72 overflow-y-auto"
                    >{{ sec.text }}</pre
                  >
                  <div
                    v-if="scriptEditing"
                    class="flex items-center gap-2 mt-2 flex-wrap"
                  >
                    <button
                      class="cv-btn cv-btn-sm cv-btn-ghost"
                      :disabled="scriptSectionIsDefault(sec.key)"
                      @click="restoreScriptSection(sec.key)"
                    >
                      ↺ Restaurar padrão desta seção
                    </button>
                    <span class="text-[11px] text-n-slate-9"
                      >{{
                        (scriptDraft[sec.key] || '').length
                      }}
                      caracteres</span
                    >
                  </div>
                </div>
              </template>

              <div class="flex items-center gap-2 mt-3 flex-wrap">
                <template v-if="!scriptEditing">
                  <button class="cv-btn cv-btn-sm" @click="startScriptEdit">
                    <span class="i-lucide-pencil text-xs" /> Editar Roteiro
                  </button>
                  <button
                    class="cv-btn cv-btn-sm cv-btn-ghost"
                    :class="scriptHistoryOpen ? 'cv-chip-on' : ''"
                    title="Versões anteriores do Roteiro e dos Passos — dá para voltar"
                    @click="toggleScriptHistory"
                  >
                    <span class="i-lucide-history text-xs" /> 🕘 Histórico
                  </button>
                  <span class="text-[11px] text-n-slate-9">{{
                    scriptIsV2
                      ? 'o texto acima só entra no Testar agente (Roteiro 2)'
                      : 'o texto acima é o que os atendentes leem hoje'
                  }}</span>
                </template>
                <template v-else>
                  <button
                    class="cv-btn cv-btn-sm cv-green"
                    :disabled="savingScript"
                    @click="saveScript"
                  >
                    <span
                      :class="
                        savingScript
                          ? 'i-lucide-loader-circle animate-spin'
                          : 'i-lucide-save'
                      "
                      class="text-xs"
                    />
                    {{
                      savingScript
                        ? 'Salvando…'
                        : scriptIsV2
                          ? 'Salvar Roteiro 2 (só no teste)'
                          : 'Salvar Roteiro (vale na hora)'
                    }}
                  </button>
                  <button
                    class="cv-btn cv-btn-sm cv-btn-ghost"
                    :disabled="savingScript"
                    @click="cancelScriptEdit"
                  >
                    Descartar
                  </button>
                </template>
              </div>

              <!-- 🕘 rodada 191: histórico de versões (Roteiro + Passos) -->
              <div v-if="scriptHistoryOpen" class="cv-sub p-4 mt-3">
                <div class="flex items-center gap-2 flex-wrap mb-2">
                  <p class="text-sm font-bold text-n-slate-12">
                    🕘 Histórico de versões
                  </p>
                  <span class="text-[11px] text-n-slate-9"
                    >uma versão é guardada antes de cada edição e de cada
                    orientação aplicada · últimas 30</span
                  >
                  <button
                    class="cv-btn cv-btn-ghost cv-btn-sm ml-auto"
                    :disabled="loadingVersions"
                    @click="loadScriptVersions"
                  >
                    <span
                      class="i-lucide-refresh-cw text-xs"
                      :class="loadingVersions ? 'animate-spin' : ''"
                    />
                    Atualizar
                  </button>
                </div>
                <p
                  v-if="versionsError"
                  class="cv-sub cv-amber px-3 py-2 text-xs text-n-slate-12 mb-2"
                >
                  ⚠️ {{ versionsError }}
                </p>
                <p
                  v-else-if="!loadingVersions && !scriptVersions.length"
                  class="text-xs text-n-slate-9"
                >
                  Nenhuma versão guardada ainda — a primeira aparece quando você
                  salvar o Roteiro ou aplicar uma orientação.
                </p>
                <div v-else class="space-y-1.5">
                  <div
                    v-for="v in scriptVersions"
                    :key="v.id"
                    class="cv-row px-3 py-2 flex items-center gap-x-2 gap-y-1 flex-wrap"
                  >
                    <span
                      class="cv-chip"
                      :class="v.kind === 'stage' ? 'cv-slate' : 'cv-gold'"
                    >
                      {{ versionLabel(v) }}
                    </span>
                    <span class="text-xs text-n-slate-12 font-medium">{{
                      fmtVersionDate(v.created_at)
                    }}</span>
                    <!-- no celular a nota ocupa a linha inteira (senão vira uma coluna de 1 palavra) -->
                    <span
                      class="text-[11px] text-n-slate-10 min-w-0 basis-full sm:basis-auto sm:flex-1 leading-snug"
                    >
                      {{ v.note || 'sem nota' }}
                      <template v-if="v.author?.name">
                        · {{ v.author.name }}</template
                      >
                      <template v-if="v.size"
                        >· {{ v.size }} caracteres</template
                      >
                    </span>
                    <button
                      class="cv-btn cv-btn-sm cv-btn-ghost"
                      :disabled="restoringVersion === v.id"
                      title="Volta o texto para esta versão (a de agora fica guardada)"
                      @click="restoreScriptVersion(v)"
                    >
                      <span
                        :class="
                          restoringVersion === v.id
                            ? 'i-lucide-loader-circle animate-spin'
                            : 'i-lucide-undo-2'
                        "
                        class="text-xs"
                      />
                      Voltar para esta
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ✍️ ORIENTAÇÕES (rodada 191): fila de "como deveria ter respondido" -->
          <GuidancesPanel
            ref="guidancesPanel"
            @test="openTestAgent"
            @applied="onGuidanceApplied"
          />

          <ShadowReviewModal
            v-if="shadowOpen"
            :agent-key="shadowAgentKey"
            :agent-name="AGENT_META[shadowAgentKey]?.title"
            @close="shadowOpen = false"
            @guided="onGuided"
          />
          <AgentTestModal
            v-if="testAgentKey"
            :agent-key="testAgentKey"
            :agent-name="AGENT_META[testAgentKey]?.title || testAgentKey"
            :prefill="testPrefill"
            :script-version="testScriptVersion"
            @close="
              testAgentKey = '';
              testPrefill = '';
              testScriptVersion = 'v1';
            "
            @edit-script="openScriptFromTest"
            @guided="onGuided"
          />

          <!-- 🃏 Cards × Lista -->
          <div class="flex items-center justify-between gap-2 flex-wrap pt-1">
            <p class="text-sm font-bold text-n-slate-12">
              Agentes
              <span class="text-n-slate-9 font-normal text-xs"
                >· clique num card para abrir e configurar</span
              >
            </p>
            <div class="cv-seg cv-seg-sm">
              <button
                class="cv-seg-item"
                :class="{ 'cv-seg-on': agentsView === 'cards' }"
                @click="agentsView = 'cards'"
              >
                <span class="i-lucide-layout-grid text-xs" /> Cards
              </button>
              <button
                class="cv-seg-item"
                :class="{ 'cv-seg-on': agentsView === 'list' }"
                @click="agentsView = 'list'"
              >
                <span class="i-lucide-rows-3 text-xs" /> Lista
              </button>
            </div>
          </div>

          <template v-for="group in agentGroups" :key="group.title">
            <!-- Cabeçalho da seção (rodada 189: rótulo do kit) -->
            <div class="flex items-center gap-2 pt-2">
              <span class="text-base">{{ group.icon }}</span>
              <p class="cv-label !text-[11px]">{{ group.title }}</p>
              <span class="cv-chip cv-slate">{{
                group.keys.length +
                (group.title === 'Atendimento ao paciente' ? 2 : 0)
              }}</span>
            </div>
            <div
              :class="agentsView === 'cards' ? 'cv-agents-grid' : 'space-y-5'"
            >
              <!-- 📅 item 253: Confirmação de consulta (robô de mensagem modelo, sem IA) — mesmo visual dos agentes -->
              <ConfirmationAgentCard
                v-if="group.title === 'Atendimento ao paciente'"
                :view="agentsView"
                :open="!!expandedAgents.confirmacao"
                :opening="openingAgent === 'confirmacao'"
                :is-admin="canEditAgents"
                @toggle="toggleAgentExpand('confirmacao')"
              />
              <!-- 📊 item 256: Pesquisa de satisfação (NPS) pós-cirurgia -->
              <NpsSurveyCard
                v-if="group.title === 'Atendimento ao paciente'"
                :view="agentsView"
                :open="!!expandedAgents.nps_survey"
                :opening="openingAgent === 'nps_survey'"
                :is-admin="canEditAgents"
                @toggle="toggleAgentExpand('nps_survey')"
                @open-agent="focusAgent"
              />
              <div
                v-for="(agent, key) in groupAgents(group)"
                :id="`cv-agent-${key}`"
                :key="key"
                class="cv-block flex flex-col"
                :class="[
                  agentsView === 'cards' && expandedAgents[key]
                    ? 'cv-agent-open'
                    : '',
                  openingAgent === key ? 'cv-agent-opening' : '',
                ]"
                :style="agentVars(key)"
              >
                <!-- Fio superior na cor do agente -->
                <div
                  class="h-1 w-full flex-shrink-0"
                  :style="{ background: AGENT_META[key].gradient }"
                />

                <div
                  :class="
                    agentsView === 'cards' && !expandedAgents[key]
                      ? 'p-4 flex-1 flex flex-col'
                      : 'p-5 sm:p-6'
                  "
                >
                  <!-- 🃏 Card compacto (modo Cards, fechado): clique = abre no lugar -->
                  <div
                    v-if="agentsView === 'cards' && !expandedAgents[key]"
                    class="flex-1 flex flex-col gap-2.5 cursor-pointer select-none"
                    @click="toggleAgentExpand(key)"
                  >
                    <div class="flex items-start justify-between gap-2">
                      <span class="cv-icon cv-icon-xl">
                        <span :class="AGENT_META[key].icon" class="text-lg" />
                      </span>
                      <!-- interruptor em TODOS os cards (rodada 195: o Agente de Ligação também) -->
                      <div class="flex flex-col items-end gap-1" @click.stop>
                        <button
                          class="cv-switch cv-switch-lg"
                          :class="agent.enabled ? 'cv-switch-on' : ''"
                          :title="
                            agent.enabled
                              ? 'Desligar este agente agora'
                              : 'Ligar este agente agora'
                          "
                          :disabled="togglingAgent === key"
                          :aria-pressed="agent.enabled"
                          @click="toggleAgent(key)"
                        />
                        <span class="text-[10px] text-n-slate-9">{{
                          togglingAgent === key
                            ? 'salvando…'
                            : agent.enabled
                              ? 'Ligado'
                              : 'Desligado'
                        }}</span>
                      </div>
                    </div>
                    <div>
                      <p
                        class="text-base font-bold text-n-slate-12 leading-tight"
                      >
                        {{ AGENT_META[key].title }}
                      </p>
                      <div class="flex items-center gap-1.5 flex-wrap mt-1.5">
                        <span class="cv-chip">{{ AGENT_META[key].tag }}</span>
                        <span
                          class="cv-chip"
                          :class="agent.enabled ? 'cv-green' : 'cv-slate'"
                        >
                          ● {{ agent.enabled ? 'Ligado' : 'Desligado' }}
                        </span>
                        <span
                          v-if="agent.has_draft"
                          class="cv-chip cv-amber"
                          title="Rascunho salvo que ainda não vale"
                          >📝 Rascunho</span
                        >
                      </div>
                    </div>
                    <p
                      class="text-xs text-n-slate-10 leading-relaxed line-clamp-3"
                    >
                      {{ AGENT_META[key].description }}
                    </p>
                    <div class="mt-auto flex items-center gap-2 flex-wrap pt-1">
                      <span
                        v-if="usageByAgent(key)"
                        class="cv-chip cv-slate"
                        title="análises e custo nos últimos 30 dias"
                      >
                        30 d: {{ usageByAgent(key).calls }}
                        {{ usageNoun(key) }} ·
                        {{ fmtUsd(usageByAgent(key).cost_usd) }}
                      </span>
                      <button
                        class="cv-btn cv-btn-sm ml-auto"
                        @click.stop="toggleAgentExpand(key)"
                      >
                        <span class="i-lucide-maximize-2 text-xs" /> Abrir
                      </button>
                    </div>
                  </div>

                  <!-- Cabeçalho completo (Lista, ou card aberto): clique = desce/recolhe o agente completo -->
                  <div
                    v-else
                    class="flex items-start gap-3 cursor-pointer select-none"
                    :class="expandedAgents[key] ? 'mb-3' : ''"
                    @click="toggleAgentExpand(key)"
                  >
                    <span class="cv-icon cv-icon-xl">
                      <span :class="AGENT_META[key].icon" class="text-lg" />
                    </span>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <p
                          class="text-base font-bold text-n-slate-12 leading-tight"
                        >
                          {{ AGENT_META[key].title }}
                        </p>
                        <span class="cv-chip">{{ AGENT_META[key].tag }}</span>
                        <span
                          class="cv-chip"
                          :class="agent.enabled ? 'cv-green' : 'cv-slate'"
                        >
                          ● {{ agent.enabled ? 'Ligado' : 'Desligado' }}
                        </span>
                        <span
                          v-if="agent.has_draft"
                          class="cv-chip cv-amber"
                          title="Existe um rascunho salvo que ainda NÃO está valendo — clique em Publicar para aplicar"
                        >
                          📝 Rascunho não publicado
                        </span>
                        <!-- 🗺️ item 170: abre o fluxograma deste agente na aba Fluxos -->
                        <button
                          class="cv-chip cv-slate"
                          title="Ver o caminho que este agente percorre (aba Fluxos)"
                          @click.stop="openFlow(key)"
                        >
                          <span class="i-lucide-git-branch text-[10px]" />
                          Ver fluxo
                        </button>
                      </div>
                      <p
                        class="text-xs text-n-slate-10 mt-1 leading-relaxed"
                        :class="expandedAgents[key] ? '' : 'line-clamp-2'"
                      >
                        {{ AGENT_META[key].description }}
                      </p>
                    </div>
                    <!-- INTERRUPTOR definitivo: grava na hora, sem "Salvar" (rodada 195: vale para o Agente de Ligação também) -->
                    <div
                      class="flex flex-col items-end gap-1 flex-shrink-0"
                      @click.stop
                    >
                      <button
                        class="cv-switch cv-switch-lg"
                        :class="agent.enabled ? 'cv-switch-on' : ''"
                        :title="
                          agent.enabled
                            ? 'Desligar este agente agora (para tudo: botões, automações e cron)'
                            : 'Ligar este agente agora'
                        "
                        :disabled="togglingAgent === key"
                        :aria-pressed="agent.enabled"
                        @click="toggleAgent(key)"
                      />
                      <span class="text-[10px] text-n-slate-9">{{
                        togglingAgent === key
                          ? 'salvando…'
                          : agent.enabled
                            ? 'Ligado'
                            : 'Desligado'
                      }}</span>
                    </div>
                    <span
                      class="i-lucide-chevron-down text-n-slate-9 text-lg mt-2 flex-shrink-0 transition-transform duration-200"
                      :class="expandedAgents[key] ? 'rotate-180' : ''"
                    />
                  </div>

                  <!-- corpo completo do agente: desce com animação leve (rodada 195: o Agente de Ligação usa o mesmo corpo) -->
                  <div v-if="expandedAgents[key]" class="cevico-agent-body">
                    <!-- Onde se aplica -->
                    <p class="cv-label mb-1.5">Onde se aplica</p>
                    <div class="flex flex-wrap gap-1.5 mb-4">
                      <span
                        v-for="(t, i) in AGENT_META[key].triggers"
                        :key="i"
                        class="cv-chip cv-chip-lg cv-chip-wrap"
                      >
                        <span
                          :class="t.icon"
                          class="text-xs"
                          :style="{ color: AGENT_META[key].color }"
                        />
                        {{ t.label }}
                      </span>
                    </div>

                    <!-- Modelo + Esforço (vazio = recomendado pelo sistema) -->
                    <div
                      class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2"
                      :class="!editingAgent[key] ? 'opacity-70' : ''"
                    >
                      <div>
                        <label
                          class="text-xs font-medium text-n-slate-11 block mb-1.5"
                          >Modelo de IA</label
                        >
                        <select
                          v-model="agent.model"
                          :disabled="!editingAgent[key]"
                          class="cv-input w-full text-sm disabled:cursor-not-allowed"
                        >
                          <option value="">
                            ⭐ {{ recommendedModelLabel(key) }}
                          </option>
                          <option
                            v-for="m in AGENT_MODELS"
                            :key="m.value"
                            :value="m.value"
                          >
                            {{ m.label }}
                          </option>
                        </select>
                      </div>
                      <div>
                        <label
                          class="text-xs font-medium text-n-slate-11 block mb-1.5"
                        >
                          Esforço
                          <span class="text-n-slate-9 font-normal"
                            >(quanto pensa)</span
                          >
                        </label>
                        <select
                          v-model="agent.effort"
                          :disabled="!editingAgent[key]"
                          class="cv-input w-full text-sm disabled:cursor-not-allowed"
                        >
                          <option value="">
                            ⭐ {{ recommendedEffortLabel(key) }}
                          </option>
                          <option
                            v-for="e in AGENT_EFFORTS"
                            :key="e.value"
                            :value="e.value"
                          >
                            {{ e.label }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <!-- Em uso + dica + uso 30d -->
                    <div class="flex items-center gap-2 flex-wrap mb-4">
                      <span
                        class="cv-chip cv-chip-lg cv-chip-on"
                        :style="{ background: AGENT_META[key].gradient }"
                      >
                        Em uso: {{ resolvedModel(key, agent) }} · esforço
                        {{ resolvedEffort(key, agent) }}
                      </span>
                      <span v-if="usageByAgent(key)" class="cv-chip cv-chip-lg">
                        30 dias: {{ usageByAgent(key).calls }}
                        {{ usageNoun(key) }} ·
                        {{ fmtUsd(usageByAgent(key).cost_usd) }}
                      </span>
                      <span
                        class="text-[11px] text-n-slate-9 flex items-center gap-1"
                      >
                        <span class="i-lucide-lightbulb text-xs" />
                        {{ AGENT_META[key].suggestion }}
                      </span>
                    </div>

                    <!-- 🗣️ Config específica do Atendente de Agendamento (rodada 188): sombra × ao vivo, caixas, colunas -->
                    <div
                      v-if="RESPONDER_WA_KEYS.includes(key)"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <!-- 🎛 Painel de situação -->
                      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div
                          class="cv-stat px-3 py-2"
                          :class="
                            situationOf(agent).tone === 'live'
                              ? 'cv-green'
                              : situationOf(agent).tone === 'shadow'
                                ? 'cv-slate'
                                : ''
                          "
                        >
                          <p class="text-[10px] text-n-slate-10">Situação</p>
                          <p
                            class="text-sm font-bold"
                            :class="
                              situationOf(agent).tone === 'live'
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-n-slate-12'
                            "
                          >
                            {{ situationOf(agent).label }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">
                            Caixas em que lê
                          </p>
                          <p
                            class="text-sm font-bold text-n-slate-12"
                            :title="atendInboxNames(agent).join(', ')"
                          >
                            {{
                              atendInboxNames(agent).length
                                ? atendInboxNames(agent).join(', ')
                                : 'nenhuma'
                            }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">
                            Colunas dele
                          </p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{
                              atendStageNames(agent).length +
                              (key === 'atendente_agendamento' &&
                              agent.no_card !== false
                                ? 1
                                : 0)
                            }}
                            <span
                              class="text-[10px] font-normal text-n-slate-9"
                              >{{
                                key === 'atendente_agendamento' &&
                                agent.no_card !== false
                                  ? '(inclui sem card)'
                                  : ''
                              }}</span
                            >
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">Sombra hoje</p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{ shadowTodayConversations(key) }}
                            <span class="text-[10px] font-normal text-n-slate-9"
                              >/
                              {{ agent.shadow_daily_cap || 30 }} conversas</span
                            >
                          </p>
                        </div>
                      </div>

                      <!-- 🕶️ o que é a Sombra, em linguagem simples + comandos para desligar a IA (pedido 21/09) -->
                      <div
                        class="cv-sub p-3.5 text-[11px] text-n-slate-11 leading-relaxed space-y-1.5"
                      >
                        <p>
                          <b>🕶️ Sombra, em uma frase:</b> o agente lê cada
                          mensagem do paciente e escreve numa
                          <b>nota interna</b> o que responderia{{
                            key === 'atendente_agendamento'
                              ? ' (e se agendaria, com vaga real)'
                              : ' (e se remarcaria ou cancelaria)'
                          }}. <b>O paciente não recebe nada.</b> É um estágio de
                          treino: você confere na tela Sombra, orienta, e só
                          depois liga de verdade.
                        </p>
                        <p>
                          <b>🟢 Ao vivo:</b> responde o paciente de verdade, só
                          na janela de dias e horas escolhida abaixo. Fora da
                          janela volta para a sombra sozinho. Ele só responde,
                          nunca puxa conversa.
                        </p>
                        <p class="cv-label !text-[10px] pt-1">
                          Comandos para desligar a IA
                        </p>
                        <ul class="list-disc pl-4 space-y-0.5">
                          <li>
                            <b>Numa conversa:</b> responda o paciente você
                            mesmo. Qualquer mensagem sua pausa o agente só nessa
                            conversa (ele deixa a nota ⏸).
                          </li>
                          <li>
                            <b>Reativar na conversa:</b> mande 👍 (só o polegar)
                            como mensagem. Ele deixa a nota ▶️ e volta a
                            responder.
                          </li>
                          <li>
                            <b>Parar tudo agora:</b> o interruptor no topo deste
                            card desliga na hora, em todas as conversas.
                          </li>
                          <li>
                            Em sombra nada disso é preciso: ele nunca fala com o
                            paciente.
                          </li>
                        </ul>
                      </div>

                      <!-- modo -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Modo de trabalho
                        </p>
                        <div class="cv-seg cv-seg-sm cv-green">
                          <button
                            class="cv-seg-item"
                            :class="agent.mode !== 'live' ? 'cv-seg-on' : ''"
                            :disabled="!editingAgent[key]"
                            title="Só anota o que teria respondido (nota interna). Nada chega ao paciente."
                            @click="agent.mode = 'shadow'"
                          >
                            🕶️ Sombra
                          </button>
                          <button
                            class="cv-seg-item"
                            :class="[
                              agent.mode === 'live' ? 'cv-seg-on' : '',
                              liveEnabled
                                ? ''
                                : 'opacity-60 cursor-not-allowed',
                            ]"
                            :disabled="!editingAgent[key] || !liveEnabled"
                            :title="
                              liveEnabled
                                ? 'Responde de verdade ao paciente na janela escolhida abaixo'
                                : 'Trancado no servidor: só abre com a variável CEVICO_RESPONDERS_LIVE=true no EasyPanel (web e sidekiq). Até lá, tudo em sombra.'
                            "
                            @click="agent.mode = 'live'"
                          >
                            <span
                              v-if="!liveEnabled"
                              class="i-lucide-lock text-[10px]"
                            />
                            🟢 Ao vivo
                          </button>
                        </div>
                        <p
                          v-if="!editingAgent[key]"
                          class="text-[10px] text-n-slate-9 mt-1"
                        >
                          Clique em Editar (no fim do card) para trocar o modo e
                          a janela.
                        </p>
                      </div>

                      <!-- 🟢 rodada 193: JANELA AO VIVO (dias + horas) -->
                      <div
                        v-if="agent.mode === 'live'"
                        class="cv-sub cv-green p-3.5 space-y-2.5"
                      >
                        <p class="text-xs font-bold text-n-slate-12">
                          🟢 Janela ao vivo
                          <span class="text-n-slate-9 font-normal"
                            >(nenhum dia marcado = todos os dias; as horas ficam
                            no campo "Horário em que atende sozinho"
                            abaixo)</span
                          >
                        </p>
                        <div class="flex flex-wrap gap-1.5">
                          <button
                            v-for="d in LIVE_DAYS"
                            :key="d.value"
                            class="cv-chip cv-chip-lg"
                            :class="
                              liveDayOn(agent, d.value) ? 'cv-chip-on' : ''
                            "
                            :style="
                              liveDayOn(agent, d.value)
                                ? {
                                    background:
                                      'linear-gradient(135deg, #059669, #34D399)',
                                  }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            @click="toggleLiveDay(key, d.value)"
                          >
                            {{ d.short }}
                          </button>
                        </div>
                        <p class="text-xs font-semibold text-n-slate-12">
                          {{ liveWindowSentence(agent) }}
                        </p>
                        <div
                          class="cv-sub cv-amber px-3 py-2 text-[11px] text-n-slate-12 leading-relaxed"
                        >
                          ⚠️ <b>Nessa janela o N8N tem que estar DESLIGADO</b>
                          para essas caixas — nunca os dois ao vivo ao mesmo
                          tempo, senão o paciente recebe duas respostas.
                        </div>
                        <p
                          v-if="!(agent.inbox_ids || []).length"
                          class="text-[11px] text-red-600 dark:text-red-400 font-medium"
                        >
                          Marque pelo menos uma caixa de WhatsApp: ao vivo sem
                          caixa não publica.
                        </p>
                      </div>

                      <!-- caixas -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Caixas de WhatsApp em que ele lê
                          <span class="text-n-slate-9 font-normal"
                            >(nenhuma marcada = desligado na prática)</span
                          >
                        </p>
                        <div class="flex flex-wrap gap-1.5">
                          <button
                            v-for="ib in whatsappInboxes"
                            :key="ib.id"
                            class="cv-chip cv-chip-lg"
                            :class="
                              (agent.inbox_ids || []).includes(ib.id)
                                ? 'cv-chip-on'
                                : ''
                            "
                            :style="
                              (agent.inbox_ids || []).includes(ib.id)
                                ? {
                                    background:
                                      'linear-gradient(135deg, #059669, #34D399)',
                                  }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            @click="toggleAtendInbox(key, ib.id)"
                          >
                            {{ ib.name }}
                          </button>
                          <p
                            v-if="!whatsappInboxes.length"
                            class="text-[11px] text-n-slate-10"
                          >
                            Nenhuma caixa de WhatsApp na conta ainda.
                          </p>
                        </div>
                      </div>

                      <!-- colunas: quem fala é decidido pela coluna do card -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Colunas do CRM em que ELE fala
                          <span class="text-n-slate-9 font-normal"
                            >(o card do paciente nessas colunas = este agente;
                            nas outras, ninguém)</span
                          >
                        </p>
                        <div
                          v-if="!allStages.length"
                          class="text-xs text-n-slate-9"
                        >
                          Carregando colunas…
                        </div>
                        <div v-else class="flex flex-wrap gap-1.5">
                          <button
                            v-if="key === 'atendente_agendamento'"
                            class="cv-chip cv-chip-lg"
                            :class="agent.no_card !== false ? 'cv-chip-on' : ''"
                            :style="
                              agent.no_card !== false
                                ? {
                                    background:
                                      'linear-gradient(135deg, #152C61, #0F5FA6)',
                                  }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            title="Contato que ainda não tem card no CRM (primeira mensagem)"
                            @click="agent.no_card = agent.no_card === false"
                          >
                            <span class="i-lucide-user-plus text-[10px]" /> Sem
                            card (contato novo)
                          </button>
                          <button
                            v-for="st in allStages"
                            :key="st.id"
                            class="cv-chip cv-chip-lg"
                            :class="
                              (agent.stage_ids || []).includes(st.id)
                                ? 'cv-chip-on'
                                : ''
                            "
                            :style="
                              (agent.stage_ids || []).includes(st.id)
                                ? {
                                    background:
                                      'linear-gradient(135deg, #059669, #34D399)',
                                  }
                                : {}
                            "
                            :disabled="
                              !editingAgent[key] ||
                              !!stageOwnedByOther(key, st.id)
                            "
                            :title="
                              stageOwnedByOther(key, st.id)
                                ? `já é do ${AGENT_META[stageOwnedByOther(key, st.id)].title}`
                                : st.pipeline
                            "
                            @click="toggleAtendStage(key, st.id)"
                          >
                            {{ st.name }}
                          </button>
                        </div>
                        <p class="text-[10px] text-n-slate-9 mt-1">
                          {{
                            key === 'atendente_agendamento'
                              ? 'Sugestão inicial: Sem card + Novos Contatos + Envio de Orçamento.'
                              : key === 'atendente_pos_op'
                                ? 'Sugestão inicial: Cirurgia Realizada + Pós Operatório (e a janela de cirurgia recente abaixo cobre quem está fora da coluna).'
                                : 'Sugestão inicial: Agendamento de Consulta + Consulta Confirmada + Desmarcou a Consulta.'
                          }}
                          Card mudou de coluna = muda quem fala, na hora. Uma
                          coluna tem um dono só.
                        </p>
                      </div>

                      <!-- 🩺 pós-op (item 251): quem recebe as tarefas + cirurgia recente pela Agenda -->
                      <!-- 🔁 item 343: "Responsável pelas tarefas" nos três atendentes (+ Equipe dos retornos) -->
                      <div
                        v-if="RESPONDER_WA_KEYS.includes(key)"
                        class="grid grid-cols-1 sm:grid-cols-2 gap-3"
                      >
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Responsável pelas tarefas
                            <span class="text-n-slate-9 font-normal"
                              >(Meu Painel)</span
                            ></label
                          >
                          <select
                            v-model="agent.task_assignee_id"
                            :disabled="!editingAgent[key]"
                            class="cv-input w-full text-sm disabled:cursor-not-allowed"
                          >
                            <option value="">
                              — quem cuida da coluna do paciente —
                            </option>
                            <option value="retorno_team">
                              Equipe dos retornos (as duas veem)
                            </option>
                            <option
                              v-for="u in teamAgents"
                              :key="`pos-op-owner-${u.id}`"
                              :value="u.id"
                            >
                              {{ u.name }}
                            </option>
                          </select>
                          <p class="text-[10px] text-n-slate-9 mt-1">
                            Sem escolha: vale quem cuida da coluna do paciente
                            (Painéis) ou o responsável pela conferência de
                            {{
                              key === 'atendente_pos_op'
                                ? 'cirurgia'
                                : 'consulta'
                            }}.
                            <template v-if="key !== 'atendente_pos_op'"
                              >Paciente de retorno vai sempre para a Equipe dos
                              retornos (Agenda → Conferência do dia).</template
                            >
                            Urgência também acende o aviso no painel.
                          </p>
                        </div>
                        <div v-if="key === 'atendente_pos_op'">
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Cirurgia recente na Agenda
                            <span class="text-n-slate-9 font-normal"
                              >(dias)</span
                            ></label
                          >
                          <input
                            v-model.number="agent.recent_surgery_days"
                            type="number"
                            min="0"
                            max="365"
                            :disabled="!editingAgent[key]"
                            class="cv-input w-full text-sm disabled:cursor-not-allowed"
                          />
                          <p class="text-[10px] text-n-slate-9 mt-1">
                            Quem operou nesses últimos dias (pela Agenda,
                            inclusive Oftalmofácil) é atendido por este agente
                            mesmo que o card não esteja na coluna. 0 = só pela
                            coluna.
                          </p>
                        </div>
                      </div>

                      <!-- ao agendar → coluna / teto da sombra / responde em / horário -->
                      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div v-if="key === 'atendente_agendamento'">
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Ao agendar (ao vivo), mover o card para</label
                          >
                          <select
                            v-model="agent.after_booking_stage_id"
                            :disabled="!editingAgent[key]"
                            class="cv-input w-full text-sm disabled:cursor-not-allowed"
                          >
                            <option value="">— não mover —</option>
                            <option
                              v-for="st in allStages"
                              :key="st.id"
                              :value="st.id"
                            >
                              {{ st.name }} ({{ st.pipeline }})
                            </option>
                          </select>
                        </div>
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Teto da sombra
                            <span class="text-n-slate-9 font-normal"
                              >(conversas por dia)</span
                            ></label
                          >
                          <input
                            v-model.number="agent.shadow_daily_cap"
                            type="number"
                            min="1"
                            max="500"
                            :disabled="!editingAgent[key]"
                            class="cv-input w-full text-sm disabled:cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                          >
                            Responde em
                            <span class="text-n-slate-9 font-normal">
                              (segundos após a mensagem)
                            </span>
                          </label>
                          <input
                            v-model.number="agent.reply_delay_seconds"
                            type="number"
                            min="3"
                            max="30"
                            :disabled="!editingAgent[key]"
                            class="cv-input w-full text-sm disabled:cursor-not-allowed"
                          />
                          <p class="text-[10px] text-n-slate-9 mt-1">
                            Entre 5 e 10 s parece humano e junta mensagens
                            picadas.
                          </p>
                        </div>
                        <div
                          class="sm:col-span-3 flex items-start gap-2 text-[11px] leading-snug rounded-lg px-3 py-2"
                          :class="
                            geminiKeySet
                              ? 'bg-n-alpha-1 text-n-slate-11'
                              : 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                          "
                        >
                          <span
                            :class="
                              geminiKeySet
                                ? 'i-lucide-ear'
                                : 'i-lucide-triangle-alert'
                            "
                            class="text-sm shrink-0 mt-px"
                          />
                          <span v-if="geminiKeySet">
                            <b>Áudio e imagem:</b> o sistema transcreve os
                            áudios e lê as imagens que o paciente manda (Gemini)
                            antes de o agente responder. A equipe vê o texto
                            embaixo do anexo, na conversa.
                          </span>
                          <span v-else>
                            <b>Áudio e imagem:</b> configure a chave do Gemini
                            em Integrações → IA para o agente ouvir áudios e ler
                            imagens. Sem ela, ele pede para o paciente escrever.
                          </span>
                        </div>
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Horário em que atende sozinho
                            <span class="text-n-slate-9 font-normal"
                              >(janela ao vivo; vazio = o dia inteiro)</span
                            ></label
                          >
                          <div class="flex items-center gap-1.5">
                            <input
                              v-model="agent.hours_start"
                              type="time"
                              :disabled="!editingAgent[key]"
                              class="cv-input flex-1 min-w-0 text-sm disabled:cursor-not-allowed"
                            />
                            <span class="text-[11px] text-n-slate-9">até</span>
                            <input
                              v-model="agent.hours_end"
                              type="time"
                              :disabled="!editingAgent[key]"
                              class="cv-input flex-1 min-w-0 text-sm disabled:cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="flex items-center gap-2 flex-wrap">
                        <button
                          class="cv-btn cv-btn-sm cv-green"
                          title="Converse com o agente como se fosse o paciente — IA de verdade, caixa interna sem envio"
                          @click="openTestAgent(key)"
                        >
                          <span class="i-lucide-flask-conical text-xs" /> 🧪
                          Testar agente
                        </button>
                        <button
                          class="cv-btn cv-btn-sm"
                          @click="
                            shadowAgentKey = key;
                            shadowOpen = true;
                          "
                        >
                          <span class="i-lucide-columns-2 text-xs" /> 🕶️ Abrir
                          tela Sombra (interno × N8N)
                        </button>
                        <button
                          class="cv-btn cv-btn-sm cv-btn-ghost"
                          @click="
                            scriptExpanded = true;
                            scriptOpenSection = 'persona';
                            document
                              .getElementById('cv-roteiro')
                              ?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              });
                          "
                        >
                          <span class="i-lucide-scroll-text text-xs" /> Ver o
                          Roteiro CEVICO
                        </button>
                        <span class="text-[10px] text-n-slate-9"
                          >O campo <b>Passos desta etapa</b> abaixo é só o passo
                          a passo deste agente{{
                            key === 'atendente_agendamento'
                              ? ' (recepção → agendamento)'
                              : ' (suporte a quem já marcou)'
                          }}.</span
                        >
                      </div>

                      <!-- 📒 Registro de atividade -->
                      <div v-if="atendenteEvents(key).length">
                        <p class="cv-label mb-1">📒 Registro de atividade</p>
                        <div class="max-h-40 overflow-y-auto space-y-1">
                          <p
                            v-for="(ev, i) in atendenteEvents(key)"
                            :key="i"
                            class="text-[11px] text-n-slate-11"
                          >
                            <span class="text-n-slate-9">{{
                              new Date(ev.at).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            }}</span>
                            · #{{ ev.conversation_id }} {{ ev.contact }} —
                            {{ ATENDENTE_EVENT_LABELS[ev.type] || ev.type }}
                            <span v-if="ev.note" class="text-n-slate-9"
                              >({{ ev.note }})</span
                            >
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- 🎙️ rodada 195: Agente de Ligação — leads não responsivos (sombra × ao vivo, colunas, ritmo, janela, caixa) -->
                    <div
                      v-if="key === 'voice'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <!-- 🎛 Painel de situação -->
                      <div
                        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2"
                      >
                        <div
                          class="cv-stat px-3 py-2"
                          :class="
                            voiceSituation(agent).tone === 'live'
                              ? 'cv-green'
                              : voiceSituation(agent).tone === 'shadow'
                                ? 'cv-slate'
                                : ''
                          "
                        >
                          <p class="text-[10px] text-n-slate-10">Situação</p>
                          <p
                            class="text-sm font-bold"
                            :class="
                              voiceSituation(agent).tone === 'live'
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-n-slate-12'
                            "
                          >
                            {{ voiceSituation(agent).label }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">
                            Colunas vigiadas
                          </p>
                          <p
                            class="text-sm font-bold text-n-slate-12"
                            :title="voiceStageNames(agent).join(', ')"
                          >
                            {{ voiceStageNames(agent).length || 'nenhuma' }}
                            <span
                              v-if="voiceStageNames(agent).length"
                              class="text-[10px] font-normal text-n-slate-9 block"
                              >{{ voiceStageNames(agent).join(' · ') }}</span
                            >
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">
                            {{
                              voiceSituation(agent).tone === 'live'
                                ? 'Ligações hoje'
                                : 'Ligaria hoje'
                            }}
                          </p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{
                              voiceSituation(agent).tone === 'live'
                                ? voiceCallsToday()
                                : voiceShadowItems().length
                            }}
                            <span class="text-[10px] font-normal text-n-slate-9"
                              >/ {{ agent.daily_cap || 20 }} por dia</span
                            >
                          </p>
                        </div>
                        <div
                          class="cv-stat px-3 py-2"
                          :class="voiceReady ? 'cv-green' : 'cv-amber'"
                        >
                          <p class="text-[10px] text-n-slate-10">ElevenLabs</p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{
                              voiceReady
                                ? '✅ conectada'
                                : '⚠️ falta configurar'
                            }}
                          </p>
                          <button
                            class="text-[10px] underline text-n-slate-10 hover:text-n-slate-12"
                            @click="openVoiceSettings"
                          >
                            {{
                              voiceReady
                                ? 'voz e número'
                                : 'configurar em Integrações'
                            }}
                          </button>
                        </div>
                      </div>

                      <!-- 🕶️ o que é a Sombra aqui + comandos para desligar -->
                      <div
                        class="cv-sub p-3.5 text-[11px] text-n-slate-11 leading-relaxed space-y-1.5"
                      >
                        <p>
                          <b>🕶️ Sombra, em uma frase:</b> de hora em hora ele
                          monta a lista de quem ligaria hoje (leads parados nas
                          colunas vigiadas) e você testa a conversa por texto.
                          <b>Nada é discado.</b>
                        </p>
                        <p>
                          <b>🟢 Ao vivo:</b> liga de verdade pela ElevenLabs, só
                          na janela de dias e horas escolhida abaixo. Quem
                          atende e marca entra na Agenda e recebe a confirmação
                          no WhatsApp; quem não pode falar segue por escrito.
                        </p>
                        <p class="cv-label !text-[10px] pt-1">
                          Comandos para desligar a IA
                        </p>
                        <ul class="list-disc pl-4 space-y-0.5">
                          <li>
                            <b>Parar tudo agora:</b> o interruptor no topo deste
                            card desliga na hora.
                          </li>
                          <li>
                            Ele <b>nunca liga duas vezes no mesmo dia</b> para a
                            mesma pessoa, e espera 48 h entre tentativas.
                          </li>
                          <li>
                            <b>Tirar alguém da lista:</b> mova o card para outra
                            coluna ou marque a etiqueta
                            <code>nao_perturbe</code>.
                          </li>
                        </ul>
                      </div>

                      <!-- modo -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Modo de trabalho
                        </p>
                        <div class="cv-seg cv-seg-sm cv-green">
                          <button
                            class="cv-seg-item"
                            :class="agent.mode !== 'live' ? 'cv-seg-on' : ''"
                            :disabled="!editingAgent[key]"
                            title="Só monta a lista de quem ligaria e permite testar por texto. Nada é discado."
                            @click="agent.mode = 'shadow'"
                          >
                            🕶️ Sombra
                          </button>
                          <button
                            class="cv-seg-item"
                            :class="[
                              agent.mode === 'live' ? 'cv-seg-on' : '',
                              liveEnabled
                                ? ''
                                : 'opacity-60 cursor-not-allowed',
                            ]"
                            :disabled="!editingAgent[key] || !liveEnabled"
                            :title="
                              liveEnabled
                                ? 'Liga de verdade pela ElevenLabs na janela escolhida abaixo'
                                : 'Trancado no servidor: só abre com a variável CEVICO_RESPONDERS_LIVE=true no EasyPanel (web e sidekiq). Até lá, tudo em sombra.'
                            "
                            @click="agent.mode = 'live'"
                          >
                            <span
                              v-if="!liveEnabled"
                              class="i-lucide-lock text-[10px]"
                            />
                            🟢 Ao vivo
                          </button>
                        </div>
                        <p
                          v-if="!editingAgent[key]"
                          class="text-[10px] text-n-slate-9 mt-1"
                        >
                          Clique em Editar (no fim do card) para trocar o modo,
                          as colunas e a janela.
                        </p>
                      </div>

                      <!-- 🟢 janela ao vivo (dias + horas) -->
                      <div
                        v-if="agent.mode === 'live'"
                        class="cv-sub cv-green p-3.5 space-y-2.5"
                      >
                        <p class="text-xs font-bold text-n-slate-12">
                          🟢 Janela ao vivo
                          <span class="text-n-slate-9 font-normal"
                            >(nenhum dia marcado = todos os dias; as horas ficam
                            no campo "Horário em que liga" abaixo)</span
                          >
                        </p>
                        <div class="flex flex-wrap gap-1.5">
                          <button
                            v-for="d in LIVE_DAYS"
                            :key="d.value"
                            class="cv-chip cv-chip-lg"
                            :class="
                              liveDayOn(agent, d.value) ? 'cv-chip-on' : ''
                            "
                            :style="
                              liveDayOn(agent, d.value)
                                ? {
                                    background:
                                      'linear-gradient(135deg, #059669, #34D399)',
                                  }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            @click="toggleLiveDay(key, d.value)"
                          >
                            {{ d.short }}
                          </button>
                        </div>
                        <p class="text-xs font-semibold text-n-slate-12">
                          {{ liveWindowSentence(agent) }}
                        </p>
                        <p
                          v-if="!(agent.stage_ids || []).length"
                          class="text-[11px] text-red-600 dark:text-red-400 font-medium"
                        >
                          Marque pelo menos uma coluna para vigiar: ao vivo sem
                          coluna não publica.
                        </p>
                        <p
                          v-if="!voiceReady"
                          class="text-[11px] text-red-600 dark:text-red-400 font-medium"
                        >
                          A ElevenLabs ainda não está configurada (chave, voz e
                          número em Integrações): ao vivo não publica sem ela.
                        </p>
                      </div>

                      <!-- colunas vigiadas -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Colunas que ele vigia
                          <span class="text-n-slate-9 font-normal"
                            >(lead parado nessas colunas, sem responder =
                            candidato à ligação)</span
                          >
                        </p>
                        <div
                          v-if="!allStages.length"
                          class="text-xs text-n-slate-9"
                        >
                          Carregando colunas…
                        </div>
                        <div v-else class="flex flex-wrap gap-1.5">
                          <button
                            v-for="st in allStages"
                            :key="st.id"
                            class="cv-chip cv-chip-lg"
                            :class="
                              (agent.stage_ids || []).includes(st.id)
                                ? 'cv-chip-on'
                                : ''
                            "
                            :style="
                              (agent.stage_ids || []).includes(st.id)
                                ? { background: AGENT_META[key].gradient }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            :title="st.pipeline"
                            @click="toggleAtendStage(key, st.id)"
                          >
                            {{ st.name }}
                          </button>
                        </div>
                        <p class="text-[10px] text-n-slate-9 mt-1">
                          Sugestão inicial: Envio de Orçamento + Novos Contatos.
                          Card mudou de coluna = sai da lista na hora.
                        </p>
                      </div>

                      <!-- ritmo: horas de silêncio, dias olhados, teto, tentativas -->
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Liga depois de
                            <span class="text-n-slate-9 font-normal"
                              >(horas sem resposta do lead)</span
                            ></label
                          >
                          <div class="flex items-center gap-2">
                            <input
                              v-model.number="agent.silence_hours"
                              type="number"
                              min="1"
                              max="720"
                              :disabled="!editingAgent[key]"
                              class="cv-input w-full text-sm disabled:cursor-not-allowed"
                            />
                            <span class="text-xs text-n-slate-10 flex-shrink-0"
                              >horas</span
                            >
                          </div>
                        </div>
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Olha leads dos últimos
                            <span class="text-n-slate-9 font-normal"
                              >(mais antigo que isso, deixa quieto)</span
                            ></label
                          >
                          <div class="flex items-center gap-2">
                            <input
                              v-model.number="agent.lookback_days"
                              type="number"
                              min="1"
                              max="90"
                              :disabled="!editingAgent[key]"
                              class="cv-input w-full text-sm disabled:cursor-not-allowed"
                            />
                            <span class="text-xs text-n-slate-10 flex-shrink-0"
                              >dias</span
                            >
                          </div>
                        </div>
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >No máximo
                            <span class="text-n-slate-9 font-normal"
                              >(ligações por dia)</span
                            ></label
                          >
                          <div class="flex items-center gap-2">
                            <input
                              v-model.number="agent.daily_cap"
                              type="number"
                              min="1"
                              max="500"
                              :disabled="!editingAgent[key]"
                              class="cv-input w-full text-sm disabled:cursor-not-allowed"
                            />
                            <span class="text-xs text-n-slate-10 flex-shrink-0"
                              >ligações por dia</span
                            >
                          </div>
                        </div>
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Tentativas por lead
                            <span class="text-n-slate-9 font-normal"
                              >(em 14 dias, com 48 h entre elas)</span
                            ></label
                          >
                          <div class="flex items-center gap-2">
                            <input
                              v-model.number="agent.max_attempts"
                              type="number"
                              min="1"
                              max="10"
                              :disabled="!editingAgent[key]"
                              class="cv-input w-full text-sm disabled:cursor-not-allowed"
                            />
                            <span class="text-xs text-n-slate-10 flex-shrink-0"
                              >tentativas</span
                            >
                          </div>
                        </div>
                      </div>

                      <!-- horário + caixa do WhatsApp -->
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Horário em que liga
                            <span class="text-n-slate-9 font-normal"
                              >(janela ao vivo; vazio = o dia inteiro)</span
                            ></label
                          >
                          <div class="flex items-center gap-1.5">
                            <input
                              v-model="agent.hours_start"
                              type="time"
                              :disabled="!editingAgent[key]"
                              class="cv-input flex-1 min-w-0 text-sm disabled:cursor-not-allowed"
                            />
                            <span class="text-[11px] text-n-slate-9">até</span>
                            <input
                              v-model="agent.hours_end"
                              type="time"
                              :disabled="!editingAgent[key]"
                              class="cv-input flex-1 min-w-0 text-sm disabled:cursor-not-allowed"
                            />
                          </div>
                        </div>
                        <div>
                          <label
                            class="text-xs font-medium text-n-slate-11 block mb-1.5"
                            >Caixa do WhatsApp para continuar por escrito
                            <span class="text-n-slate-9 font-normal"
                              >(quem não pode falar recebe por aqui)</span
                            ></label
                          >
                          <select
                            v-model="agent.handoff_inbox_id"
                            :disabled="!editingAgent[key]"
                            class="cv-input w-full text-sm disabled:cursor-not-allowed"
                          >
                            <option value="">— escolher a caixa —</option>
                            <option
                              v-for="ib in whatsappInboxes"
                              :key="ib.id"
                              :value="ib.id"
                            >
                              {{ ib.name }}
                            </option>
                          </select>
                          <p
                            v-if="!whatsappInboxes.length"
                            class="text-[10px] text-n-slate-9 mt-1"
                          >
                            Nenhuma caixa de WhatsApp na conta ainda.
                          </p>
                          <p
                            v-else-if="!agent.handoff_inbox_id"
                            class="text-[10px] text-amber-600 dark:text-amber-400 mt-1"
                          >
                            Sem caixa, ele não consegue mandar a confirmação nem
                            seguir por escrito.
                          </p>
                        </div>
                      </div>

                      <!-- botões -->
                      <div class="flex items-center gap-2 flex-wrap">
                        <button
                          class="cv-btn cv-btn-sm cv-green"
                          title="Converse por texto como se fosse o lead que atendeu o telefone — IA de verdade, nada é discado"
                          @click="openTestAgent(key)"
                        >
                          <span class="i-lucide-flask-conical text-xs" /> 🧪
                          Testar agente
                        </button>
                        <button
                          class="cv-btn cv-btn-sm"
                          :disabled="voiceShadowRunning"
                          title="Roda a seleção agora, em sombra, e mostra a lista (nada é discado)"
                          @click="runVoiceShadow"
                        >
                          <span
                            :class="
                              voiceShadowRunning
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-clipboard-list'
                            "
                            class="text-xs"
                          />
                          📋 Ver quem ligaria hoje
                        </button>
                        <button
                          class="cv-btn cv-btn-sm cv-btn-ghost"
                          title="Chave, voz, número e sincronização com a ElevenLabs (Integrações)"
                          @click="openVoiceSettings"
                        >
                          <span class="i-lucide-mic text-xs" /> 🎙️ Voz e número
                        </button>
                        <button
                          class="cv-btn cv-btn-sm cv-btn-ghost"
                          @click="
                            scriptExpanded = true;
                            scriptOpenSection = 'persona';
                            document
                              .getElementById('cv-roteiro')
                              ?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start',
                              });
                          "
                        >
                          <span class="i-lucide-scroll-text text-xs" /> Ver o
                          Roteiro CEVICO
                        </button>
                        <span class="text-[10px] text-n-slate-9"
                          >O campo <b>Passos desta ligação</b> abaixo é só o
                          passo a passo dele ao telefone.</span
                        >
                      </div>

                      <!-- 📋 quem ele ligaria hoje -->
                      <div
                        v-if="voiceShadowItems().length"
                        class="cv-sub p-3.5 space-y-1.5"
                      >
                        <p class="cv-label mb-1">
                          📋
                          {{
                            voiceSituation(agent).tone === 'live'
                              ? 'Fila de hoje'
                              : 'Ligaria hoje'
                          }}
                          para {{ voiceShadowItems().length }} pessoa(s)
                          <span class="font-normal normal-case text-n-slate-9"
                            >· nada é discado em sombra</span
                          >
                        </p>
                        <div class="max-h-56 overflow-y-auto space-y-1.5">
                          <div
                            v-for="(it, i) in voiceShadowItems()"
                            :key="it.contact_id || i"
                            class="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3 text-[11px] text-n-slate-11 border-b last:border-b-0 pb-1.5"
                            style="border-color: rgb(var(--cv-rgb) / 0.12)"
                          >
                            <span
                              class="font-semibold text-n-slate-12 sm:w-40 flex-shrink-0"
                            >
                              {{ it.name || 'sem nome' }}
                              <span
                                v-if="it.phone_final"
                                class="font-normal text-n-slate-9"
                                >· …{{ it.phone_final }}</span
                              >
                            </span>
                            <span
                              v-if="it.stage"
                              class="cv-chip cv-slate flex-shrink-0 self-start"
                              >{{ it.stage }}</span
                            >
                            <span class="flex-1 min-w-0 leading-relaxed">{{
                              it.motivo || it.objective || ''
                            }}</span>
                          </div>
                        </div>
                      </div>
                      <p
                        v-else-if="voiceShadowList !== null"
                        class="text-[11px] text-n-slate-10"
                      >
                        Ninguém se encaixa agora: nenhum lead parado nas colunas
                        vigiadas dentro do prazo.
                      </p>

                      <!-- 📒 Registro de atividade -->
                      <div v-if="voiceEvents().length">
                        <p class="cv-label mb-1">📒 Registro de atividade</p>
                        <div class="max-h-40 overflow-y-auto space-y-1">
                          <p
                            v-for="(ev, i) in voiceEvents()"
                            :key="i"
                            class="text-[11px] text-n-slate-11"
                          >
                            <span class="text-n-slate-9">{{
                              ev.at
                                ? new Date(ev.at).toLocaleString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : ''
                            }}</span>
                            <span v-if="ev.contact || ev.name">
                              · {{ ev.contact || ev.name }}</span
                            >
                            —
                            {{ VOICE_EVENT_LABELS[ev.type] || ev.type }}
                            <span v-if="ev.count" class="text-n-slate-9"
                              >({{ ev.count }})</span
                            >
                            <span v-if="ev.note" class="text-n-slate-9"
                              >({{ ev.note }})</span
                            >
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Config específica do Atendente Instagram (respondedor) -->
                    <div
                      v-if="key === 'instagram'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <!-- 🎛 Painel de situação: a configuração dele, num relance -->
                      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div
                          class="cv-stat px-3 py-2"
                          :class="agent.enabled ? 'cv-green' : ''"
                        >
                          <p class="text-[10px] text-n-slate-10">Situação</p>
                          <p
                            class="text-sm font-bold"
                            :class="
                              agent.enabled
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-n-slate-11'
                            "
                          >
                            {{ agent.enabled ? '● Atendendo' : '○ Desligado' }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">
                            Caixas em que atende
                          </p>
                          <p
                            class="text-sm font-bold text-n-slate-12 truncate"
                            :title="instagramInboxNames(agent).join(', ')"
                          >
                            {{
                              instagramInboxNames(agent).length
                                ? instagramInboxNames(agent).join(', ')
                                : 'nenhuma'
                            }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">
                            Respostas
                            <span class="text-n-slate-9">(últ. registros)</span>
                          </p>
                          <p class="text-sm font-bold text-n-slate-12">
                            💬 {{ instagramStats().replied }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">
                            Agendamentos / avisos
                          </p>
                          <p class="text-sm font-bold text-n-slate-12">
                            📅 {{ instagramStats().scheduled }}
                            <span
                              v-if="instagramStats().errors"
                              class="text-amber-500"
                              >· ⚠️ {{ instagramStats().errors }}</span
                            >
                          </p>
                        </div>
                      </div>
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        ⚠️ Este agente FALA COM O PACIENTE nas caixas abaixo.
                        Ele agenda só na Agenda interna, nunca inventa
                        valores/horários, e as confirmações oficiais vão pelo
                        WhatsApp. Se um humano responder na conversa, ele PAUSA
                        na hora — 👍 do atendimento reativa.
                      </div>
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Caixas de entrada em que ele atende
                          <span class="text-n-slate-9 font-normal"
                            >(nenhuma marcada = desligado na prática)</span
                          >
                        </p>
                        <div class="flex flex-wrap gap-1.5">
                          <button
                            v-for="ib in inboxes"
                            :key="ib.id"
                            class="cv-chip cv-chip-lg"
                            :class="
                              (agent.inbox_ids || []).includes(ib.id)
                                ? 'cv-chip-on'
                                : ''
                            "
                            :style="
                              (agent.inbox_ids || []).includes(ib.id)
                                ? {
                                    background:
                                      'linear-gradient(135deg, #C2185B, #7C3AED)',
                                  }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            @click="toggleInstagramInbox(ib.id)"
                          >
                            {{ ib.name }}
                          </button>
                          <p
                            v-if="!inboxes.length"
                            class="text-[11px] text-n-slate-10"
                          >
                            Nenhuma caixa de entrada na conta ainda.
                          </p>
                        </div>
                        <p class="text-[10px] text-n-slate-9 mt-1">
                          Escolha a caixa do Instagram quando ela for conectada
                          (Configurações → Caixas de Entrada). Salve e publique
                          para valer.
                        </p>
                      </div>
                      <!-- 📒 Registro de atividade -->
                      <div v-if="instagramEvents().length">
                        <p class="cv-label mb-1">📒 Registro de atividade</p>
                        <div class="max-h-40 overflow-y-auto space-y-1">
                          <p
                            v-for="(ev, i) in instagramEvents()"
                            :key="i"
                            class="text-[11px] text-n-slate-11"
                          >
                            <span class="text-n-slate-9">{{
                              new Date(ev.at).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            }}</span>
                            · #{{ ev.conversation_id }} {{ ev.contact }} —
                            {{ INSTAGRAM_EVENT_LABELS[ev.type] || ev.type }}
                            <span v-if="ev.note" class="text-n-slate-9"
                              >({{ ev.note }})</span
                            >
                          </p>
                        </div>
                      </div>
                    </div>

                    <!-- Config específica do Radar de Oportunidades (perene) -->
                    <div
                      v-if="key === 'opportunity'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <!-- O que ele monitora, direto ao ponto -->
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        <p>
                          📡 Monitora só o movimento <b>novo</b> de cada coluna
                          vigiada (espera > {{ agent.wait_minutes }} min) ·
                          avisa no <b>Meu Painel</b> do atendente escolhido ·
                          <b>nunca fala com o paciente</b>.
                        </p>
                        <p v-if="radarLastRun()" class="text-n-slate-10 mt-0.5">
                          Última rodada: {{ radarLastRun().candidates }} no
                          filtro · {{ radarLastRun().analyzed }} analisados ·
                          {{ radarLastRun().new_alerts }} novos avisos
                        </p>
                      </div>

                      <div
                        class="flex items-center gap-2 flex-wrap text-xs text-n-slate-11"
                      >
                        Avisar quando o paciente esperar mais de
                        <input
                          v-model.number="agent.wait_minutes"
                          type="number"
                          min="1"
                          :disabled="!editingAgent[key]"
                          class="cv-input !h-8 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                          style="width: 4.5rem"
                        />
                        min sem resposta
                      </div>

                      <!-- Meta de tempo de atendimento: vira o relatório pessoal
                   de cada atendente no Meu Painel -->
                      <div class="cv-sub px-3 py-2.5">
                        <div
                          class="flex items-center gap-2 flex-wrap text-xs text-n-slate-11"
                        >
                          <span
                            class="i-lucide-target text-sm"
                            style="color: #059669"
                          />
                          <b class="text-n-slate-12"
                            >Meta de tempo de atendimento:</b
                          >
                          responder o paciente em até
                          <input
                            v-model.number="agent.response_goal_minutes"
                            type="number"
                            min="1"
                            :disabled="!editingAgent[key]"
                            class="cv-input !h-8 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                            style="width: 4.5rem"
                          />
                          minutos
                        </div>
                        <p class="text-[11px] text-n-slate-9 mt-1">
                          Cada atendente acompanha a própria meta no
                          <b>Meu Painel</b>: tempo médio de resposta, % das
                          respostas dentro da meta e o selo de meta batida.
                          Admin vê a quebra por atendente.
                        </p>
                      </div>

                      <!-- Vigias: coluna + painel do atendente + janela de tempo -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Colunas vigiadas
                          <span class="text-n-slate-9 font-normal"
                            >(cada coluna com seu atendente e janela — sem vigia
                            o Radar fica desligado)</span
                          >
                        </p>
                        <div
                          v-if="!allStages.length"
                          class="text-xs text-n-slate-9"
                        >
                          Carregando colunas…
                        </div>
                        <template v-else>
                          <div
                            v-if="!agent.watchers.length"
                            class="text-xs text-n-slate-9 mb-2"
                          >
                            Nenhuma coluna vigiada ainda — adicione a primeira
                            abaixo.
                          </div>
                          <div v-else class="space-y-2 mb-2">
                            <div
                              v-for="(w, wi) in agent.watchers"
                              :key="wi"
                              class="flex flex-col sm:flex-row sm:items-end gap-2 cv-sub px-3 py-2.5"
                            >
                              <div class="flex-1 min-w-0">
                                <label
                                  class="text-[10px] font-medium text-n-slate-9 block mb-0.5 flex items-center gap-1"
                                >
                                  <span
                                    class="i-lucide-columns-3 text-[10px]"
                                    style="color: #dc2626"
                                  />
                                  Coluna vigiada
                                </label>
                                <select
                                  v-model="w.stage_id"
                                  :disabled="!editingAgent[key]"
                                  class="cv-input !h-8 w-full text-xs disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                  <option value="" disabled>
                                    Escolha a coluna…
                                  </option>
                                  <option
                                    v-for="s in allStages"
                                    :key="s.id"
                                    :value="s.id"
                                  >
                                    {{ s.name }} ({{ s.pipeline }})
                                  </option>
                                </select>
                              </div>
                              <div class="flex-1 min-w-0">
                                <label
                                  class="text-[10px] font-medium text-n-slate-9 block mb-0.5"
                                  >Avisar no painel de</label
                                >
                                <select
                                  v-model="w.user_id"
                                  :disabled="!editingAgent[key]"
                                  class="cv-input !h-8 w-full text-xs disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                  <option :value="null">
                                    👥 Todos os atendentes
                                  </option>
                                  <option
                                    v-for="ag in teamAgents"
                                    :key="ag.id"
                                    :value="ag.id"
                                  >
                                    {{ ag.available_name || ag.name }}
                                  </option>
                                </select>
                              </div>
                              <div class="flex-1 min-w-0">
                                <label
                                  class="text-[10px] font-medium text-n-slate-9 block mb-0.5"
                                  >Olhando o movimento das</label
                                >
                                <select
                                  v-model.number="w.lookback_hours"
                                  :disabled="!editingAgent[key]"
                                  class="cv-input !h-8 w-full text-xs disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                  <option
                                    v-for="o in LOOKBACK_OPTIONS"
                                    :key="o.value"
                                    :value="o.value"
                                  >
                                    {{ o.label }}
                                  </option>
                                </select>
                              </div>
                              <button
                                v-if="editingAgent[key]"
                                class="text-n-slate-9 hover:text-red-500 i-lucide-trash-2 text-sm flex-shrink-0 self-end sm:self-auto sm:mb-2"
                                title="Remover esta vigia"
                                @click="removeWatcher(wi)"
                              />
                            </div>
                          </div>
                          <button
                            v-if="editingAgent[key]"
                            class="cv-btn cv-btn-sm cv-btn-ghost border-dashed"
                            style="
                              border-color: rgba(220, 38, 38, 0.5);
                              color: #dc2626;
                            "
                            @click="addWatcher"
                          >
                            <span class="i-lucide-plus text-xs" />
                            Vigiar outra coluna
                          </button>
                        </template>
                      </div>

                      <!-- Radar pontual: roda uma vez, não fica ativo -->
                      <div
                        class="border-t border-n-weak pt-3 flex items-center gap-2 flex-wrap"
                      >
                        <button
                          class="cv-btn cv-btn-sm"
                          style="
                            background: linear-gradient(
                              135deg,
                              #059669,
                              #4ade80
                            );
                          "
                          @click="showSweepModal = true"
                        >
                          <span class="i-lucide-scan-search text-xs" />
                          Radar pontual…
                        </button>
                        <span class="text-[11px] text-n-slate-9">
                          varre AGORA a coluna que você escolher e avisa o
                          atendente escolhido — roda uma vez, não fica ativo
                        </span>
                      </div>
                    </div>

                    <!-- Respondedor de Comentários: conexão da Meta + registro -->
                    <div
                      v-if="key === 'comments'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        <p>
                          Responde os <b>comentários públicos</b> dos posts e
                          anúncios a cada 5 min · sem preço/dado clínico em
                          público · caso sério = <b>marca pro humano</b>.
                          Precisa do <b>token da Página</b> (app da Meta — o
                          mesmo caminho do canal Instagram).
                        </p>
                      </div>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <label class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >ID da Página do Facebook (opcional)</span
                          >
                          <input
                            v-model="agent.fb_page_id"
                            type="text"
                            :disabled="!editingAgent[key]"
                            placeholder="ex.: 1234567890"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                        </label>
                        <label class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >ID da conta Instagram Business (opcional)</span
                          >
                          <input
                            v-model="agent.ig_user_id"
                            type="text"
                            :disabled="!editingAgent[key]"
                            placeholder="ex.: 17841400000000"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                        </label>
                      </div>
                      <label class="block">
                        <span class="text-[10px] font-medium text-n-slate-9">
                          Token de acesso da Página
                          {{
                            agent.page_token_set
                              ? '— já conectado ✓ (cole um novo só pra trocar)'
                              : ''
                          }}
                        </span>
                        <input
                          v-model="agent.page_access_token"
                          type="password"
                          :disabled="!editingAgent[key]"
                          :placeholder="
                            agent.page_token_set
                              ? '••••••••••••'
                              : 'cole o Page Access Token da Meta'
                          "
                          class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                        />
                      </label>
                      <!-- registro de atividade (aparência nativa) -->
                      <div v-if="(settings?.ai?.comments_events || []).length">
                        <p class="cv-label mb-1">
                          Últimos comentários tratados
                        </p>
                        <div class="space-y-1 max-h-44 overflow-y-auto">
                          <div
                            v-for="(ev, ei) in settings.ai.comments_events"
                            :key="ei"
                            class="cv-row px-2.5 py-1.5 text-[11px]"
                          >
                            <div class="flex items-center gap-1.5 flex-wrap">
                              <span
                                :class="
                                  ev.platform === 'instagram'
                                    ? 'i-lucide-instagram'
                                    : 'i-lucide-facebook'
                                "
                                class="text-[11px] text-n-slate-9"
                              />
                              <b class="text-n-slate-12">@{{ ev.author }}</b>
                              <span
                                class="text-[9px] font-bold px-1.5 rounded-full uppercase"
                                :style="
                                  ev.status === 'respondido'
                                    ? 'background: rgba(16,185,129,0.15); color: #047857'
                                    : ev.status === 'humano'
                                      ? 'background: rgba(245,158,11,0.15); color: #B45309'
                                      : 'background: rgba(100,116,139,0.15); color: #64748B'
                                "
                              >
                                {{ ev.status }}
                              </span>
                            </div>
                            <p class="text-n-slate-10 mt-0.5">
                              “{{ ev.comment }}”
                            </p>
                            <p
                              v-if="ev.reply && ev.status === 'respondido'"
                              class="text-n-slate-11 mt-0.5"
                            >
                              ↳ {{ ev.reply }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 27/09: papel e responsabilidades — o Mentor julga cada pessoa pelo papel dela -->
                    <div
                      v-if="key === 'mentor'"
                      class="cv-sub p-4 mb-4 space-y-2"
                    >
                      <div class="flex items-center gap-2 flex-wrap">
                        <p class="text-xs font-bold text-n-slate-12">
                          Papéis e responsabilidades
                        </p>
                        <span class="text-[11px] text-n-slate-9"
                          >quem cuida do quê — o Mentor não cobra chat de quem
                          fecha cirurgia</span
                        >
                        <button
                          class="cv-btn cv-btn-sm ml-auto"
                          :disabled="!teamRolesDirty || savingTeamRoles"
                          @click="saveTeamRoles"
                        >
                          <Spinner v-if="savingTeamRoles" :size="12" />
                          <span v-else class="i-lucide-save text-xs" /> Salvar
                          papéis
                        </button>
                      </div>
                      <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
                        <div
                          v-for="ag in humanTeam"
                          :key="'role' + ag.id"
                          class="cv-sub p-3 space-y-1.5"
                        >
                          <div class="flex items-center gap-2">
                            <span
                              class="text-xs font-semibold text-n-slate-12 flex-1 truncate"
                              >{{ ag.available_name || ag.name }}</span
                            >
                            <input
                              v-model="roleOf(ag.id).papel"
                              class="cv-input !h-7 text-xs w-44"
                              style="margin-bottom: 0"
                              placeholder="Papel (ex.: Fechamento)"
                              @input="touchTeamRoles"
                            />
                          </div>
                          <textarea
                            v-model="roleOf(ag.id).responsabilidades"
                            rows="2"
                            class="cv-input w-full text-xs"
                            style="margin-bottom: 0"
                            placeholder="Responsabilidades: o que só esta pessoa faz (ex.: fecha cirurgias, confere a agenda do dia, assume conversas que a IA pausou…)"
                            @input="touchTeamRoles"
                          />
                        </div>
                      </div>
                      <p class="text-[10px] text-n-slate-9">
                        Sem texto, o Mentor usa o que o sistema já sabe (painel
                        da pessoa, responsável pela conferência, médico,
                        administrador). Ele também recebe a lista do que o
                        sistema faz sozinho (atendentes de IA, follow-up,
                        confirmação, NPS, Radar) e não cobra isso de ninguém.
                      </p>
                    </div>

                    <!-- Mentor do Time: como funciona + gerar agora -->
                    <div
                      v-if="key === 'mentor'"
                      class="cv-sub p-4 mb-4 space-y-2"
                    >
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        <p>
                          Toda <b>segunda de manhã</b> ele lê a semana de cada
                          pessoa e deixa o feedback no <b>Meu Painel</b> dela:
                          ponto forte, o ponto fraco a corrigir e soluções
                          simples · compara com a <b>mediana do time</b> sem
                          expor ninguém · <b>nunca fala com o paciente</b>.
                        </p>
                      </div>
                      <div class="flex items-center gap-2 flex-wrap">
                        <p class="text-xs font-medium text-n-slate-11 flex-1">
                          Primeira rodada ou teste
                          <span class="text-n-slate-9 font-normal"
                            >(analisa os últimos 7 dias agora, sem esperar
                            segunda)</span
                          >
                        </p>
                        <button
                          class="cv-btn cv-btn-sm"
                          :style="{ background: AGENT_META.mentor.gradient }"
                          :disabled="isRunningMentor || !agent.enabled"
                          :title="
                            agent.enabled
                              ? ''
                              : 'Ligue o Mentor no interruptor acima primeiro'
                          "
                          @click="runMentorNow"
                        >
                          {{
                            isRunningMentor
                              ? 'Gerando… (1-2 min)'
                              : 'Gerar feedback agora'
                          }}
                        </button>
                      </div>
                    </div>

                    <!-- Consultor Comercial: insights p/ a gestão -->
                    <div
                      v-if="key === 'sales'"
                      class="cv-sub p-4 mb-4 space-y-2"
                    >
                      <div class="flex items-center gap-2 flex-wrap">
                        <p class="text-xs font-medium text-n-slate-11 flex-1">
                          💡 Insights comerciais
                          <span class="text-n-slate-9 font-normal"
                            >(analisa as conversas que FECHARAM cirurgia —
                            alimentadas pelo Monitor de Fechamento)</span
                          >
                        </p>
                        <button
                          class="cv-btn cv-btn-sm"
                          :style="{ background: AGENT_META.sales.gradient }"
                          :disabled="isGeneratingInsights"
                          @click="generateSalesInsights"
                        >
                          {{
                            isGeneratingInsights
                              ? 'Analisando… (1-2 min)'
                              : 'Gerar insights comerciais'
                          }}
                        </button>
                      </div>
                      <div
                        v-if="salesInsights()?.text"
                        class="cv-row p-3 max-h-72 overflow-y-auto"
                      >
                        <p class="text-[10px] text-n-slate-9 mb-1.5">
                          {{ salesInsights().conversations }} conversa(s)
                          analisada(s) ·
                          {{ fmtLogDate(salesInsights().generated_at) }}
                        </p>
                        <pre
                          class="text-[11px] text-n-slate-11 whitespace-pre-wrap font-sans leading-relaxed"
                          >{{ salesInsights().text }}</pre
                        >
                      </div>
                      <p
                        v-else-if="salesInsights()?.error"
                        class="text-[11px] text-amber-600"
                      >
                        ⚠️ {{ salesInsights().error }}
                      </p>
                    </div>

                    <!-- 🏗️ CONSTRUTOR PRO: referências de estilo + teto de resposta -->
                    <div
                      v-if="key === 'pagebuilder'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <p
                        class="text-xs font-bold text-n-slate-12 flex items-center gap-1.5"
                      >
                        <span
                          class="i-lucide-ruler text-xs"
                          style="color: #d4af37"
                        />
                        Construtor PRO
                        <span class="font-normal text-n-slate-9"
                          >— rédeas da montagem: estilo e tamanho da
                          resposta</span
                        >
                      </p>
                      <div>
                        <p class="text-[11px] font-medium text-n-slate-11 mb-1">
                          Referências de estilo
                          <span class="text-n-slate-9 font-normal"
                            >(cole exemplos de páginas que você admira,
                            diretrizes de marca, o que nunca pode faltar — o
                            Construtor se inspira nelas em TODA montagem)</span
                          >
                        </p>
                        <textarea
                          v-model="aiAgents.pagebuilder.references"
                          rows="3"
                          class="cv-input !h-8 w-full text-xs"
                          placeholder="Ex: hero curto e emocional · benefícios sempre em cards · fechar com FAQ + CTA dourado · tom calmo, público 55+, letra grande…"
                        />
                      </div>
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="text-[11px] text-n-slate-10"
                          >Teto de resposta (tokens):</span
                        >
                        <input
                          v-model="aiAgents.pagebuilder.max_tokens"
                          type="number"
                          min="1000"
                          max="60000"
                          step="1000"
                          placeholder="30000"
                          class="cv-input !h-8 text-xs"
                          style="width: 6.5rem"
                        />
                        <span class="text-[10px] text-n-slate-9"
                          >vazio = padrão (30.000). Página grande truncada?
                          Aumente. Custo alto? Diminua.</span
                        >
                      </div>
                      <button
                        class="cv-btn cv-btn-sm"
                        style="
                          background: linear-gradient(135deg, #d4af37, #f4de8e);
                          color: #072a4c;
                        "
                        :disabled="savingBuilderPro"
                        @click="saveBuilderPro"
                      >
                        {{
                          savingBuilderPro
                            ? 'Salvando…'
                            : 'Salvar rédeas do Construtor'
                        }}
                      </button>
                    </div>

                    <!-- ✍️ ESTÚDIO DO COPYWRITER: referências + conteúdo multi-formato -->
                    <div
                      v-if="key === 'copywriter'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <p
                        class="text-xs font-bold text-n-slate-12 flex items-center gap-1.5"
                      >
                        <span
                          class="i-lucide-pen-line text-xs"
                          style="color: #7c3aed"
                        />
                        Estúdio de conteúdo
                        <span class="font-normal text-n-slate-9"
                          >— escolha o formato e a estrutura; o resultado é seu
                          para copiar</span
                        >
                      </p>

                      <!-- referências da casa: o agente segue esse estilo em TUDO -->
                      <div>
                        <p class="text-[11px] font-medium text-n-slate-11 mb-1">
                          Suas estruturas e referências de copywriting
                          <span class="text-n-slate-9 font-normal"
                            >(cole exemplos que você gosta, frases da casa,
                            regras de estilo — vale para páginas e para o
                            Estúdio)</span
                          >
                        </p>
                        <textarea
                          v-model="aiAgents.copywriter.references"
                          rows="3"
                          class="cv-input !h-8 w-full text-xs"
                          placeholder="Ex: sempre abrir com pergunta que toca a dor · nunca usar 'agende já' · exemplo de copy aprovada: ..."
                        />
                        <button
                          class="cv-btn cv-btn-sm mt-1"
                          style="
                            background: linear-gradient(
                              135deg,
                              #7c3aed,
                              #5b21b6
                            );
                          "
                          :disabled="savingRefs"
                          @click="saveCopyReferences"
                        >
                          {{ savingRefs ? 'Salvando…' : 'Salvar referências' }}
                        </button>
                      </div>

                      <!-- formato: botões em linha -->
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span class="text-[11px] text-n-slate-10"
                          >Formato:</span
                        >
                        <button
                          v-for="m in STUDIO_MODALITIES"
                          :key="m.key"
                          class="cv-chip cv-chip-lg"
                          :class="studio.modality === m.key ? 'cv-chip-on' : ''"
                          :style="
                            studio.modality === m.key
                              ? { background: AGENT_META.copywriter.gradient }
                              : {}
                          "
                          @click="studio.modality = m.key"
                        >
                          {{ m.label }}
                        </button>
                        <span class="text-[10px] text-n-slate-9"
                          >· páginas são no editor de Páginas</span
                        >
                      </div>

                      <!-- estrutura narrativa: botões em linha -->
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span class="text-[11px] text-n-slate-10"
                          >Estrutura:</span
                        >
                        <button
                          v-for="st in STUDIO_STRUCTURES"
                          :key="st.key"
                          class="cv-chip cv-chip-lg"
                          :class="
                            studio.structure === st.key ? 'cv-chip-on' : ''
                          "
                          @click="studio.structure = st.key"
                        >
                          {{ st.label }}
                        </button>
                      </div>

                      <textarea
                        v-model="studio.briefing"
                        rows="3"
                        class="cv-input !h-8 w-full text-xs"
                        placeholder="Briefing: assunto, objetivo e o que não pode faltar. Ex: carrossel sobre os 5 mitos da cirurgia de catarata, tom acolhedor, CTA para avaliação."
                      />
                      <div class="flex items-center gap-2 flex-wrap">
                        <select
                          v-model="studio.form_id"
                          class="h-8 cv-sub px-2 text-[11px] text-n-slate-12 max-w-[240px]"
                        >
                          <option value="">Sem insights de formulário</option>
                          <option
                            v-for="f in studioForms"
                            :key="f.id"
                            :value="f.id"
                          >
                            Usar insights de: {{ f.name }}
                          </option>
                        </select>
                        <button
                          class="cv-btn cv-btn-sm ml-auto"
                          :style="{
                            background: AGENT_META.copywriter.gradient,
                          }"
                          :disabled="studio.generating"
                          @click="generateStudio"
                        >
                          <span
                            :class="
                              studio.generating
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-sparkles'
                            "
                            class="text-xs"
                          />
                          {{
                            studio.generating ? 'Escrevendo…' : 'Gerar conteúdo'
                          }}
                        </button>
                      </div>

                      <!-- resultado -->
                      <div v-if="studio.result" class="cv-row p-3 space-y-2">
                        <div class="flex items-center gap-2">
                          <p class="text-xs font-bold text-n-slate-12 flex-1">
                            {{ studio.result.titulo }}
                          </p>
                          <button
                            class="text-[11px] font-medium text-n-brand hover:underline flex items-center gap-1"
                            @click="copyStudioResult"
                          >
                            <span class="i-lucide-copy text-[10px]" /> Copiar
                            tudo
                          </button>
                        </div>
                        <div
                          v-for="(b, bi) in studio.result.blocos"
                          :key="bi"
                          class="cv-input"
                        >
                          <p
                            class="text-[10px] font-bold text-n-slate-9 uppercase tracking-wide"
                          >
                            {{ b.rotulo }}
                          </p>
                          <p
                            class="text-[11px] text-n-slate-11 whitespace-pre-wrap leading-relaxed"
                          >
                            {{ b.texto }}
                          </p>
                        </div>
                        <p
                          v-if="studio.result.legenda"
                          class="text-[11px] text-n-slate-11 whitespace-pre-wrap"
                        >
                          <b>Legenda:</b> {{ studio.result.legenda }}
                        </p>
                        <p
                          v-if="studio.result.hashtags"
                          class="text-[11px] text-n-slate-10"
                        >
                          {{ studio.result.hashtags }}
                        </p>
                      </div>
                    </div>

                    <!-- Colunas de atuação (Analista / Monitor de Fechamento / NPS) -->
                    <div
                      v-if="STAGE_AGENTS.includes(key)"
                      class="cv-sub p-4 mb-4 space-y-2"
                    >
                      <p class="text-xs font-medium text-n-slate-11">
                        Colunas de atuação
                        <span class="text-n-slate-9 font-normal">
                          (card entrou na coluna → o agente lê a conversa
                          <template v-if="key === 'closing'"
                            >; sugestão: as colunas de "Indicação de
                            Cirurgia"</template
                          >
                          <template v-else-if="key === 'nps'"
                            >; sugestão: a coluna de Pós-Operatório</template
                          >)
                        </span>
                      </p>
                      <div
                        v-if="!allStages.length"
                        class="text-xs text-n-slate-9"
                      >
                        Carregando colunas…
                      </div>
                      <div v-else class="flex flex-wrap gap-1.5">
                        <button
                          v-for="st in allStages"
                          :key="st.id"
                          class="cv-chip cv-chip-lg"
                          :class="
                            agentStageIds[key].includes(st.id)
                              ? 'cv-chip-on'
                              : ''
                          "
                          :style="
                            agentStageIds[key].includes(st.id)
                              ? { background: AGENT_META[key].gradient }
                              : {}
                          "
                          @click="toggleAgentStage(key, st.id)"
                        >
                          {{ st.name }}
                        </button>
                      </div>
                      <button
                        class="cv-btn cv-btn-sm"
                        :style="{ background: AGENT_META[key].gradient }"
                        :disabled="savingAgentStages === key"
                        @click="saveAgentStages(key)"
                      >
                        {{
                          savingAgentStages === key
                            ? 'Salvando…'
                            : 'Salvar colunas de atuação'
                        }}
                      </button>
                    </div>

                    <!-- Config do Secretário da Agenda -->
                    <div
                      v-if="key === 'scheduler'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        📥 Anota consultas na Agenda do sistema lendo as
                        conversas — <b>nunca fala com o paciente</b> (quem
                        conversa é o Atendente IA do N8N). Lê quando o card
                        ENTRA nas colunas escolhidas
                        <b>e relê a cada mensagem do paciente nessas colunas</b>
                        — a confirmação que chega depois não escapa mais.
                        Reagendamentos atualizam a consulta existente;
                        <b
                          >cancelamento sem novo horário tira a consulta da
                          Agenda</b
                        >. Fora das colunas: quem tem tarefa "⚠️ Confirmar
                        consulta" aberta ou fala em remarcar/cancelar também
                        redispara a leitura sozinho.
                      </div>

                      <!-- Colunas onde o Secretário atua -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Colunas onde o Secretário atua
                          <span class="text-n-slate-9 font-normal"
                            >(card entrou + mensagens do paciente na coluna → lê
                            a conversa e anota na Agenda; nenhuma marcada = só
                            manual)</span
                          >
                        </p>
                        <div
                          v-if="!allStages.length"
                          class="text-xs text-n-slate-9"
                        >
                          Carregando colunas…
                        </div>
                        <div v-else class="flex flex-wrap gap-1.5">
                          <button
                            v-for="s in allStages"
                            :key="s.id"
                            class="cv-chip cv-chip-lg"
                            :class="
                              schedulerStageIds.includes(s.id)
                                ? 'cv-chip-on'
                                : ''
                            "
                            :style="
                              schedulerStageIds.includes(s.id)
                                ? {
                                    background:
                                      'linear-gradient(135deg, #B8860B, #D4A017)',
                                  }
                                : {}
                            "
                            @click="toggleSchedulerStage(s.id)"
                          >
                            {{ s.name }}
                          </button>
                        </div>
                        <button
                          class="cv-btn cv-btn-sm mt-2"
                          style="
                            background: linear-gradient(
                              135deg,
                              #b8860b,
                              #d4a017
                            );
                          "
                          :disabled="isSavingSchedulerStages"
                          @click="saveSchedulerStages"
                        >
                          {{
                            isSavingSchedulerStages
                              ? 'Salvando…'
                              : 'Salvar colunas de atuação'
                          }}
                        </button>
                      </div>

                      <!-- Preencher com o histórico -->
                      <div
                        class="flex items-center gap-2 flex-wrap border-t border-n-weak pt-3"
                      >
                        <button
                          class="cv-btn cv-btn-sm"
                          style="
                            background: linear-gradient(
                              135deg,
                              #b8860b,
                              #d4a017
                            );
                          "
                          @click="showBackfillModal = true"
                        >
                          <span class="i-lucide-calendar-search text-xs" />
                          Preencher agenda com o histórico…
                        </button>
                        <span
                          v-if="backfillLastRun()"
                          class="text-[11px] text-n-slate-10"
                        >
                          Última varredura:
                          {{ backfillLastRun().scanned }} conversas ·
                          {{ backfillLastRun().created || 0 }} criadas ·
                          {{ backfillLastRun().rescheduled || 0 }} reagendadas ·
                          {{ backfillLastRun().already || 0 }} já existiam
                        </span>
                      </div>

                      <!-- Registro de atividade: cada leitura vira uma linha -->
                      <div class="border-t border-n-weak pt-3">
                        <button
                          class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 hover:text-n-brand"
                          @click="showSchedulerLog = !showSchedulerLog"
                        >
                          <span
                            :class="
                              showSchedulerLog
                                ? 'i-lucide-chevron-up'
                                : 'i-lucide-chevron-down'
                            "
                            class="text-xs"
                          />
                          📒 Registro de atividade ({{ schedulerLog().length }}
                          última(s) leitura(s))
                        </button>
                        <div
                          v-if="showSchedulerLog"
                          class="mt-2 space-y-1 max-h-56 overflow-y-auto pr-1"
                        >
                          <p
                            v-if="!schedulerLog().length"
                            class="text-xs text-n-slate-9"
                          >
                            Nenhuma leitura ainda — as linhas aparecem aqui
                            conforme o Secretário trabalhar.
                          </p>
                          <div
                            v-for="(entry, i) in schedulerLog()"
                            :key="i"
                            class="cv-input !h-8"
                          >
                            <span class="text-n-slate-9">{{
                              fmtLogDate(entry.at)
                            }}</span>
                            <span
                              class="font-medium text-n-slate-12 truncate max-w-[160px]"
                              >{{ entry.name }}</span
                            >
                            <span v-if="entry.when" class="text-n-slate-10"
                              >→ consulta {{ fmtLogDate(entry.when) }}</span
                            >
                            <span
                              class="cv-chip ml-auto"
                              :class="
                                (SCHEDULER_OUTCOMES[entry.outcome] || {}).class
                              "
                            >
                              {{
                                (SCHEDULER_OUTCOMES[entry.outcome] || {})
                                  .label || entry.outcome
                              }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 🌾 Config da Colheitadeira da Base -->
                    <div
                      v-if="key === 'harvest'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        🌾 Todo mês ele pontua a base fria, escolhe os leads
                        mais propensos e manda a mensagem modelo com um
                        <b>gancho pessoal</b> escrito pela IA — um a um, dentro
                        do teto diário. Com a caixinha marcada,
                        <b>nada sai sem a sua aprovação</b>.
                      </div>

                      <!-- Modo de trabalho: organizar (etiqueta no CRM) × enviar (mensagem) -->
                      <div>
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          O que ele faz com os leads aprovados
                        </p>
                        <div
                          class="grid grid-cols-1 sm:grid-cols-2 gap-2"
                          role="radiogroup"
                        >
                          <button
                            role="radio"
                            :aria-checked="agent.mode !== 'send'"
                            class="cv-choice p-3 disabled:cursor-not-allowed"
                            :class="agent.mode !== 'send' ? 'cv-choice-on' : ''"
                            :style="
                              agent.mode !== 'send'
                                ? {
                                    borderColor: AGENT_META.harvest.color,
                                    background: AGENT_META.harvest.color + '14',
                                  }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            @click="agent.mode = 'organize'"
                          >
                            <p class="text-xs font-bold text-n-slate-12">
                              🗂️ Organizar oportunidades
                              <span class="font-normal text-n-slate-9"
                                >(recomendado)</span
                              >
                            </p>
                            <p
                              class="text-[11px] text-n-slate-10 mt-0.5 leading-relaxed"
                            >
                              a IA escolhe e etiqueta os melhores leads frios
                              como oportunidade_AAAA_MM — nenhuma mensagem sai;
                              o time trabalha a lista no CRM filtrando pela
                              etiqueta
                            </p>
                          </button>
                          <button
                            role="radio"
                            :aria-checked="agent.mode === 'send'"
                            class="cv-choice p-3 disabled:cursor-not-allowed"
                            :class="agent.mode === 'send' ? 'cv-choice-on' : ''"
                            :style="
                              agent.mode === 'send'
                                ? {
                                    borderColor: AGENT_META.harvest.color,
                                    background: AGENT_META.harvest.color + '14',
                                  }
                                : {}
                            "
                            :disabled="!editingAgent[key]"
                            @click="agent.mode = 'send'"
                          >
                            <p class="text-xs font-bold text-n-slate-12">
                              📤 Enviar mensagem modelo
                            </p>
                            <p
                              class="text-[11px] text-n-slate-10 mt-0.5 leading-relaxed"
                            >
                              envia a mensagem modelo aprovada com gancho
                              pessoal, respeitando o teto diário
                            </p>
                          </button>
                        </div>
                      </div>

                      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <label class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >Quantos leads por mês</span
                          >
                          <input
                            v-model.number="agent.monthly_size"
                            type="number"
                            min="1"
                            placeholder="300"
                            :disabled="!editingAgent[key]"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                        </label>
                        <label class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >Frio há pelo menos (dias)</span
                          >
                          <input
                            v-model.number="agent.cold_days"
                            type="number"
                            min="1"
                            placeholder="60"
                            :disabled="!editingAgent[key]"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                        </label>
                        <label v-if="agent.mode === 'send'" class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >Teto de envios por dia</span
                          >
                          <input
                            v-model.number="agent.daily_cap"
                            type="number"
                            min="1"
                            placeholder="50"
                            :disabled="!editingAgent[key]"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                          <span class="text-[10px] text-n-slate-9 block mt-0.5"
                            >anti-bloqueio: menos é mais seguro</span
                          >
                        </label>
                        <label class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >Dia do mês que a colheita gera a prévia</span
                          >
                          <input
                            v-model.number="agent.day_of_month"
                            type="number"
                            min="1"
                            max="28"
                            placeholder="1"
                            :disabled="!editingAgent[key]"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                        </label>
                      </div>

                      <div v-if="agent.mode === 'send'">
                        <label
                          class="text-xs font-medium text-n-slate-11 block mb-1.5"
                          >Caixa de WhatsApp que envia</label
                        >
                        <select
                          v-model="agent.inbox_id"
                          :disabled="!editingAgent[key]"
                          class="cv-input w-full text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          <option :value="null" disabled>
                            Escolha a caixa…
                          </option>
                          <option
                            v-for="ib in whatsappInboxes"
                            :key="ib.id"
                            :value="ib.id"
                          >
                            {{ ib.name }}
                          </option>
                        </select>
                        <p
                          v-if="!whatsappInboxes.length"
                          class="text-[11px] text-n-slate-10 mt-1"
                        >
                          Nenhuma caixa de WhatsApp na conta ainda
                          (Configurações → Caixas de Entrada).
                        </p>
                      </div>

                      <div v-if="agent.mode === 'send'">
                        <p class="text-xs font-medium text-n-slate-11 mb-1.5">
                          Mensagem modelo
                          <span class="text-n-slate-9 font-normal"
                            >(template aprovado pela Meta — sem ele a colheita
                            não envia)</span
                          >
                        </p>
                        <div class="flex items-center gap-2">
                          <div class="flex-1 min-w-0">
                            <p
                              v-if="agent.template_params"
                              class="text-xs text-n-slate-12 truncate"
                            >
                              📋 {{ agent.template_params.name }}
                              <span
                                v-if="agent.message_preview"
                                class="text-n-slate-9"
                                >— "{{
                                  agent.message_preview.slice(0, 60)
                                }}…"</span
                              >
                            </p>
                            <p v-else class="text-xs text-amber-600">
                              Nenhum modelo escolhido ainda.
                            </p>
                          </div>
                          <button
                            class="cv-btn cv-btn-sm cv-btn-ghost flex-shrink-0"
                            :disabled="!editingAgent[key]"
                            :title="
                              editingAgent[key]
                                ? ''
                                : 'Clique em Editar lá embaixo para trocar o modelo'
                            "
                            @click="openHarvestTemplatePicker"
                          >
                            {{
                              agent.template_params
                                ? 'Trocar modelo'
                                : 'Escolher modelo'
                            }}
                          </button>
                        </div>
                        <p class="text-[10px] text-n-slate-9 mt-1">
                          💡 Use
                          <code class="bg-n-alpha-2 px-1 rounded"
                            >[gancho]</code
                          >
                          numa variável do modelo — a IA escreve um gancho
                          pessoal para cada paciente;
                          <code class="bg-n-alpha-2 px-1 rounded"
                            >[procedimento]</code
                          >
                          também funciona.
                        </p>
                      </div>

                      <label
                        class="flex items-center gap-2 text-xs text-n-slate-11 cursor-pointer"
                      >
                        <input
                          v-model="agent.require_approval"
                          type="checkbox"
                          class="rounded accent-n-brand"
                          :disabled="!editingAgent[key]"
                        />
                        Exigir minha aprovação antes de enviar
                        <span class="text-n-slate-9"
                          >(recomendado — a prévia espera o seu ok)</span
                        >
                      </label>
                    </div>

                    <!-- 🌾 OPERAÇÃO DA COLHEITA: o mês em andamento -->
                    <div
                      v-if="key === 'harvest'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <p class="cv-label">
                        Operação da colheita<template
                          v-if="harvestMonthLabel()"
                        >
                          · {{ harvestMonthLabel() }}
                        </template>
                      </p>

                      <!-- status do mês em linguagem humana -->
                      <div class="flex items-center gap-2 flex-wrap">
                        <span
                          class="cv-chip cv-chip-lg"
                          :class="harvestStatusInfo().class"
                        >
                          {{ harvestStatusInfo().label }}
                        </span>
                        <span
                          v-if="harvest?.approved_at"
                          class="text-[10px] text-n-slate-9"
                        >
                          aprovada<template v-if="harvest.approved_by">
                            por {{ harvest.approved_by }}</template
                          >
                          · {{ fmtLogDate(harvest.approved_at) }}
                        </span>
                      </div>

                      <!-- modo organize: a lista virou etiqueta — ambiente de trabalho no CRM -->
                      <div
                        v-if="harvest?.organized_label"
                        class="cv-sub cv-green px-3.5 py-2.5 text-[11px] text-green-700 dark:text-green-400"
                      >
                        ✅ {{ harvestOrganizedCount() }} lead(s) etiquetado(s)
                        como <b>{{ harvest.organized_label }}</b> — filtre por
                        essa etiqueta no CRM para trabalhar a lista.
                      </div>

                      <div
                        v-if="harvestGenerating"
                        class="cv-sub cv-amber px-3.5 py-2.5 text-[11px] text-amber-700 dark:text-amber-400"
                      >
                        ⏳ Gerando a prévia… leva uns 2 minutos — esta tela
                        atualiza sozinha a cada 15 segundos.
                      </div>
                      <div
                        v-if="harvest?.last_error"
                        class="cv-sub cv-red px-3.5 py-2.5 text-[11px] text-red-600 dark:text-red-400"
                      >
                        ⚠️ {{ harvest.last_error }}
                      </div>

                      <!-- números do mês -->
                      <div
                        v-if="harvest?.stats"
                        class="grid grid-cols-3 sm:grid-cols-5 gap-2"
                      >
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">Base fria</p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{ harvest.stats.pool ?? 0 }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">Planejados</p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{ harvest.stats.planned ?? 0 }}
                          </p>
                        </div>
                        <div class="cv-stat cv-green px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">Enviados</p>
                          <p
                            class="text-sm font-bold text-green-600 dark:text-green-400"
                          >
                            {{ harvest.stats.sent ?? 0 }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">Responderam</p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{ harvest.stats.replied ?? 0 }}
                          </p>
                        </div>
                        <div class="cv-stat px-3 py-2">
                          <p class="text-[10px] text-n-slate-10">Pendentes</p>
                          <p class="text-sm font-bold text-n-slate-12">
                            {{ harvest.stats.pending ?? 0 }}
                          </p>
                        </div>
                      </div>
                      <p
                        v-if="harvest?.stats?.skipped"
                        class="text-[10px] text-n-slate-9"
                      >
                        {{ harvest.stats.skipped }} lead(s) pulado(s) por você
                        neste mês.
                      </p>

                      <!-- botões conforme o status -->
                      <div class="flex items-center gap-2 flex-wrap">
                        <button
                          v-if="
                            !harvest?.status ||
                            ['preview', 'done'].includes(harvest.status)
                          "
                          class="cv-btn cv-btn-sm disabled:opacity-50"
                          :style="{ background: AGENT_META.harvest.gradient }"
                          :disabled="
                            harvestGenerating || harvestActing === 'preview'
                          "
                          @click="runHarvestPreview"
                        >
                          <span
                            :class="
                              harvestGenerating || harvestActing === 'preview'
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-sparkles'
                            "
                            class="text-xs"
                          />
                          {{
                            harvestGenerating
                              ? 'Gerando… (~2 min)'
                              : 'Gerar prévia agora'
                          }}
                        </button>
                        <button
                          v-if="harvest?.selected?.length"
                          class="cv-btn cv-btn-sm cv-btn-ghost"
                          :disabled="harvestActing === 'refresh'"
                          @click="refreshHarvestPreview"
                        >
                          <span
                            :class="
                              harvestActing === 'refresh'
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-eye'
                            "
                            class="text-xs"
                          />
                          Ver/atualizar prévia
                        </button>
                        <button
                          v-if="harvest?.status === 'preview'"
                          class="cv-btn cv-btn-sm"
                          style="
                            background: linear-gradient(
                              135deg,
                              #059669,
                              #34d399
                            );
                          "
                          :disabled="!!harvestActing"
                          @click="approveHarvest"
                        >
                          <span
                            :class="
                              harvestActing === 'approve'
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-check'
                            "
                            class="text-xs"
                          />
                          {{
                            agent.mode === 'send'
                              ? 'Aprovar e começar'
                              : 'Aprovar e etiquetar'
                          }}
                        </button>
                        <button
                          v-if="harvest?.status === 'approved'"
                          class="cv-btn cv-btn-sm cv-btn-ghost cv-amber"
                          :disabled="!!harvestActing"
                          @click="pauseHarvest"
                        >
                          ⏸ Pausar
                        </button>
                        <button
                          v-if="harvest?.status === 'paused'"
                          class="cv-btn cv-btn-sm cv-btn-ghost cv-green"
                          :disabled="!!harvestActing"
                          @click="resumeHarvest"
                        >
                          ▶️ Retomar
                        </button>
                        <button
                          v-if="harvest?.status === 'approved'"
                          class="cv-btn cv-btn-sm cv-btn-ghost"
                          :disabled="!!harvestActing"
                          title="Dispara o lote de hoje agora, respeitando o teto diário"
                          @click="harvestSendNow"
                        >
                          <span
                            :class="
                              harvestActing === 'send_now'
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-send'
                            "
                            class="text-xs"
                          />
                          Enviar lote agora
                        </button>
                      </div>

                      <!-- prévia: a lista dos escolhidos do mês -->
                      <div
                        v-if="showHarvestTable && harvest?.selected?.length"
                        class="space-y-1 max-h-80 overflow-y-auto pr-1"
                      >
                        <div
                          v-for="lead in harvest.selected"
                          :key="lead.contact_id"
                          class="flex items-center gap-2 flex-wrap text-[11px] cv-sub px-2.5 py-1.5"
                          :class="lead.skipped ? 'opacity-50' : ''"
                        >
                          <span
                            v-if="lead.sent_at"
                            class="text-green-600 font-bold flex-shrink-0"
                            title="Mensagem já enviada"
                            >✓</span
                          >
                          <span
                            class="font-semibold text-n-slate-12 truncate max-w-[150px]"
                            >{{ lead.name }}</span
                          >
                          <span
                            v-if="lead.stage_name"
                            class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-1 text-n-slate-10 whitespace-nowrap"
                            >{{ lead.stage_name }}</span
                          >
                          <span
                            v-if="lead.procedure"
                            class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-1 text-n-slate-10 whitespace-nowrap"
                            >{{ lead.procedure }}</span
                          >
                          <span
                            v-if="lead.value"
                            class="text-n-slate-11 whitespace-nowrap"
                            >{{ fmtBrl(lead.value) }}</span
                          >
                          <span class="text-n-slate-10 whitespace-nowrap"
                            >frio há {{ lead.cold_days }}d</span
                          >
                          <span
                            class="cv-chip flex-shrink-0"
                            :class="scoreClass(lead.score)"
                            >{{ lead.score }}</span
                          >
                          <span
                            v-if="lead.hook"
                            class="italic text-n-slate-10 basis-full sm:basis-auto sm:flex-1 truncate"
                            :title="lead.hook"
                            >“{{ lead.hook }}”</span
                          >
                          <span
                            v-if="lead.skipped"
                            class="text-[10px] text-n-slate-9 ml-auto"
                            >pulado</span
                          >
                          <button
                            v-else-if="!lead.sent_at"
                            class="text-n-slate-9 hover:text-red-500 i-lucide-x text-sm flex-shrink-0 ml-auto"
                            title="Pular este lead (não recebe a mensagem desta colheita)"
                            @click="skipHarvestLead(lead)"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- 📊 Config + estado do Gestor Autônomo -->
                    <div
                      v-if="key === 'manager'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        📊 Todo dia ele compara o funil com a
                        <b>média das últimas 12 semanas</b>. Caiu além da
                        sensibilidade? Ele <b>abre uma tarefa</b> para o time e
                        escreve o <b>briefing do dia</b> no Meu Painel.
                        <b>Nunca fala com o paciente</b> — só lê números.
                      </div>

                      <div
                        class="flex items-center gap-2 flex-wrap text-xs text-n-slate-11"
                      >
                        Sensibilidade: avisar quando cair mais de
                        <input
                          v-model.number="agent.drop_pct"
                          type="number"
                          min="1"
                          max="90"
                          placeholder="25"
                          :disabled="!editingAgent[key]"
                          class="cv-input !h-8 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
                          style="width: 4.5rem"
                        />
                        % vs a média
                      </div>

                      <!-- último briefing + desvios encontrados -->
                      <div
                        v-if="managerState()"
                        class="border-t border-n-weak pt-3 space-y-2"
                      >
                        <p class="cv-label">
                          Última rodada<template
                            v-if="managerState().last_run_at"
                          >
                            ·
                            {{ fmtLogDate(managerState().last_run_at) }}
                          </template>
                        </p>
                        <div v-if="managerState().brief" class="cv-row p-3">
                          <p
                            class="text-xs text-n-slate-11 leading-relaxed whitespace-pre-wrap"
                          >
                            {{ managerState().brief }}
                          </p>
                        </div>
                        <div
                          v-if="(managerState().findings || []).length"
                          class="flex flex-wrap gap-1.5"
                        >
                          <span
                            v-for="(f, fi) in managerState().findings"
                            :key="fi"
                            class="cv-chip cv-red"
                            :title="f.window ? `janela: ${f.window}` : ''"
                          >
                            {{ managerFindingLabel(f) }}
                          </span>
                        </div>
                        <p class="text-[11px] text-n-slate-10">
                          abriu {{ managerState().tasks_opened || 0 }} tarefa(s)
                        </p>
                      </div>

                      <div
                        class="flex items-center gap-2 flex-wrap border-t border-n-weak pt-3"
                      >
                        <button
                          class="cv-btn cv-btn-sm"
                          :style="{ background: AGENT_META.manager.gradient }"
                          :disabled="isRunningManager || !agent.enabled"
                          :title="
                            agent.enabled
                              ? ''
                              : 'Ligue o Gestor no interruptor acima primeiro'
                          "
                          @click="runManagerNow"
                        >
                          {{
                            isRunningManager
                              ? 'Rodando… (~30 s)'
                              : 'Rodar agora'
                          }}
                        </button>
                        <span class="text-[11px] text-n-slate-9"
                          >lê o funil agora e atualiza o briefing do dia</span
                        >
                      </div>
                    </div>

                    <!-- 🎓 Config do Auditor de Conversas -->
                    <div
                      v-if="key === 'auditor'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        🎓 Todo dia ele relê as conversas de <b>ontem</b> e dá
                        <b>nota 0-10</b> contra o script da clínica, apontando o
                        que faltou em cada uma. O ranking por atendente vira
                        <b>coaching contínuo</b> — e ele
                        <b>nunca fala com o paciente</b>.
                      </div>

                      <label class="block sm:w-64">
                        <span class="text-[10px] font-medium text-n-slate-9"
                          >Teto de conversas auditadas por dia</span
                        >
                        <input
                          v-model.number="agent.daily_cap"
                          type="number"
                          min="1"
                          placeholder="150"
                          :disabled="!editingAgent[key]"
                          class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                        />
                        <span class="text-[10px] text-n-slate-9 block mt-0.5"
                          >controla o custo da auditoria diária</span
                        >
                      </label>
                    </div>

                    <!-- 🎓 QUALIDADE DO TIME: ranking + falhas mais comuns -->
                    <div
                      v-if="key === 'auditor'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <p class="cv-label">Qualidade do time</p>

                      <div class="flex items-center gap-2 flex-wrap">
                        <button
                          class="cv-btn cv-btn-sm"
                          :style="{ background: AGENT_META.auditor.gradient }"
                          :disabled="isRunningAuditor || !agent.enabled"
                          :title="
                            agent.enabled
                              ? 'Re-audita as conversas de ontem (~2 min)'
                              : 'Ligue o Auditor no interruptor acima primeiro'
                          "
                          @click="runAuditorNow"
                        >
                          {{
                            isRunningAuditor
                              ? 'Auditando… (~2 min)'
                              : 'Auditar agora'
                          }}
                        </button>
                        <span
                          v-if="auditorLast()?.days_done"
                          class="text-[11px] text-n-slate-10"
                        >
                          último dia auditado:
                          <b class="text-n-slate-12">{{
                            fmtDay(auditorLast().days_done)
                          }}</b>
                          <template v-if="auditorLast()?.last_run_at">
                            · rodou
                            {{
                              fmtLogDate(auditorLast().last_run_at)
                            }}</template
                          >
                        </span>
                        <span v-else class="text-[11px] text-n-slate-10"
                          >nenhuma auditoria ainda — o primeiro ranking sai
                          depois da primeira rodada</span
                        >
                      </div>

                      <div
                        v-if="isRunningAuditor"
                        class="cv-sub cv-amber px-3.5 py-2.5 text-[11px] text-amber-700 dark:text-amber-400"
                      >
                        ⏳ Auditando… leva ~2 minutos — o ranking abaixo
                        atualiza aqui sozinho a cada 20 segundos.
                      </div>

                      <!-- período da análise: a régua padrão do sistema (item 321) -->
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <PeriodRuler v-model="auditorPeriod" glass class="min-w-0" />
                        <span
                          class="text-[10px] text-n-slate-9"
                          title="O Auditor guarda a nota de cada dia por 100 dias (antes de 03/10/2026 guardava 30)."
                          >guarda 100 dias</span
                        >
                        <span
                          v-if="auditorSummary"
                          class="text-[11px] text-n-slate-9 ml-auto"
                        >
                          {{ auditorSummary.audited_total || 0 }} conversa(s)
                          auditada(s) no período
                        </span>
                      </div>

                      <div
                        v-if="loadingAuditorSummary && !auditorSummary"
                        class="flex items-center gap-2 text-xs text-n-slate-10 py-2"
                      >
                        <Spinner :size="16" class="text-n-brand" /> Carregando a
                        qualidade do time…
                      </div>

                      <template v-else-if="auditorSummary">
                        <!-- ranking por atendente (já vem ordenado pela média) -->
                        <div
                          v-if="(auditorSummary.ranking || []).length"
                          class="space-y-1.5"
                        >
                          <p class="cv-label">Ranking por atendente</p>
                          <div
                            v-for="row in auditorSummary.ranking"
                            :key="row.user_id"
                            class="cv-sub px-2.5 py-1.5"
                          >
                            <div
                              class="flex items-center gap-2 flex-wrap text-xs"
                            >
                              <span
                                class="font-semibold text-n-slate-12 truncate max-w-[180px]"
                                >{{ auditorRowName(row) }}</span
                              >
                              <span class="text-n-slate-10 whitespace-nowrap"
                                >{{ row.audited }} auditada(s)</span
                              >
                              <span
                                class="cv-chip ml-auto flex-shrink-0"
                                :class="auditorAvgClass(row.avg)"
                                title="média das notas 0-10 no período"
                              >
                                {{ fmtAvg(row.avg) }}
                              </span>
                            </div>
                            <div
                              v-if="(row.top_gaps || []).length"
                              class="flex flex-wrap gap-1 mt-1"
                            >
                              <span
                                v-for="(gap, gi) in row.top_gaps"
                                :key="gi"
                                class="cv-chip cv-amber"
                              >
                                {{ gap }}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p v-else class="text-xs text-n-slate-10">
                          Nenhuma conversa auditada no período ainda — o ranking
                          aparece depois da primeira auditoria.
                        </p>

                        <!-- falhas mais comuns do time -->
                        <div
                          v-if="(auditorSummary.team_gaps || []).length"
                          class="space-y-1 border-t border-n-weak pt-2"
                        >
                          <p class="cv-label">Falhas mais comuns do time</p>
                          <p
                            v-for="(g, gi) in auditorSummary.team_gaps"
                            :key="gi"
                            class="text-xs text-n-slate-11"
                          >
                            <span
                              class="font-medium text-amber-700 dark:text-amber-400"
                              >{{ g.gap }}</span
                            >
                            <span class="text-n-slate-9">
                              · {{ g.count }}×</span
                            >
                          </p>
                        </div>
                      </template>
                    </div>

                    <!-- 🎨 Config do Criativo Perpétuo -->
                    <div
                      v-if="key === 'creative'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <div
                        class="cv-sub px-3.5 py-2.5 text-[11px] text-n-slate-11 leading-relaxed"
                      >
                        🎨 Toda <b>segunda 08:30</b> ele encontra os
                        <b>vencedores da semana</b> — os termos do Google e os
                        anúncios do Meta que mais viraram
                        <b>consulta e cirurgia</b> na jornada real do banco
                        (últimos 90 dias) — e escreve variações de copy prontas.
                        <b>Nada vai ao ar sozinho</b>: você aprova, copia e cola
                        no Gerenciador de Anúncios.
                      </div>

                      <div class="grid grid-cols-2 gap-2 sm:w-96">
                        <label class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >Vencedores por semana</span
                          >
                          <input
                            v-model.number="agent.winners_count"
                            type="number"
                            min="1"
                            placeholder="3"
                            :disabled="!editingAgent[key]"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                        </label>
                        <label class="block">
                          <span class="text-[10px] font-medium text-n-slate-9"
                            >Variações por vencedor</span
                          >
                          <input
                            v-model.number="agent.variations_count"
                            type="number"
                            min="1"
                            placeholder="3"
                            :disabled="!editingAgent[key]"
                            class="mt-0.5 w-full h-8 cv-sub px-2 text-xs text-n-slate-12 disabled:opacity-60"
                          />
                        </label>
                      </div>
                    </div>

                    <!-- 🎨 CRIATIVOS DA SEMANA: vencedores + variações p/ aprovar -->
                    <div
                      v-if="key === 'creative'"
                      class="cv-sub p-4 mb-4 space-y-3"
                    >
                      <p class="cv-label">
                        Criativos da semana<template
                          v-if="creativeState?.week_key"
                        >
                          · semana de
                          {{ fmtWeekKey(creativeState.week_key) }}
                        </template>
                      </p>

                      <div class="flex items-center gap-2 flex-wrap">
                        <button
                          class="cv-btn cv-btn-sm"
                          :style="{ background: AGENT_META.creative.gradient }"
                          :disabled="isRunningCreative || !agent.enabled"
                          :title="
                            agent.enabled
                              ? 'Gera os vencedores + variações da semana agora (~2 min)'
                              : 'Ligue o Criativo no interruptor acima primeiro'
                          "
                          @click="runCreativeNow"
                        >
                          {{
                            isRunningCreative
                              ? 'Gerando… (~2 min)'
                              : 'Gerar agora'
                          }}
                        </button>
                        <span
                          v-if="creativeState?.generated_at"
                          class="text-[11px] text-n-slate-10"
                        >
                          gerado {{ fmtLogDate(creativeState.generated_at) }}
                        </span>
                        <span
                          v-else-if="creativeLast()?.generated_at"
                          class="text-[11px] text-n-slate-10"
                        >
                          última geração
                          {{ fmtLogDate(creativeLast().generated_at) }}
                        </span>
                        <span
                          class="cv-chip cv-green ml-auto cursor-help"
                          title="aprovadas ficam guardadas para o Estúdio Criativo"
                        >
                          📦
                          {{ (creativeState?.approved_log || []).length }}
                          aprovada(s) na despensa
                        </span>
                      </div>

                      <div
                        v-if="isRunningCreative"
                        class="cv-sub cv-amber px-3.5 py-2.5 text-[11px] text-amber-700 dark:text-amber-400"
                      >
                        ⏳ Gerando as variações… leva ~2 minutos — esta tela
                        atualiza sozinha a cada 20 segundos.
                      </div>

                      <div
                        v-if="loadingCreative && !creativeState"
                        class="flex items-center gap-2 text-xs text-n-slate-10 py-2"
                      >
                        <Spinner :size="16" class="text-n-brand" /> Carregando
                        os criativos da semana…
                      </div>

                      <template
                        v-else-if="(creativeState?.winners || []).length"
                      >
                        <div
                          v-for="(w, wi) in creativeState.winners"
                          :key="wi"
                          class="cv-sub p-3 space-y-2"
                        >
                          <!-- cabeçalho do vencedor -->
                          <div class="flex items-center gap-2 flex-wrap">
                            <span class="text-sm flex-shrink-0">🥇</span>
                            <p
                              class="text-xs font-bold text-n-slate-12 truncate max-w-[240px]"
                              :title="w.name"
                            >
                              {{ w.name }}
                            </p>
                            <span
                              class="cv-chip flex-shrink-0"
                              :style="{
                                backgroundColor:
                                  AGENT_META.creative.color + '1A',
                                color: AGENT_META.creative.color,
                              }"
                            >
                              {{ w.kind_label }}
                            </span>
                          </div>

                          <!-- jornada real em pílulas -->
                          <div
                            class="flex items-center gap-1 flex-wrap text-[10px] font-medium"
                          >
                            <span class="cv-chip"
                              >{{ w.stats?.leads ?? 0 }} leads</span
                            >
                            <span class="text-n-slate-9">→</span>
                            <span class="cv-chip"
                              >{{ w.stats?.booked ?? 0 }} consultas</span
                            >
                            <span class="text-n-slate-9">→</span>
                            <span class="cv-chip"
                              >{{ w.stats?.attended ?? 0 }} compareceram</span
                            >
                            <span class="text-n-slate-9">→</span>
                            <span class="cv-chip cv-green"
                              >{{ w.stats?.surgeries ?? 0 }} cirurgias</span
                            >
                            <span
                              v-if="w.stats?.revenue"
                              class="text-n-slate-10"
                              >· {{ fmtBrl(w.stats.revenue) }}</span
                            >
                          </div>

                          <!-- variações: cartõezinhos p/ aprovar/recusar/copiar -->
                          <div class="space-y-2">
                            <div
                              v-for="(v, vi) in w.variations"
                              :key="vi"
                              class="cv-sub px-3 py-2.5 space-y-1.5"
                              :class="
                                v.status === 'rejected' ? 'opacity-50' : ''
                              "
                            >
                              <div class="flex items-center gap-1.5 flex-wrap">
                                <span v-if="v.angulo" class="cv-chip cv-slate">
                                  {{ v.angulo }}
                                </span>
                                <span
                                  v-if="v.status === 'approved'"
                                  class="cv-chip cv-green"
                                  :title="
                                    v.reviewed_by
                                      ? `aprovada por ${v.reviewed_by}`
                                      : ''
                                  "
                                >
                                  ✓ aprovada
                                </span>
                                <span
                                  v-else-if="v.status === 'rejected'"
                                  class="cv-chip cv-slate"
                                >
                                  ✗ recusada
                                </span>
                                <button
                                  class="text-[11px] font-medium text-n-brand hover:underline flex items-center gap-1 ml-auto flex-shrink-0"
                                  title="Copia gancho + texto + CTA — cole no Gerenciador de Anúncios ou no Estúdio"
                                  @click="copyCreativeVariation(v)"
                                >
                                  <span class="i-lucide-copy text-[10px]" /> 📋
                                  Copiar
                                </button>
                              </div>
                              <p class="text-xs font-bold text-n-slate-12">
                                {{ v.gancho }}
                              </p>
                              <p
                                class="text-[11px] text-n-slate-11 leading-relaxed"
                                style="white-space: pre-line"
                              >
                                {{ v.texto }}
                              </p>
                              <p
                                v-if="v.cta"
                                class="text-[11px] text-n-slate-10"
                              >
                                <b>CTA:</b> {{ v.cta }}
                              </p>
                              <div
                                v-if="v.status === 'pending'"
                                class="flex items-center gap-2 pt-1"
                              >
                                <button
                                  class="cv-btn cv-btn-sm"
                                  style="
                                    background: linear-gradient(
                                      135deg,
                                      #059669,
                                      #34d399
                                    );
                                  "
                                  :disabled="
                                    reviewingCreative === `${wi}-${vi}`
                                  "
                                  @click="
                                    reviewCreativeVariation(wi, vi, 'approved')
                                  "
                                >
                                  ✓ Aprovar
                                </button>
                                <button
                                  class="cv-btn cv-btn-sm cv-btn-ghost"
                                  :disabled="
                                    reviewingCreative === `${wi}-${vi}`
                                  "
                                  @click="
                                    reviewCreativeVariation(wi, vi, 'rejected')
                                  "
                                >
                                  ✗ Recusar
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      <p
                        v-else-if="!loadingCreative && !isRunningCreative"
                        class="text-xs text-n-slate-10"
                      >
                        Nenhum criativo nesta semana ainda — toda segunda 08:30
                        ele gera sozinho, ou clique em "Gerar agora".
                      </p>
                    </div>

                    <label
                      class="text-xs font-semibold text-n-slate-12 block mb-1.5"
                    >
                      {{
                        key === 'voice'
                          ? 'Passos desta ligação'
                          : RESPONDER_WA_KEYS.includes(key)
                            ? 'Passos desta etapa'
                            : 'Prompt do agente'
                      }}
                      <span class="text-n-slate-9 font-normal">
                        {{
                          key === 'voice'
                            ? '(o passo a passo que ele segue AO TELEFONE; o Roteiro CEVICO e as regras de voz entram antes · vazio = passos padrão abaixo)'
                            : RESPONDER_WA_KEYS.includes(key)
                              ? '(o passo a passo que só este agente segue; o Roteiro CEVICO entra antes, para todos · vazio = passos padrão abaixo)'
                              : '(vazio = usa o prompt padrão abaixo)'
                        }}
                      </span>
                    </label>
                    <textarea
                      v-model="agent.prompt"
                      :rows="
                        RESPONDER_WA_KEYS.includes(key) || key === 'voice'
                          ? 12
                          : 4
                      "
                      :disabled="!editingAgent[key]"
                      class="cv-input w-full text-xs font-mono leading-relaxed disabled:opacity-70 disabled:cursor-not-allowed"
                      :placeholder="agent.default_prompt"
                    />

                    <!-- EDITAR | SALVAR (rascunho) | PUBLICAR -->
                    <div
                      class="flex items-center gap-2 flex-wrap mt-4 pt-4 border-t"
                      style="border-color: rgb(var(--cv-rgb) / 0.18)"
                    >
                      <button
                        v-if="!editingAgent[key]"
                        class="cv-btn cv-btn-sm cv-btn-ghost"
                        @click="startEdit(key)"
                      >
                        <span class="i-lucide-pencil text-xs" />
                        Editar
                      </button>
                      <template v-else>
                        <button
                          class="cv-btn cv-btn-sm cv-btn-ghost cv-amber"
                          :disabled="savingAgent === key"
                          title="Guarda as mudanças SEM aplicar — o agente continua como está até você publicar"
                          @click="saveAgentDraft(key)"
                        >
                          <span
                            :class="
                              savingAgent === key
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-save'
                            "
                            class="text-xs"
                          />
                          Salvar rascunho
                        </button>
                        <button
                          class="cv-btn cv-btn-sm"
                          :disabled="savingAgent === key"
                          title="Aplica de verdade — vale nas próximas análises"
                          @click="publishAgent(key)"
                        >
                          <span
                            :class="
                              savingAgent === key
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-rocket'
                            "
                            class="text-xs"
                          />
                          Publicar
                        </button>
                        <button
                          class="text-xs text-n-slate-9 hover:text-n-slate-11 px-2 py-2"
                          :disabled="savingAgent === key"
                          @click="discardEdit(key)"
                        >
                          Descartar mudanças
                        </button>
                      </template>
                      <button
                        v-if="!editingAgent[key] && agent.has_draft"
                        class="cv-btn cv-btn-sm cv-btn-pulse"
                        :disabled="savingAgent === key"
                        title="Publica o rascunho salvo — vale nas próximas análises"
                        @click="publishAgent(key)"
                      >
                        <span
                          :class="
                            savingAgent === key
                              ? 'i-lucide-loader-circle animate-spin'
                              : 'i-lucide-rocket'
                          "
                          class="text-xs"
                        />
                        Publicar rascunho
                      </button>
                      <span class="text-[10px] text-n-slate-9 ml-auto">
                        Salvar = guarda sem aplicar · Publicar = passa a valer ·
                        interruptor liga/desliga na hora
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- ══ MODO PROGRAMAÇÃO — painel panorâmico de automações ══ -->
        <div
          v-else-if="activeTab === 'programacao'"
          class="max-w-3xl space-y-6"
        >
          <div class="flex items-center justify-between flex-wrap gap-2">
            <p class="text-sm text-n-slate-11">
              Painel panorâmico: <b>tudo</b> que trabalha sozinho no sistema,
              num lugar só — agentes de IA, robôs, réguas e automações de
              coluna, com a situação de cada um.
            </p>
            <button
              class="text-sm px-3 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 flex items-center gap-1.5 flex-shrink-0 font-medium"
              @click="openProgrammingMode"
            >
              ⚡ Abrir Modo Programação no CRM
            </button>
          </div>

          <!-- ✨ Agentes de IA -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <p
                class="text-xs font-bold text-n-slate-11 uppercase tracking-wide flex items-center gap-1.5"
              >
                <span
                  class="i-lucide-sparkles text-sm"
                  style="color: #7c3aed"
                />
                Agentes de IA
                <span class="text-n-slate-9 font-normal normal-case"
                  >({{ Object.keys(aiAgents).length }})</span
                >
              </p>
              <button
                class="text-xs font-medium text-n-brand hover:underline"
                @click="activeTab = 'agentes'"
              >
                configurar →
              </button>
            </div>
            <div class="grid sm:grid-cols-2 gap-2">
              <button
                v-for="(agent, key) in aiAgents"
                :key="key"
                class="text-left flex items-start gap-2.5 p-3 bg-n-solid-2 border border-n-weak rounded-xl hover:border-n-brand/50 transition-colors"
                @click="activeTab = 'agentes'"
              >
                <span
                  class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  :style="{ background: AGENT_META[key].gradient }"
                >
                  <span
                    :class="AGENT_META[key].icon"
                    class="text-white text-sm"
                  />
                </span>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <p class="text-sm font-semibold text-n-slate-12 truncate">
                      {{ AGENT_META[key].title }}
                    </p>
                    <span
                      class="text-[9px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                      :class="
                        agent.enabled
                          ? 'bg-green-500/15 text-green-600'
                          : 'bg-n-alpha-2 text-n-slate-10'
                      "
                      >● {{ agent.enabled ? 'Ligado' : 'Desligado' }}</span
                    >
                  </div>
                  <p class="text-[11px] text-n-slate-10 truncate">
                    {{ resolvedModel(key, agent)
                    }}<template v-if="key === 'opportunity'">
                      · {{ agent.watchers.length }} coluna(s) vigiada(s)
                    </template>
                  </p>
                </div>
              </button>
            </div>
          </div>

          <!-- 🤖 Robôs de follow-up -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <p
                class="text-xs font-bold text-n-slate-11 uppercase tracking-wide flex items-center gap-1.5"
              >
                <span class="i-lucide-bot text-sm" style="color: #0f5fa6" />
                Robôs de follow-up
                <span class="text-n-slate-9 font-normal normal-case"
                  >({{ bots.length }})</span
                >
              </p>
              <button
                class="text-xs font-medium text-n-brand hover:underline"
                @click="activeTab = 'robos'"
              >
                gerenciar →
              </button>
            </div>
            <p v-if="!bots.length" class="text-xs text-n-slate-9 pl-1">
              Nenhum robô criado.
            </p>
            <div v-else class="space-y-1.5">
              <button
                v-for="bot in bots"
                :key="bot.id"
                class="w-full text-left flex items-center gap-3 px-3 py-2 bg-n-solid-2 border border-n-weak rounded-xl hover:border-n-brand/50 transition-colors"
                @click="activeTab = 'robos'"
              >
                <span
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :class="bot.active ? 'bg-green-500' : 'bg-n-slate-9'"
                />
                <p class="text-sm font-medium text-n-slate-12 flex-1 truncate">
                  {{ bot.name }}
                </p>
                <span class="text-[11px] text-n-slate-10 flex-shrink-0"
                  >{{ (bot.steps || []).length }} cutucada(s)</span
                >
                <span
                  class="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0"
                  :class="
                    bot.active
                      ? 'bg-green-500/15 text-green-600'
                      : 'bg-n-alpha-2 text-n-slate-9'
                  "
                  >{{ bot.active ? 'Ativo' : 'Pausado' }}</span
                >
              </button>
            </div>
          </div>

          <!-- 📣 Réguas de mensagem -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <p
                class="text-xs font-bold text-n-slate-11 uppercase tracking-wide flex items-center gap-1.5"
              >
                <span
                  class="i-lucide-megaphone text-sm"
                  style="color: #b8860b"
                />
                Réguas de mensagem
                <span class="text-n-slate-9 font-normal normal-case"
                  >({{ automations.length }})</span
                >
              </p>
              <button
                class="text-xs font-medium text-n-brand hover:underline"
                @click="goToCampaign"
              >
                abrir Campanha WhatsApp →
              </button>
            </div>
            <div v-if="loadingReguas" class="flex justify-center py-4">
              <Spinner :size="20" class="text-n-brand" />
            </div>
            <p
              v-else-if="!automations.length"
              class="text-xs text-n-slate-9 pl-1"
            >
              Nenhuma régua criada.
            </p>
            <div v-else class="space-y-1.5">
              <button
                v-for="a in automations"
                :key="a.id"
                class="w-full text-left flex items-center gap-3 px-3 py-2 bg-n-solid-2 border border-n-weak rounded-xl hover:border-n-brand/50 transition-colors"
                @click="goToCampaign"
              >
                <span
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :class="a.active ? 'bg-green-500' : 'bg-n-slate-9'"
                />
                <p class="text-sm font-medium text-n-slate-12 flex-1 truncate">
                  {{ a.name }}
                </p>
                <span
                  v-if="a.trigger_label"
                  class="text-[11px] text-n-slate-10 flex-shrink-0 truncate max-w-32"
                  >🏷 {{ a.trigger_label }}</span
                >
                <span
                  class="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0"
                  :class="
                    a.active
                      ? 'bg-green-500/15 text-green-600'
                      : 'bg-n-alpha-2 text-n-slate-9'
                  "
                  >{{ a.active ? 'Ativa' : 'Pausada' }}</span
                >
              </button>
            </div>
          </div>

          <!-- ⚡ Automações de coluna -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <p
                class="text-xs font-bold text-n-slate-11 uppercase tracking-wide flex items-center gap-1.5"
              >
                <span class="i-lucide-zap text-sm" style="color: #eab308" />
                Automações de coluna
                <span class="text-n-slate-9 font-normal normal-case"
                  >({{ columnAutomations.length }})</span
                >
              </p>
              <button
                class="text-xs font-medium text-n-brand hover:underline"
                @click="openProgrammingMode"
              >
                criar/editar no CRM →
              </button>
            </div>
            <div
              v-if="loadingColumnAutomations"
              class="flex justify-center py-6"
            >
              <Spinner :size="24" class="text-n-brand" />
            </div>
            <p
              v-else-if="!columnAutomations.length"
              class="text-xs text-n-slate-9 pl-1"
            >
              Nenhuma automação de coluna ainda.
            </p>
            <div v-else class="space-y-1.5">
              <div
                v-for="a in columnAutomations"
                :key="a.id"
                class="flex items-center gap-3 px-3 py-2 bg-n-solid-2 border border-n-weak rounded-xl"
              >
                <span
                  class="w-2 h-2 rounded-full flex-shrink-0"
                  :class="a.active ? 'bg-green-500' : 'bg-n-slate-9'"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-n-slate-12 truncate">
                    {{ a.name }}
                  </p>
                  <p class="text-xs text-n-slate-10 truncate">
                    Coluna "{{ a.stage_name }}" ·
                    {{ ACTION_LABELS[a.action_type] || a.action_type }}
                  </p>
                </div>
                <span
                  class="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0"
                  :class="
                    a.active
                      ? 'bg-green-500/15 text-green-600'
                      : 'bg-n-alpha-2 text-n-slate-9'
                  "
                  >{{ a.active ? 'Ativa' : 'Pausada' }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- ══ TRATAMENTO DE DADOS ══ -->
        <!-- ══ RESULTADOS — dashboard das automações de coluna ══ -->
        <div v-else-if="activeTab === 'resultados'" class="max-w-3xl space-y-5">
          <p class="text-sm text-n-slate-11">
            Quantas vezes cada automação de coluna trabalhou no período —
            disparos, falhas e pacientes alcançados. Réguas de mensagem têm
            resultados na Campanha WhatsApp.
          </p>

          <!-- Régua de período padrão CEVICO -->
          <PeriodRuler v-model="resultsPeriod" />

          <div
            v-if="loadingResults"
            class="flex items-center gap-2 text-sm text-n-slate-10 py-8"
          >
            <Spinner class="w-4 h-4" /> Carregando resultados…
          </div>

          <template v-else-if="resultsData">
            <!-- KPIs -->
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div
                class="rounded-xl px-4 py-3 text-white shadow"
                style="background: linear-gradient(135deg, #0f5fa6, #0b4a82)"
              >
                <p class="text-[11px] font-medium text-white/80">Disparos</p>
                <p class="text-xl font-bold leading-tight">
                  {{ resultsData.totals.fired }}
                </p>
              </div>
              <div
                class="rounded-xl px-4 py-3 text-white shadow"
                style="background: linear-gradient(135deg, #5b21b6, #7c3aed)"
              >
                <p class="text-[11px] font-medium text-white/80">
                  Pacientes alcançados
                </p>
                <p class="text-xl font-bold leading-tight">
                  {{ resultsData.totals.contacts }}
                </p>
              </div>
              <div
                class="rounded-xl px-4 py-3 text-white shadow"
                style="background: linear-gradient(135deg, #b8860b, #d4a017)"
              >
                <p class="text-[11px] font-medium text-white/80">
                  Automações ativas
                </p>
                <p class="text-xl font-bold leading-tight">
                  {{ resultsData.totals.active_automations }}
                </p>
              </div>
              <div
                class="rounded-xl px-4 py-3 shadow"
                :class="
                  resultsData.totals.failed
                    ? 'text-white'
                    : 'bg-n-solid-1 border border-n-weak'
                "
                :style="
                  resultsData.totals.failed
                    ? {
                        background: 'linear-gradient(135deg, #DC2626, #F59E0B)',
                      }
                    : {}
                "
              >
                <p
                  class="text-[11px] font-medium"
                  :class="
                    resultsData.totals.failed
                      ? 'text-white/80'
                      : 'text-n-slate-10'
                  "
                >
                  Falhas
                </p>
                <p
                  class="text-xl font-bold leading-tight"
                  :class="resultsData.totals.failed ? '' : 'text-n-slate-12'"
                >
                  {{ resultsData.totals.failed }}
                </p>
              </div>
            </div>

            <!-- Disparos por dia -->
            <div
              v-if="resultsData.timeline.length > 1"
              class="rounded-2xl border-2 border-n-weak bg-n-solid-2 p-4"
            >
              <p
                class="text-xs font-bold text-n-slate-11 uppercase tracking-wide mb-3"
              >
                Disparos por dia
              </p>
              <div class="flex items-end gap-1 h-24 overflow-x-auto">
                <div
                  v-for="d in resultsData.timeline"
                  :key="d.date"
                  class="flex flex-col items-center gap-1 flex-1 min-w-[22px]"
                  :title="`${fmtDay(d.date)}: ${d.fired} disparo(s)`"
                >
                  <span class="text-[9px] text-n-slate-10 leading-none">{{
                    d.fired || ''
                  }}</span>
                  <div
                    class="w-full rounded-t-md"
                    :style="{
                      height: `${Math.max(d.fired ? 8 : 2, (d.fired / maxTimelineFired()) * 64)}px`,
                      background: d.fired
                        ? 'linear-gradient(180deg, #7C3AED, #0F5FA6)'
                        : 'rgba(148,163,184,0.25)',
                    }"
                  />
                  <span
                    class="text-[8px] text-n-slate-9 leading-none whitespace-nowrap"
                    >{{ fmtDay(d.date) }}</span
                  >
                </div>
              </div>
            </div>

            <!-- Ranking por automação -->
            <div class="rounded-2xl border-2 border-n-weak bg-n-solid-2 p-4">
              <p
                class="text-xs font-bold text-n-slate-11 uppercase tracking-wide mb-3"
              >
                Automação por automação
              </p>
              <div
                v-if="!resultsData.automations.length"
                class="text-sm text-n-slate-10 py-4"
              >
                Nenhuma automação com movimento no período.
              </div>
              <div v-else class="space-y-2">
                <div
                  v-for="a in resultsData.automations"
                  :key="a.id"
                  class="rounded-xl border border-n-weak bg-n-solid-1 px-3.5 py-2.5"
                >
                  <div class="flex items-center gap-2 flex-wrap">
                    <span
                      class="w-2 h-2 rounded-full flex-shrink-0"
                      :style="{ backgroundColor: a.stage_color || '#94A3B8' }"
                    />
                    <p class="text-sm font-semibold text-n-slate-12 truncate">
                      {{ a.name }}
                    </p>
                    <span
                      class="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0"
                      :class="
                        a.active
                          ? 'bg-green-500/15 text-green-600'
                          : 'bg-n-alpha-2 text-n-slate-10'
                      "
                      >{{ a.active ? 'Ativa' : 'Pausada' }}</span
                    >
                    <span
                      class="ml-auto text-sm font-bold text-n-slate-12 flex-shrink-0"
                      >{{ a.fired }}×</span
                    >
                  </div>
                  <div
                    class="flex items-center gap-2 flex-wrap text-[11px] text-n-slate-10 mt-1"
                  >
                    <span>{{ a.stage_name }}</span>
                    <span
                      >·
                      {{ TRIGGER_LABELS[a.trigger_type] || a.trigger_type }} →
                      {{ ACTION_LABELS[a.action_type] || a.action_type }}</span
                    >
                    <span>· {{ a.contacts }} paciente(s)</span>
                    <span v-if="a.failed" class="text-red-500 font-medium"
                      >· {{ a.failed }} falha(s)</span
                    >
                    <span class="ml-auto"
                      >último: {{ fmtLastFired(a.last_fired_at) }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- ══ PAINEL DOS AGENTES DE IA (item 85, só admin) ══ -->
        <AiAgentsDashboard v-else-if="activeTab === 'painel_ia'" />

        <!-- ══ 🗺️ MAPA DE FLUXOS (item 170) — componente sob demanda ══ -->
        <div v-else-if="activeTab === 'fluxos'">
          <FlowsMap />
        </div>

        <!-- ══ TRATAMENTO DE DADOS UNIFICADO (item 70) ══ -->
        <div v-else-if="activeTab === 'tratamento'" class="max-w-3xl">
          <p class="text-sm text-n-slate-11 mb-4">
            A casa de TODAS as ferramentas para tratar a base: identificação
            tradicional (filtros, etiquetas, unificação — sempre com prévia
            antes de aplicar) e identificação com inteligência.
          </p>
          <DataTreatmentTools />

          <!-- extras com IA que já moram no hub (só admin: mexem no Radar e na Agenda) -->
          <div v-if="canEditAgents" class="grid sm:grid-cols-2 gap-4 mt-2 mb-6">
            <button
              class="text-left rounded-2xl border border-n-weak bg-n-solid-2 p-4 hover:border-n-brand transition-colors flex items-start gap-3"
              @click="showSweepModal = true"
            >
              <span
                class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style="background: linear-gradient(135deg, #dc2626, #f59e0b)"
              >
                <span class="i-lucide-scan-search text-white text-base" />
              </span>
              <span>
                <p class="text-sm font-bold text-n-slate-12 mb-1">
                  Radar pontual
                </p>
                <p class="text-xs text-n-slate-10 leading-relaxed">
                  Varredura ÚNICA por leads aguardando resposta (coluna +
                  etiqueta + período) → avisos no Meu Painel. Não fica ativa.
                </p>
              </span>
            </button>
            <button
              class="text-left rounded-2xl border border-n-weak bg-n-solid-2 p-4 hover:border-n-brand transition-colors flex items-start gap-3"
              @click="showBackfillModal = true"
            >
              <span
                class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style="background: linear-gradient(135deg, #b8860b, #d4a017)"
              >
                <span class="i-lucide-calendar-search text-white text-base" />
              </span>
              <span>
                <p class="text-sm font-bold text-n-slate-12 mb-1">
                  Preencher a Agenda com o histórico
                </p>
                <p class="text-xs text-n-slate-10 leading-relaxed">
                  O Agente de Agendamento lê as confirmações antigas e registra
                  as consultas na Agenda — sem duplicar, nada vai ao paciente.
                </p>
              </span>
            </button>
          </div>
        </div>
      </div>

      <!-- Modal robô (componente compartilhado) -->
      <FollowupBotModal
        v-if="showBotModal"
        :bot="editingBot"
        @close="showBotModal = false"
        @saved="onBotSaved"
      />
      <!-- Janela isolada: Radar PONTUAL (roda uma vez, não fica ativo) -->
      <div
        v-if="showSweepModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        @click.self="showSweepModal = false"
      >
        <div
          class="bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        >
          <div
            class="h-1.5 w-full flex-shrink-0"
            style="background: linear-gradient(135deg, #059669, #4ade80)"
          />
          <div
            class="flex items-center justify-between px-5 py-4 border-b border-n-weak"
          >
            <h2
              class="text-base font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-scan-search" style="color: #059669" />
              Radar pontual
              <span
                class="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-n-alpha-2 text-n-slate-11"
                >roda uma vez</span
              >
            </h2>
            <button
              class="text-n-slate-10 hover:text-n-slate-12 i-lucide-x text-xl"
              @click="showSweepModal = false"
            />
          </div>
          <div class="p-5 space-y-4">
            <p class="text-xs text-n-slate-10">
              Varre AGORA os leads aguardando resposta no recorte escolhido e
              cria os avisos no <b>Meu Painel</b> do atendente escolhido, em
              alguns minutos. Diferente do Radar perene, <b>não fica ativo</b> —
              é uma auditoria única. Nada é enviado ao paciente.
            </p>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5"
                >Coluna</label
              >
              <select
                v-model="sweep.stage_id"
                class="w-full border border-n-weak rounded-lg px-2 py-2 text-sm bg-n-solid-2 text-n-slate-12"
              >
                <option value="">Colunas vigiadas (padrão)</option>
                <option v-for="s in allStages" :key="s.id" :value="s.id">
                  {{ s.name }} ({{ s.pipeline }})
                </option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5"
                >Enviar os avisos para o painel de</label
              >
              <select
                v-model="sweep.user_id"
                class="w-full border border-n-weak rounded-lg px-2 py-2 text-sm bg-n-solid-2 text-n-slate-12"
              >
                <option :value="null">👥 Todos os atendentes</option>
                <option v-for="ag in teamAgents" :key="ag.id" :value="ag.id">
                  {{ ag.available_name || ag.name }}
                </option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5"
                >Etiqueta
                <span class="text-n-slate-9 font-normal"
                  >(opcional)</span
                ></label
              >
              <select
                v-model="sweep.label"
                class="w-full border border-n-weak rounded-lg px-2 py-2 text-sm bg-n-solid-2 text-n-slate-12"
              >
                <option value="">Qualquer etiqueta</option>
                <option v-for="l in accountLabels" :key="l.id" :value="l.title">
                  {{ l.title }}
                </option>
              </select>
            </div>
            <div>
              <label class="text-xs font-medium text-n-slate-11 block mb-1.5"
                >Período (mensagens aguardando das…)</label
              >
              <select
                v-model.number="sweep.since_hours"
                class="w-full border border-n-weak rounded-lg px-2 py-2 text-sm bg-n-solid-2 text-n-slate-12"
              >
                <option :value="6">últimas 6 horas</option>
                <option :value="24">últimas 24 horas</option>
                <option :value="72">últimos 3 dias</option>
                <option :value="168">últimos 7 dias</option>
              </select>
            </div>
          </div>
          <div class="px-5 py-4 border-t border-n-weak flex gap-2">
            <button
              class="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-white py-2 rounded-lg disabled:opacity-50"
              style="background: linear-gradient(135deg, #dc2626, #f59e0b)"
              :disabled="isSweeping"
              @click="runSweep"
            >
              <span
                :class="
                  isSweeping
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-scan-search'
                "
                class="text-sm"
              />
              {{ isSweeping ? 'Iniciando…' : 'Varrer agora' }}
            </button>
            <button
              class="px-4 border border-n-weak rounded-lg py-2 text-sm text-n-slate-11"
              @click="showSweepModal = false"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>

      <!-- Janela: escolher a mensagem modelo da Colheitadeira -->
      <div
        v-if="showHarvestTemplatePicker"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        @click.self="closeHarvestTemplatePicker"
      >
        <div
          class="bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden"
        >
          <div
            class="h-1.5 w-full flex-shrink-0"
            :style="{ background: AGENT_META.harvest.gradient }"
          />
          <div
            class="flex items-center justify-between px-5 py-4 border-b border-n-weak flex-shrink-0"
          >
            <h2
              class="text-base font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-wheat" style="color: #ca8a04" />
              {{
                harvestPickerTemplate
                  ? 'Preencher variáveis do modelo'
                  : 'Escolher mensagem modelo'
              }}
            </h2>
            <button
              class="text-n-slate-10 hover:text-n-slate-12 i-lucide-x text-xl"
              @click="closeHarvestTemplatePicker"
            />
          </div>
          <div class="flex-1 overflow-y-auto p-5 space-y-3">
            <p class="text-[11px] text-n-slate-10">
              💡 Escreva
              <code class="bg-n-alpha-2 px-1 rounded">[gancho]</code> numa
              variável do corpo — a IA troca por um gancho pessoal para cada
              paciente;
              <code class="bg-n-alpha-2 px-1 rounded">[procedimento]</code>
              também funciona. O
              <code class="bg-n-alpha-2 px-1 rounded">{{
                '\{\{contact.first_name\}\}'
              }}</code>
              vira o nome do paciente.
            </p>
            <WhatsAppTemplateParser
              v-if="harvestPickerTemplate"
              :template="harvestPickerTemplate"
              @send-message="useHarvestTemplate"
              @reset-template="harvestPickerTemplate = null"
            >
              <template #actions="{ sendMessage, resetTemplate, disabled }">
                <footer class="flex gap-2 justify-end">
                  <button
                    class="px-3 py-1.5 text-sm border border-n-weak rounded-lg text-n-slate-11"
                    @click="resetTemplate"
                  >
                    Trocar modelo
                  </button>
                  <button
                    class="px-4 py-1.5 text-sm bg-n-brand text-white rounded-lg disabled:opacity-50"
                    :disabled="disabled"
                    @click="sendMessage"
                  >
                    Usar este modelo
                  </button>
                </footer>
              </template>
            </WhatsAppTemplateParser>
            <TemplatesPicker
              v-else
              :inbox-id="harvestPickerInboxId"
              @on-select="harvestPickerTemplate = $event"
            />
          </div>
        </div>
      </div>

      <!-- Modal: preencher a Agenda com o histórico -->
      <div
        v-if="showBackfillModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        @click.self="showBackfillModal = false"
      >
        <div
          class="bg-n-solid-1 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          <div
            class="h-1.5 w-full"
            style="background: linear-gradient(135deg, #b8860b, #d4a017)"
          />
          <div
            class="flex items-center justify-between px-5 py-4 border-b border-n-weak"
          >
            <h2
              class="text-base font-semibold text-n-slate-12 flex items-center gap-2"
            >
              <span class="i-lucide-calendar-search" style="color: #b8860b" />
              Preencher agenda com o histórico
            </h2>
            <button
              class="text-n-slate-10 hover:text-n-slate-12 i-lucide-x text-xl"
              @click="showBackfillModal = false"
            />
          </div>
          <div class="p-5 space-y-4">
            <p class="text-xs text-n-slate-11 leading-relaxed">
              O Agente de Agendamento vai ler as conversas com
              <b>confirmação de agendamento</b> (ex.: "Consulta confirmada" do
              bot) e registrar cada consulta na Agenda do sistema — criando as
              novas e <b>reagendando</b> as que mudaram de horário. Nada é
              enviado ao paciente.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5"
                  >Olhar as conversas dos últimos</label
                >
                <select
                  v-model.number="backfill.since_days"
                  class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12"
                >
                  <option :value="7">7 dias</option>
                  <option :value="30">30 dias</option>
                  <option :value="90">90 dias</option>
                  <option :value="180">180 dias</option>
                </select>
              </div>
              <div>
                <label class="text-xs font-medium text-n-slate-11 block mb-1.5"
                  >Teto de conversas</label
                >
                <select
                  v-model.number="backfill.limit"
                  class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-solid-2 text-n-slate-12"
                >
                  <option :value="50">50</option>
                  <option :value="100">100</option>
                  <option :value="200">200</option>
                  <option :value="300">300 (máximo)</option>
                </select>
              </div>
            </div>
            <div
              class="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-400"
            >
              ⚠️ Cada conversa = 1 análise de IA (Sonnet ≈ US$ 0,01–0,02 cada).
              O Agente de Agendamento precisa estar <b>LIGADO</b> e com a chave
              configurada. Rodar de novo não duplica consultas.
            </div>
          </div>
          <div class="flex gap-2 px-5 pb-5">
            <button
              class="flex-1 flex items-center justify-center gap-1.5 text-white rounded-lg py-2 text-sm font-semibold disabled:opacity-50"
              style="background: linear-gradient(135deg, #b8860b, #d4a017)"
              :disabled="isBackfilling"
              @click="runBackfill"
            >
              <span
                :class="
                  isBackfilling
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-play'
                "
                class="text-sm"
              />
              {{ isBackfilling ? 'Iniciando…' : 'Começar a preencher' }}
            </button>
            <button
              class="px-4 border border-n-weak rounded-lg py-2 text-sm text-n-slate-11"
              @click="showBackfillModal = false"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 🃏 rodada 191: grade de cards compactos dos agentes (auto-fill, como a Central) */
.cv-agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15.5rem, 1fr));
  gap: 1rem;
  align-items: stretch;
}
.cv-agents-grid > .cv-agent-open {
  grid-column: 1 / -1;
}
/* o card que acabou de abrir: anel pulsando na cor do agente */
.cv-agent-opening {
  animation: cvAgentOpening 1.1s ease-out;
}
@keyframes cvAgentOpening {
  0% {
    box-shadow: 0 0 0 0 rgb(var(--cv-rgb) / 0.6);
    transform: scale(0.988);
  }
  35% {
    box-shadow: 0 0 0 12px rgb(var(--cv-rgb) / 0.16);
    transform: scale(1.004);
  }
  100% {
    box-shadow: 0 0 0 0 rgb(var(--cv-rgb) / 0);
    transform: none;
  }
}
/* sanfona dos agentes: aparição suave e leve */
.cevico-agent-body {
  animation: agentReveal 0.28s ease;
}
@keyframes agentReveal {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
</style>
